import React from 'react';
import env from '../config/env';
import { parseISO } from 'date-fns';
import { PDFDocument, rgb, StandardFonts, grayscale, PageSizes } from 'pdf-lib';
import { format, addDays, subDays, subHours, addHours } from 'date-fns';
import { CsvBuilder } from 'filefy';
import JSZip from 'jszip';
import saveAs from 'file-saver';
import { IUnit, IOutdoor, IPriceList } from '../types/unit';
import { IDeal, IDealAdjustment, IDealOption } from '../types/CreateDealForm';
import { IPurchaserInfo, IRealtorInfo, IDealDeposit } from '../types/CreateDealForm';
import ExcelJS from 'exceljs';
import { IUpgrade } from '../types/colourSelection';
import { mainCategories, randomColors, unitStatusTitles, unitTypesColour, libertyProjects } from './Constants';
import { IProject, IStatus } from '../types/project';
import { ILease } from '../types/lease';

export function stringSplit(string: string) {
  let stringArray = string.split(' ');
  return stringArray;
}

export function isLetter(string: string) {
  let letter = string.charAt(string.length - 1);
  if (letter.match(/[a-z]/i)) {
    return string.slice(0, -1).toString();
  } else {
    return string;
  }
}

// Sold/pending should be driven by each status's category rather than a hardcoded code list, so
// any custom status (e.g. brokerage-specific sale codes) is picked up automatically once it's
// marked category "sales" in project settings.
export const getSalesStatusCodes = (project?: IProject) => {
  const allStatus: IStatus[] = [...((project?.status as any) || []), ...unitStatusTitles];
  return allStatus.filter((status: IStatus) => status.category === 'sales').map((status: IStatus) => status.code);
};

export const formatPhoneNumber = (value: string) => {
  let num = value.replace(/[^\d]/g, '');
  return `${
    num.length > 3 && num.length < 11 ? `${num.slice(0, 3)}-${num.length > 6 ? `${num.slice(3, 6)}-${num.slice(6)}` : num.slice(3)}` : num
  }`;
};

export const keyToCamel = (array: any) => {
  let newO: any, origKey, newKey, value;
  if (array instanceof Array) {
    return array.map(function (value) {
      if (typeof value === 'object') {
        value = keyToCamel(value);
      }
      return value;
    });
  } else {
    newO = {};
    for (origKey in array) {
      if (array.hasOwnProperty(origKey)) {
        newKey = (origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey).toString();
        value = array[origKey];
        if (value instanceof Array || (value !== null && value.constructor === Object)) {
          value = keyToCamel(value);
        }
        newO[newKey] = value;
      }
    }
  }
  return newO;
};

export const stringWithSpaces = (string: string) => {
  let newString = string.replace(/([A-Z])/g, ' $1').trim();
  return newString;
};

export const formatSin = (value: string) => {
  let num = value.replace(/[^\d]/g, '');
  return `${
    num.length > 3 && num.length < 10 ? `${num.slice(0, 3)} ${num.length > 6 ? `${num.slice(3, 6)} ${num.slice(6)}` : num.slice(3)}` : num
  }`;
};

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const capitalizeFirstLetterEachWord = (string: string) => {
  const words = string.split(' ');
  let ret = '';
  for (let word of words) {
    ret += word.charAt(0).toUpperCase();
    ret += word.slice(1);
    ret += ' ';
  }
  if (words.length > 0) ret = ret.slice(0, ret.length - 1);
  return ret;
};

export function camelToTitle(string: string) {
  const result = string.replace(/([A-Z])/g, ' $1');
  const titleCase = result.charAt(0).toUpperCase() + result.slice(1);
  return titleCase;
}

export const dashToTitle = (string: string) => {
  return capitalizeFirstLetterEachWord(string.replace(/-/g, ' '));
};

export function uppercaseToTitle(string: string) {
  const output = string.replace(/(\w)(\w*)/g, (_, firstChar, rest) => firstChar + rest.toLowerCase());

  return output;
}

export const normalToCamel = (string: string) => {
  return string.toLocaleLowerCase().replace(/\s+(.)/g, function (match, group) {
    return group.toUpperCase();
  });
};

export function uintToString(uintArray: any) {
  var encodedString = String.fromCharCode.apply(null, uintArray),
    decodedString = decodeURIComponent(escape(atob(encodedString)));
  return decodedString;
}

const _MS_PER_DAY = 1000 * 60 * 60 * 24;

export function dateDiffInDays(a: Date, b: Date) {
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export const urlName = (string: string) => {
  return string.replace(/\s+/g, '-').toLowerCase();
};

export const numberWithCommas = (num: number) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getOutdoorSize = (outdoor?: IOutdoor[]) => {
  return outdoor ? outdoor.reduce((total: number, o: IOutdoor) => total + (o.size || 0), 0) : 0;
};

export const getOutdoorSizeDisplay = (outdoor?: IOutdoor[]) => {
  return outdoor && outdoor.length ? outdoor.map((o: IOutdoor) => o.size || 0).join('+') : 0;
};

export const getOutdoorType = (outdoor?: IOutdoor[]) => {
  return outdoor
    ? outdoor
        .map((o: IOutdoor) => o.name)
        .filter(Boolean)
        .join(', ')
    : '';
};

export const numToCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const numToCurrencyDecimal = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

// Price merge fields carry how many decimal places to show in `format` (set by double-clicking the field in the Merge
// Creator). A price field with no format - including every field saved before this - defaults to 0 decimals.
// Non-price fields get the formatter the merge value would normally use.
const priceCurrencyFormatters: { [decimals: number]: Intl.NumberFormat } = {};
export const currencyFor = (mergeValue: any, fallback: Intl.NumberFormat) => {
  if (!/price/i.test(mergeValue?.key || '')) return fallback;
  const raw = `${mergeValue?.format ?? ''}`.trim();
  const decimals = /^\d+$/.test(raw) ? Math.min(parseInt(raw, 10), 4) : 0;
  if (!priceCurrencyFormatters[decimals]) {
    priceCurrencyFormatters[decimals] = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return priceCurrencyFormatters[decimals];
};

export const convertDate = (timestamp: any) => {
  return format(parseISO(timestamp), 'PPPP HH:mm:ss');
};

export const convertDDMMYYYY = (timestamp: any) => {
  return format(parseISO(timestamp), 'dd/MM/yyyy');
};

export const convertDateType = (timestamp: any, type: string) => {
  if (!timestamp) {
    if (type === 'day') {
      return format(new Date(), 'dd');
    } else if (type === 'year') {
      return format(new Date(), 'yy');
    } else if (type === 'month') {
      return format(new Date(), 'LLLL');
    }
  } else {
    if (type === 'day') {
      return format(parseISO(timestamp), 'dd');
    } else if (type === 'year') {
      return format(parseISO(timestamp), 'yy');
    } else if (type === 'month') {
      return format(parseISO(timestamp), 'LLLL');
    }
  }
};

export const convertAllDates = (timestamp: any, dateFormat: string) => {
  return format(new Date(timestamp), dateFormat);
};

export const getKeyByValue = (object: any, value: any) => {
  return `${Object.keys(object).find((key) => object[key] === value)}`;
};

export const calculateOptions = (mergeValue: string, project: any, options: any = null, toOptions: any = null, currency: Intl.NumberFormat = numToCurrency) => {
  if (mergeValue === 'OP1' || mergeValue === 'OP1Price' || mergeValue === 'OP1Num') {
    let selectedOption = options.find((option: any) => project.options[0].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP2' || mergeValue === 'OP2Price' || mergeValue === 'OP2Num') {
    let selectedOption = options.find((option: any) => project.options[1].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP3' || mergeValue === 'OP3Price' || mergeValue === 'OP3Num') {
    let selectedOption = options.find((option: any) => project.options[2].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP4' || mergeValue === 'OP4Price' || mergeValue === 'OP4Num') {
    let selectedOption = options.find((option: any) => project.options[3].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP5' || mergeValue === 'OP5Price' || mergeValue === 'OP5Num') {
    let selectedOption = options.find((option: any) => project.options[4].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP6' || mergeValue === 'OP6Price' || mergeValue === 'OP6Num') {
    let selectedOption = options.find((option: any) => project.options[5].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP7' || mergeValue === 'OP7Price' || mergeValue === 'OP7Num') {
    let selectedOption = options.find((option: any) => project.options[6].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP8' || mergeValue === 'OP8Price' || mergeValue === 'OP8Num') {
    let selectedOption = options.find((option: any) => project.options[7].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP9' || mergeValue === 'OP9Price' || mergeValue === 'OP9Num') {
    let selectedOption = options.find((option: any) => project.options[8].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'OP10' || mergeValue === 'OP10Price' || mergeValue === 'OP10Num') {
    let selectedOption = options.find((option: any) => project.options[9].name === option.name);
    if (selectedOption) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP1' || mergeValue === 'toOP1Price' || mergeValue === 'toOP1Num') {
    let selectedOption = toOptions.find((option: any) => project.options[0].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP2' || mergeValue === 'toOP2Price' || mergeValue === 'toOP2Num') {
    let selectedOption = toOptions.find((option: any) => project.options[1].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP3' || mergeValue === 'toOP3Price' || mergeValue === 'toOP3Num') {
    let selectedOption = toOptions.find((option: any) => project.options[2].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP4' || mergeValue === 'toOP4Price' || mergeValue === 'toOP4Num') {
    let selectedOption = toOptions.find((option: any) => project.options[3].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP5' || mergeValue === 'toOP5Price' || mergeValue === 'toOP5Num') {
    let selectedOption = toOptions.find((option: any) => project.options[4].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP6' || mergeValue === 'toOP6Price' || mergeValue === 'toOP6Num') {
    let selectedOption = toOptions.find((option: any) => project.options[5].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP7' || mergeValue === 'toOP7Price' || mergeValue === 'toOP7Num') {
    let selectedOption = toOptions.find((option: any) => project.options[6].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP8' || mergeValue === 'toOP8Price' || mergeValue === 'toOP8Num') {
    let selectedOption = toOptions.find((option: any) => project.options[7].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP9' || mergeValue === 'toOP9Price' || mergeValue === 'toOP9Num') {
    let selectedOption = toOptions.find((option: any) => project.options[8].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  } else if (mergeValue === 'toOP10' || mergeValue === 'toOP10Price' || mergeValue === 'toOP10Num') {
    let selectedOption = toOptions.find((option: any) => project.options[9].name === option.name);
    if (selectedOption && selectedOption.purchaseAmount) {
      return {
        purchaseNumber: `${selectedOption.purchaseAmount}`,
        purchaseAmount: `${selectedOption.purchaseAmount} (${numberToEnglish(selectedOption.purchaseAmount)})`,
        amount: currency.format(selectedOption.amount),
      };
    } else
      return {
        purchaseNumber: `0`,
        purchaseAmount: '0 (NIL)',
        amount: project.defaultZeroValue ? project.defaultZeroValue : '$0',
      };
  }
};

const capitalizeDifference = (type: string) => {
  if (type === 'email') {
    return 'Email';
  } else if (type === 'fullName') {
    return 'Full Name';
  } else if (type === 'fullAddress') {
    return 'Full Address';
  } else if (type === 'dob') {
    return 'Date of Birth';
  } else if (type === 'address') {
    return 'Address';
  } else if (type === 'city') {
    return 'City';
  } else if (type === 'province') {
    return 'Province';
  } else if (type === 'country') {
    return 'Country';
  } else if (type === 'primaryPhone') {
    return 'Phone Number';
  } else if (type === 'sin') {
    return 'SIN';
  }
};

export const calculateCommission = (mergeValue: string, project: any, totalPrice: number, unit: any, realtor: any) => {
  let price = unit.type === 'commercial' ? unit.basePrice : netHST(unit.basePrice, project);
  if (mergeValue === 'CommissionPercentAmount1NetHstNoRebate') {
    return numToCurrencyDecimal.format(parseFloat(((realtor.coopRates[0].percentage / 100) * netHSTnoRebate(unit.basePrice)).toFixed(2)));
  }
  if (project.commissionIncludeOptions) {
    price = unit.type === 'commercial' ? totalPrice : netHST(totalPrice, project);
  }
  if (realtor.coopRates.length === 0) {
    return '';
  }
  if (mergeValue === 'CommissionPercentAmount1') {
    return numToCurrencyDecimal.format(parseFloat(((realtor.coopRates[0].percentage / 100) * price).toFixed(2)));
  } else if (mergeValue === 'CommissionPercentAmount2') {
    return numToCurrencyDecimal.format(parseFloat(((realtor.coopRates[1].percentage / 100) * price).toFixed(2)));
  } else if (mergeValue === 'CommissionPercentAmount3') {
    return numToCurrencyDecimal.format(parseFloat(((realtor.coopRates[2].percentage / 100) * price).toFixed(2)));
  } else if (mergeValue === 'CommissionPercentAmount4') {
    return numToCurrencyDecimal.format(parseFloat(((realtor.coopRates[3].percentage / 100) * price).toFixed(2)));
  } else if (mergeValue === 'CommissionPercentAmount5') {
    return numToCurrencyDecimal.format(parseFloat(((realtor.coopRates[4].percentage / 100) * price).toFixed(2)));
  } else if (mergeValue === 'CommissionPercentAmount6') {
    return numToCurrencyDecimal.format(parseFloat(((realtor.coopRates[5].percentage / 100) * price).toFixed(2)));
  } else if (mergeValue === 'CommissionFixed1') {
    return numToCurrencyDecimal.format(
      parseFloat(((realtor.coopRates[0].percentage / 100) * price - realtor.coopRates[0].fixedAmount).toFixed(2))
    );
  } else if (mergeValue === 'CommissionFixed2') {
    return numToCurrencyDecimal.format(
      parseFloat(((realtor.coopRates[1].percentage / 100) * price - realtor.coopRates[1].fixedAmount).toFixed(2))
    );
  } else if (mergeValue === 'CommissionFixed3') {
    return numToCurrencyDecimal.format(
      parseFloat(((realtor.coopRates[2].percentage / 100) * price - realtor.coopRates[2].fixedAmount).toFixed(2))
    );
  } else if (mergeValue === 'CommissionFixed4') {
    return numToCurrencyDecimal.format(
      parseFloat(((realtor.coopRates[3].percentage / 100) * price - realtor.coopRates[3].fixedAmount).toFixed(2))
    );
  } else if (mergeValue === 'CommissionFixed5') {
    return numToCurrencyDecimal.format(
      parseFloat(((realtor.coopRates[4].percentage / 100) * price - realtor.coopRates[4].fixedAmount).toFixed(2))
    );
  } else if (mergeValue === 'CommissionFixed6') {
    return numToCurrencyDecimal.format(
      parseFloat(((realtor.coopRates[5].percentage / 100) * price - realtor.coopRates[5].fixedAmount).toFixed(2))
    );
  }
};

export const getPurchaserInfo = (value: any, type: string) => {
  if (type === 'fullName') {
    return `${value.firstName} ${value.lastName}`;
  } else if (type === 'fullAddress') {
    return `${value.streetAddress}, ${value.city}, ${value.province}, ${value.postalCode ? value.postalCode : ''}`;
  } else if (type === 'dob') {
    return convertAllDates(value.dob, 'P');
  } else return value[type];
};

export const getMergeValues = (
  mergeValue: any,
  project: any,
  unit: any,
  mergeType: any = null,
  purchaser: any,
  addPurchasers: any = [],
  deletePurchasers: any = [],
  deposit: any,
  options: any = null,
  realtor: any = null,
  toUnit: any = null,
  toOptions: any = null,
  toDeposits: any = null,
  mergeTemplates: any = null,
  deal: any = null,
  toPurchaser: any = [],
  colourSelectionChoices: any = null,
  lease: any = null,
  assignment: any = null
) => {
  let totalOption = 0;
  let toTotalOption = 0;
  let schedule = '';
  let unitBasePrice = mergeValue.key === 'NetTPrice' || mergeValue.key === 'NetTPriceW' ? netHST(unit.basePrice, project) : unit.basePrice;
  let commission: any = '';
  let soPurchaser: any = null;
  

  let newOptions = calculateOptions(mergeValue.key, project, options, toOptions, currencyFor(mergeValue, numToCurrency));

  if (mergeValue.key === 'H' || mergeValue.key === 'J') {
    schedule = mergeTemplates.find((mergeTemplate: any) => mergeTemplate.name.indexOf(mergeValue.key));
    if (schedule) {
      return mergeValue.key;
    } else return '';
  }

  let upgradeSetOne = 0;
  let upgradeSetTwo = 0;
  let upgradeSetThree = 0;
  let upgradeCreditOne = 0;
  let upgradeCreditTwo = 0;
  let upgradeCreditThree = 0;
  let decorTotalSetOne = 0;
  let decorTotalSetTwo = 0;
  let decorTotalSetThree = 0;
  let toDecorTotalSetOne = 0;
  let toDecorTotalSetTwo = 0;
  let toDecorTotalSetThree = 0;

  totalOption = options.reduce((a: any, b: any) => {
    return a + b.amount;
  }, unitBasePrice);

  if (deal) {
    if (deal.upgradeSetOne) {
      upgradeSetOne = deal.upgradeSetOne.upgradeSet
        .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
        .reduce((a: any, b: any) => {
          return a + b.price;
        }, 0);
      let credit = deal.upgradeSetOne.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
      upgradeCreditOne = credit ? credit.price : 0;
      upgradeSetOne -= upgradeCreditOne;

      decorTotalSetOne = totalOption + upgradeSetOne * 1.13;
    }
    if (deal.upgradeSetTwo) {
      upgradeSetTwo = deal.upgradeSetTwo.upgradeSet
        .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
        .reduce((a: any, b: any) => {
          return a + b.price;
        }, 0);
      let credit = deal.upgradeSetTwo.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
      upgradeCreditTwo = credit ? credit.price : 0;
      upgradeSetTwo -= upgradeCreditTwo;

      decorTotalSetTwo = totalOption + upgradeSetTwo * 1.13;
    }
    if (deal.upgradeSetThree) {
      upgradeSetThree = deal.upgradeSetThree.upgradeSet
        .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
        .reduce((a: any, b: any) => {
          return a + b.price;
        }, 0);
      let credit = deal.upgradeSetThree.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
      upgradeCreditThree = credit ? credit.price : 0;
      upgradeSetThree -= upgradeCreditThree;

      decorTotalSetThree = totalOption + upgradeSetThree * 1.13;
    }

    totalOption += upgradeSetOne * 1.13 + upgradeSetTwo * 1.13 + upgradeSetThree * 1.13;
  }

  if (realtor && realtor.coopRates && realtor.coopRates.length) {
    commission = calculateCommission(mergeValue.key, project, totalOption, unit, realtor);
  }

  if (toUnit && toUnit._id !== unit._id) {
    unitBasePrice = toUnit.basePrice;
    toTotalOption = toOptions.reduce((a: any, b: any) => {
      return a + b.amount;
    }, unitBasePrice);
  } else {
    if (toOptions) {
      toTotalOption = toOptions.reduce((a: any, b: any) => {
        return a + b.amount;
      }, unitBasePrice);

      if (deal.upgradeSetOne) {
        upgradeSetOne = deal.upgradeSetOne.upgradeSet
          .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
          .reduce((a: any, b: any) => {
            return a + b.price;
          }, 0);
        let credit = deal.upgradeSetOne.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
        upgradeCreditOne = credit ? credit.price : 0;
        upgradeSetOne -= upgradeCreditOne;

        toDecorTotalSetOne = toTotalOption + upgradeSetOne * 1.13;
      }
      if (deal.upgradeSetTwo) {
        upgradeSetTwo = deal.upgradeSetTwo.upgradeSet
          .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
          .reduce((a: any, b: any) => {
            return a + b.price;
          }, 0);
        let credit = deal.upgradeSetTwo.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
        upgradeCreditTwo = credit ? credit.price : 0;
        upgradeSetTwo -= upgradeCreditTwo;

        toDecorTotalSetTwo = toTotalOption + upgradeSetTwo * 1.13;
      }
      if (deal.upgradeSetThree) {
        upgradeSetThree = deal.upgradeSetThree.upgradeSet
          .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
          .reduce((a: any, b: any) => {
            return a + b.price;
          }, 0);
        let credit = deal.upgradeSetThree.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
        upgradeCreditThree = credit ? credit.price : 0;
        upgradeSetThree -= upgradeCreditThree;

        toDecorTotalSetThree = toTotalOption + upgradeSetThree * 1.13;
      }

      toTotalOption += upgradeSetOne * 1.13 + upgradeSetTwo * 1.13 + upgradeSetThree * 1.13;
    } else {
      toTotalOption = options.reduce((a: any, b: any) => {
        return a + b.amount;
      }, unitBasePrice);

      if (deal) {
        if (deal.upgradeSetOne) {
          upgradeSetOne = deal.upgradeSetOne.upgradeSet
            .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
            .reduce((a: any, b: any) => {
              return a + b.price;
            }, 0);
          let credit = deal.upgradeSetOne.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
          upgradeCreditOne = credit ? credit.price : 0;
          upgradeSetOne -= upgradeCreditOne;
        }
        if (deal.upgradeSetTwo) {
          upgradeSetTwo = deal.upgradeSetTwo.upgradeSet
            .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
            .reduce((a: any, b: any) => {
              return a + b.price;
            }, 0);
          let credit = deal.upgradeSetTwo.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
          upgradeCreditTwo = credit ? credit.price : 0;
          upgradeSetTwo -= upgradeCreditTwo;
        }
        if (deal.upgradeSetThree) {
          upgradeSetThree = deal.upgradeSetThree.upgradeSet
            .filter((upgrade: IUpgrade) => upgrade.subCategory !== 'Credit')
            .reduce((a: any, b: any) => {
              return a + b.price;
            }, 0);
          let credit = deal.upgradeSetThree.upgradeSet.find((upgrade: IUpgrade) => upgrade.subCategory === 'Credit');
          upgradeCreditThree = credit ? credit.price : 0;
          upgradeSetThree -= upgradeCreditThree;
        }
      }

      toTotalOption += upgradeSetOne * 1.13 + upgradeSetTwo * 1.13 + upgradeSetThree * 1.13;
    }
  }

  // Adding decimal amounts leaves floating point noise (e.g. 473900.00000000006), which the dollar formats hide
  // but the written-out amounts (e.g. TPriceW) print. Round both totals to the cent.
  const roundToCents = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
  totalOption = roundToCents(totalOption);
  toTotalOption = roundToCents(toTotalOption);

  let differences: any[] = [];

  if (purchaser && toPurchaser) {
    differences = toPurchaser.map((newPurchaser: IPurchaserInfo, index: number) => {
      let oldPurchaser = purchaser[index];
      let difference: any = Object.keys(newPurchaser).filter(
        (k: any) => newPurchaser[k as keyof typeof newPurchaser] !== oldPurchaser[k as keyof typeof oldPurchaser]
      );
      let allDifferences = difference
        .map((difference: any) => {
          if (difference === 'firstName' || difference === 'lastName') {
            return {
              purchaserNumber: index + 1,
              type: 'fullName',
            };
          } else if (
            difference === 'streetAddress' ||
            difference === 'city' ||
            difference === 'province' ||
            difference === 'country' ||
            difference === 'postalCode'
          ) {
            return {
              purchaserNumber: index + 1,
              type: 'fullAddress',
            };
          } else if (difference === 'sin' || difference === 'email' || difference === 'primaryPhone' || difference === 'dob') {
            return {
              purchaserNumber: index + 1,
              type: difference,
            };
          } else return null;
        })
        .filter((difference: any) => difference !== null);

      let seen = new Set();
      let filteredDifferences = allDifferences.filter((difference: any) => {
        const key = difference.purchaserNumber + '\u0000' + difference.type;
        //                       ^---- a string you know won't be in either name or surname
        const keep = !seen.has(key);
        if (keep) {
          seen.add(key);
        }
        return keep;
      });
      return filteredDifferences;
    });
    differences = [].concat.apply([], differences);
  }

  if (purchaser) {
    soPurchaser = purchaser.find((purchaser: IPurchaserInfo) => purchaser.corp);
  }

  let maintenance;
  let projectMaintenance;

  if (project.adjustments.length) {
    projectMaintenance = project.adjustments.find(
      (adjustment: any) => adjustment.conditionField === 'maintenance' && adjustment.conditions[0].values.includes(unit.unitType)
    );
  }

  if (deal) {
    // Optional: some callers pass a partial deal (e.g. the Developer Worksheet passes { worksheet } before a deal exists)
    maintenance = deal.adjustments?.find((adjustment: IDealAdjustment) => adjustment.adjustmentType === 'FreeMaintenance');
  }

  // Check Add Purchaser Amendments to see if it should include Additional Purchasers
  switch (mergeValue.key) {
    case 'signature':
      return 'Signature';
    case 'initial':
      return 'Initial';
    case 'date':
      return 'Date';
    case 'Suite':
      return unit?.suite;
    case 'Unit':
      return unit?.unit;
    case 'Level':
      return unit?.level;
    case 'ModelType':
      return unit?.modelType;
    case 'ModelName':
      return unit?.modelName;
    case 'Tier':
      return unit?.tier ? unit.tier : '';
    case 'UnitAddress':
      return unit?.address ? unit.address : '';
    case 'UnitCity':
      return unit?.city ? unit.city : '';
    case 'UnitProvince':
      return unit?.province ? unit.province : '';
    case 'UnitPostalCode':
      return unit?.postalCode ? unit.postalCode : '';
    case 'TownhomeSuite':
      return unit?.townhomeSuite ? unit.townhomeSuite : '';
    case 'SignDate':
      return deal && deal.signDate ? convertAllDates(deal.signDate, mergeValue.format) : '';
    case 'ExecuteDate':
      return deal && deal.executeDate ? convertAllDates(deal.executeDate, mergeValue.format) : '';
    case '1PFN':
      return purchaser[0].firstName;
    case '1PLN':
      return purchaser[0].lastName;
    case '1PLNCaps':
      return purchaser[0].lastName.toUpperCase();
    case '1PfullName':
      return `${purchaser[0].firstName} ${purchaser[0].lastName}`;
    case '1PfullNameLCaps':
      return `${purchaser[0].firstName} ${purchaser[0].lastName.toUpperCase()}`;
    case '&':
      return purchaser.length >= 2 ? '&' : '';
    case 'FullAddress':
      return `${purchaser[0].unit ? purchaser[0].unit : ''} ${purchaser[0].streetAddress}, ${purchaser[0].city}, ${
        purchaser[0].province
      }, ${purchaser[0].postalCode ? purchaser[0].postalCode : ''}`;
    case 'Address':
      return `${purchaser[0].unit ? purchaser[0].unit : ''} ${purchaser[0].streetAddress}`;
    case 'City':
      return purchaser[0].city;
    case 'Province':
      return purchaser[0].province;
    case 'Country':
      return purchaser[0].country;
    case 'PCode':
      return purchaser[0].postalCode;
    case 'Email':
      return purchaser[0].email;
    case 'Dob':
      return convertAllDates(purchaser[0].dob, mergeValue.format);
    case 'SIN':
      return purchaser[0].sin ? purchaser[0].sin : '';
    case 'Phone':
      return purchaser[0].primaryPhone;
    case 'Occupation':
      return purchaser[0].occupation;
    case 'Company':
      return purchaser[0].company ? purchaser[0].company : '';
    case 'IDType':
      return purchaser[0].idType;
    case 'IDJurisdiction':
      return purchaser[0].idJurisdiction;
    case 'IDNumber':
      return purchaser[0].idNumber;
    case 'IDExpiry':
      return convertAllDates(purchaser[0].idExpiry, mergeValue.format);
    case '2PDob':
      return purchaser.length >= 2 ? convertAllDates(purchaser[1].dob, mergeValue.format) : '';
    case '2PFN':
      return purchaser.length >= 2 ? purchaser[1].firstName : '';
    case '2PLN':
      return purchaser.length >= 2 ? purchaser[1].lastName : '';
    case '2PLNCaps':
      return purchaser.length >= 2 ? purchaser[1].lastName.toUpperCase() : '';
    case '2PfullName':
      return purchaser.length >= 2 ? `${purchaser[1].firstName} ${purchaser[1].lastName}` : '';
    case '2PfullNameLCaps':
      return purchaser.length >= 2 ? `${purchaser[1].firstName} ${purchaser[1].lastName.toUpperCase()}` : '';
    case '2PSIN':
      return purchaser.length >= 2 ? purchaser[1].sin : '';
    // Purchasers have no company field; the employer is what the purchaser forms collect as their company
    case '2PCompany':
      return purchaser.length >= 2 ? purchaser[1].employer || '' : '';
    case '2POccupation':
      return purchaser.length >= 2 ? purchaser[1].occupation || '' : '';
    case '1PEmployer':
      return purchaser.length >= 1 ? purchaser[0].employer || '' : '';
    case '2PEmployer':
      return purchaser.length >= 2 ? purchaser[1].employer || '' : '';
    // The deal's worksheet wishlist choices, e.g. "2B+D (A2, A3)" - unit type plus model types when there are any
    case 'WorksheetChoice1':
    case 'WorksheetChoice2':
    case 'WorksheetChoice3': {
      const choice = deal?.worksheet?.wishlistChoices?.[parseInt(mergeValue.key.slice(-1), 10) - 1];
      if (!choice) return '';
      const modelTypes = (choice.modelTypes || []).filter(Boolean);
      return [choice.unitType, modelTypes.length ? `(${modelTypes.join(', ')})` : ''].filter(Boolean).join(' ');
    }
    case '2PFullAddress':
      return purchaser.length >= 2
        ? `${purchaser[1].unit ? purchaser[1].unit : ''} ${purchaser[1].streetAddress}, ${purchaser[1].city}, ${purchaser[1].province}, ${
            purchaser[1].postalCode ? purchaser[1].postalCode : ''
          }`
        : '';
    case '2PAddress':
      return purchaser.length >= 2 ? `${purchaser[1].unit ? purchaser[1].unit : ''} ${purchaser[1].streetAddress}` : '';
    case '2PCity':
      return purchaser.length >= 2 ? purchaser[1].city : '';
    case '2PProvince':
      return purchaser.length >= 2 ? purchaser[1].province : '';
    case '2PPCode':
      return purchaser.length >= 2 ? purchaser[1].postalCode : '';
    case '2PPhone':
      return purchaser.length >= 2 ? purchaser[1].primaryPhone : '';
    case '2PEmail':
      return purchaser.length >= 2 ? purchaser[1].email : '';
    case '2PIDNumber':
      return purchaser.length >= 2 ? purchaser[1].idNumber : '';
    case '1PNonCorpDob':
      return !purchaser[0].corp ? convertAllDates(purchaser[0].dob, mergeValue.format) : '';
    case '1PNonCorpFN':
      return !purchaser[0].corp ? purchaser[0].firstName : '';
    case '1PNonCorpLN':
      return !purchaser[0].corp ? purchaser[0].lastName : '';
    case '1PNonCorpLNCaps':
      return !purchaser[0].corp ? purchaser[0].lastName.toUpperCase() : '';
    case '1PNonCorpfullName':
      return !purchaser[0].corp ? `${purchaser[0].firstName} ${purchaser[0].lastName}` : '';
    case '1PfullNameNonCorpLCaps':
      return !purchaser[0].corp ? `${purchaser[0].firstName} ${purchaser[0].lastName.toUpperCase()}` : '';
    case '1PNonCorpSIN':
      return !purchaser[0].corp ? purchaser[0].sin : '';
    case '1PNonCorpFullAddress':
      return !purchaser[0].corp
        ? `${purchaser[0].unit ? purchaser[0].unit : ''} ${purchaser[0].streetAddress}, ${purchaser[0].city}, ${purchaser[0].province}, ${
            purchaser[0].postalCode ? purchaser[0].postalCode : ''
          }`
        : '';
    case '1PNonCorpAddress':
      return !purchaser[0].corp ? `${purchaser[0].unit ? purchaser[0].unit : ''} ${purchaser[0].streetAddress}` : '';
    case '1PNonCorpCity':
      return !purchaser[0].corp ? purchaser[0].city : '';
    case '1PNonCorpProvince':
      return !purchaser[0].corp ? purchaser[0].province : '';
    case '1PNonCorpCountry':
      return !purchaser[0].corp ? purchaser[0].country : '';
    case '1PNonCorpPCode':
      return !purchaser[0].corp ? purchaser[0].postalCode : '';
    case '1PNonCorpPhone':
      return !purchaser[0].corp ? purchaser[0].primaryPhone : '';
    case '1PNonCorpEmail':
      return !purchaser[0].corp ? purchaser[0].email : '';
    case '2PNonCorpDob':
      return purchaser.length >= 2 && !purchaser[1].corp ? convertAllDates(purchaser[1].dob, mergeValue.format) : '';
    case '2PNonCorpFN':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].firstName : '';
    case '2PNonCorpLN':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].lastName : '';
    case '2PNonCorpLNCaps':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].lastName.toUpperCase() : '';
    case '2PNonCorpfullName':
      return purchaser.length >= 2 && !purchaser[1].corp ? `${purchaser[1].firstName} ${purchaser[1].lastName}` : '';
    case '2PfullNameNonCorpLCaps':
      return purchaser.length >= 2 && !purchaser[1].corp ? `${purchaser[1].firstName} ${purchaser[1].lastName.toUpperCase()}` : '';
    case '2PNonCorpSIN':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].sin : '';
    case '2PNonCorpFullAddress':
      return purchaser.length >= 2 && !purchaser[1].corp
        ? `${purchaser[1].unit ? purchaser[1].unit : ''} ${purchaser[1].streetAddress}, ${purchaser[1].city}, ${purchaser[1].province}, ${
            purchaser[1].postalCode ? purchaser[1].postalCode : ''
          }`
        : '';
    case '2PNonCorpAddress':
      return purchaser.length >= 2 && !purchaser[1].corp
        ? `${purchaser[1].unit ? purchaser[1].unit : ''} ${purchaser[1].streetAddress}`
        : '';
    case '2PNonCorpCity':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].city : '';
    case '2PNonCorpProvince':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].province : '';
    case '2PNonCorpCountry':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].country : '';
    case '2PNonCorpPCode':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].postalCode : '';
    case '2PNonCorpPhone':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].primaryPhone : '';
    case '2PNonCorpEmail':
      return purchaser.length >= 2 && !purchaser[1].corp ? purchaser[1].email : '';
    case '3PFN':
      return purchaser.length >= 3 ? purchaser[2].firstName : '';
    case '3PLN':
      return purchaser.length >= 3 ? purchaser[2].lastName : '';
    case '3PLNCaps':
      return purchaser.length >= 3 ? purchaser[2].lastName.toUpperCase() : '';
    case '3PfullName':
      return purchaser.length >= 3 ? `${purchaser[2].firstName} ${purchaser[2].lastName}` : '';
    case '3PfullNameLCaps':
      return purchaser.length >= 3 ? `${purchaser[2].firstName} ${purchaser[2].lastName.toUpperCase()}` : '';
    case '3PDob':
      return purchaser.length >= 3 ? convertAllDates(purchaser[2].dob, mergeValue.format) : '';
    case '3PSIN':
      return purchaser.length >= 3 ? purchaser[2].sin : '';
    case '3PFullAddress':
      return purchaser.length >= 3
        ? `${purchaser[2].unit ? purchaser[2].unit : ''} ${purchaser[2].streetAddress}, ${purchaser[2].city}, ${purchaser[2].province}, ${
            purchaser[2].postalCode ? purchaser[2].postalCode : ''
          }`
        : '';
    case '3PAddress':
      return purchaser.length >= 3 ? `${purchaser[2].unit ? purchaser[2].unit : ''} ${purchaser[2].streetAddress}` : '';
    case '3PCity':
      return purchaser.length >= 3 ? purchaser[2].city : '';
    case '3PProvince':
      return purchaser.length >= 3 ? purchaser[2].province : '';
    case '3PPCode':
      return purchaser.length >= 3 ? purchaser[2].postalCode : '';
    case '3PPhone':
      return purchaser.length >= 3 ? purchaser[2].primaryPhone : '';
    case '3PEmail':
      return purchaser.length >= 3 ? purchaser[2].email : '';
    case '3PIDNumber':
      return purchaser.length >= 3 ? purchaser[2].idNumber : '';
    case '4PFN':
      return purchaser.length >= 4 ? purchaser[3].firstName : '';
    case '4PLN':
      return purchaser.length >= 4 ? purchaser[3].lastName : '';
    case '4PLNCaps':
      return purchaser.length >= 4 ? purchaser[3].lastName.toUpperCase() : '';
    case '4PfullName':
      return purchaser.length >= 4 ? `${purchaser[3].firstName} ${purchaser[3].lastName}` : '';
    case '4PfullNameLCaps':
      return purchaser.length >= 4 ? `${purchaser[3].firstName} ${purchaser[3].lastName.toUpperCase()}` : '';
    case '4PDob':
      return purchaser.length >= 4 ? convertAllDates(purchaser[3].dob, mergeValue.format) : '';
    case '4PSIN':
      return purchaser.length >= 4 ? purchaser[3].sin : '';
    case '4PFullAddress':
      return purchaser.length >= 4
        ? `${purchaser[3].unit ? purchaser[3].unit : ''} ${purchaser[3].streetAddress}, ${purchaser[3].city}, ${purchaser[3].province}, ${
            purchaser[3].postalCode ? purchaser[3].postalCode : ''
          }`
        : '';
    case '4PAddress':
      return purchaser.length >= 4 ? `${purchaser[3].unit ? purchaser[3].unit : ''} ${purchaser[3].streetAddress}` : '';
    case '4PCity':
      return purchaser.length >= 4 ? purchaser[3].city : '';
    case '4PProvince':
      return purchaser.length >= 4 ? purchaser[3].province : '';
    case '4PPCode':
      return purchaser.length >= 4 ? purchaser[3].postalCode : '';
    case '4PPhone':
      return purchaser.length >= 4 ? purchaser[3].primaryPhone : '';
    case '4PEmail':
      return purchaser.length >= 4 ? purchaser[3].email : '';
    case '4PIDNumber':
      return purchaser.length >= 4 ? purchaser[3].idNumber : '';
    case 'SalesRep':
      return deal.salesRep ? deal.salesRep.fullName : 'Brian Shew';
    case 'ProjectAddress':
      return `${project.addresses[0].streetAddress}, ${project.addresses[0].city}, ${project.addresses[0].province}, ${
        project.addresses[0].postalCode ? project.addresses[0].postalCode : ''
      }`;
    case 'ProjectAddressSuite':
      return `${unit?.suite}-${project.addresses[0].streetAddress}, ${project.addresses[0].city}, ${project.addresses[0].province}, ${
        project.addresses[0].postalCode ? project.addresses[0].postalCode : ''
      }`;
    case 'ProjectStreetAddress':
      return `${project.addresses[0].streetAddress}`;
    case 'ProjectCity':
      return `${project.addresses[0].city}`;
    case 'ProjectProvince':
      return `${project.addresses[0].province}`;
    case 'ProjectPCode':
      return `${project.addresses[0].postalCode}`;
    case 'Directors':
      return purchaser[0].directors ? `${purchaser[0].directors}` : '';
    case '1P2PfullName':
      return purchaser.length >= 2
        ? `${purchaser[0].firstName} ${purchaser[0].lastName} & ${purchaser[1].firstName} ${purchaser[1].lastName}`
        : `${purchaser[0].firstName} ${purchaser[0].lastName}`;
    case '1P3PfullName':
      return purchaser.length >= 3
        ? `${purchaser[0].firstName} ${purchaser[0].lastName} & ${purchaser[2].firstName} ${purchaser[2].lastName}`
        : `${purchaser[0].firstName} ${purchaser[0].lastName}`;
    case '2P3PfullName':
      return purchaser.length >= 3
        ? `${purchaser[1].firstName} ${purchaser[1].lastName} & ${purchaser[2].firstName} ${purchaser[2].lastName}`
        : `${purchaser[0].firstName} ${purchaser[0].lastName}`;
    case '1P2P3PfullName':
      return purchaser.length >= 3
        ? `${purchaser[0].firstName} ${purchaser[0].lastName} & ${purchaser[1].firstName} ${purchaser[1].lastName} & ${purchaser[2].firstName} ${purchaser[2].lastName}`
        : `${purchaser[0].firstName} ${purchaser[0].lastName}`;
    case '3P4PfullName':
      return purchaser.length >= 4
        ? `${purchaser[2].firstName} ${purchaser[2].lastName} & ${purchaser[3].firstName} ${purchaser[3].lastName}`
        : `${purchaser[0].firstName} ${purchaser[0].lastName}`;
    case '1P2PfullNameLCaps':
      return purchaser.length >= 2
        ? `${purchaser[0].firstName} ${purchaser[0].lastName.toUpperCase()} & ${
            purchaser[1].firstName
          } ${purchaser[1].lastName.toUpperCase()}`
        : `${purchaser[0].firstName} ${purchaser[0].lastName.toUpperCase()}`;
    case 'AllPfullName':
      return purchaser.map((purchaser: IPurchaserInfo) => `${purchaser.firstName} ${purchaser.lastName}`).join(' & ');
    case 'AllPfullNameLCaps':
      return purchaser.map((purchaser: IPurchaserInfo) => `${purchaser.firstName} ${purchaser.lastName.toUpperCase()}`).join(' & ');
    case 'SO1fullName':
      return soPurchaser && soPurchaser.signingOfficers.length > 0 ? `${soPurchaser.signingOfficers[0].fullName}` : '';
    case 'SO1FullAddress':
      return soPurchaser && soPurchaser.signingOfficers.length > 0 ? `${soPurchaser.signingOfficers[0].streetAddress}` : '';
    case 'SO1Dob':
      return soPurchaser && soPurchaser.signingOfficers.length > 0
        ? `${convertAllDates(soPurchaser.signingOfficers[0].dob, mergeValue.format)}`
        : '';
    case 'SO1Email':
      return soPurchaser && soPurchaser.signingOfficers.length > 0 ? `${soPurchaser.signingOfficers[0].email}` : '';
    case 'SO1SIN':
      return soPurchaser && soPurchaser.signingOfficers.length > 0 ? `${soPurchaser.signingOfficers[0].sin}` : '';
    case 'SO1Phone':
      return soPurchaser && soPurchaser.signingOfficers.length > 0 ? `${soPurchaser.signingOfficers[0].primaryPhone}` : '';
    case 'SO2fullName':
      return soPurchaser && soPurchaser.signingOfficers.length > 1 ? `${soPurchaser.signingOfficers[1].fullName}` : '';
    case 'SO2FullAddress':
      return soPurchaser && soPurchaser.signingOfficers.length > 1 ? `${soPurchaser.signingOfficers[1].streetAddress}` : '';
    case 'SO2Dob':
      return soPurchaser && soPurchaser.signingOfficers.length > 1
        ? `${convertAllDates(soPurchaser.signingOfficers[1].dob, mergeValue.format)}`
        : '';
    case 'SO2Email':
      return soPurchaser && soPurchaser.signingOfficers.length > 1 ? `${soPurchaser.signingOfficers[1].email}` : '';
    case 'SO2SIN':
      return soPurchaser && soPurchaser.signingOfficers.length > 1 ? `${soPurchaser.signingOfficers[1].sin}` : '';
    case 'SO2Phone':
      return soPurchaser && soPurchaser.signingOfficers.length > 1 ? `${soPurchaser.signingOfficers[1].primaryPhone}` : '';
    case 'SO1SO2fullName':
      return soPurchaser && soPurchaser.signingOfficers.length >= 2
        ? `${soPurchaser.signingOfficers[0].fullName} & ${soPurchaser.signingOfficers[1].fullName}`
        : `${soPurchaser.signingOfficers[0].fullName}`;
    case 'ConditionalP1CorpOrPurchaserFullName':
      return soPurchaser && purchaser[0].corp && soPurchaser.email === purchaser[0].email
        ? ''
        : `${purchaser[0].firstName} ${purchaser[0].lastName}`;
    case 'ConditionalP2CorpOrPurchaserFullName':
      return soPurchaser && purchaser.length > 0 && purchaser[1].corp && soPurchaser.email === purchaser[1].email
        ? ''
        : `${purchaser[1].firstName} ${purchaser[1].lastName}`;
    case 'ConditionalCorpName':
      return soPurchaser ? `${soPurchaser.firstName} ${soPurchaser.lastName}` : '';
    case 'ConditionalCorpDob':
      return soPurchaser ? `${convertAllDates(soPurchaser.dob, mergeValue.format)}` : '';
    case 'ConditionalCorpPhone':
      return soPurchaser ? `${soPurchaser.primaryPhone}` : '';
    case 'ConditionalCorpAddress':
      return soPurchaser
        ? `${soPurchaser.streetAddress}, ${soPurchaser.city}, ${soPurchaser.province}, ${
            soPurchaser.postalCode ? soPurchaser.postalCode : ''
          }`
        : '';
    case 'ConditionalCorpEmail':
      return soPurchaser ? `${soPurchaser.email}` : '';
    case 'ConditionalCorpNumber':
      return soPurchaser ? `${soPurchaser.idNumber}` : '';
    case 'MinMortgage':
      return currencyFor(mergeValue, numToCurrency).format(totalOption - deposit.reduce((accum: any, item: any) => accum + item.amount, 0));
    // MinMortgage with the base price net of HST: netHST(base price) + all option amounts - all deposits
    case 'netMinMortgage': {
      const netOptionsTotal = (options || []).reduce((total: number, option: any) => total + (Number(option.amount) || 0), 0);
      const depositsTotal = (deposit || []).reduce((total: number, item: any) => total + (Number(item.amount) || 0), 0);
      return currencyFor(mergeValue, numToCurrency).format(netHST(unit.basePrice, project) + netOptionsTotal - depositsTotal);
    }
    case 'Deposit1':
      return deposit.length >= 1 ? currencyFor(mergeValue, numToCurrency).format(deposit[0].amount).toString() : '0';
    case 'Deposit1W':
      return deposit.length >= 1 ? numberToEnglish(deposit[0].amount) : '';
    case 'Deposit1Date':
      return deposit.length >= 1 ? convertAllDates(deposit[0].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit1DateWords':
      return deposit.length >= 1 ? convertAllDates(deposit[0].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit1%':
      return deposit.length >= 1 ? `${((deposit[0].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit1Decimal':
      return deposit.length >= 1 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[0].amount).toString() : '';
    case 'Deposit1DaysDue':
      return deposit.length >= 1 ? deposit[0].daysDue.toString() : '';
    case 'Deposit2':
      return deposit.length >= 2 ? currencyFor(mergeValue, numToCurrency).format(deposit[1].amount).toString() : '0';
    case 'Deposit2W':
      return deposit.length >= 2 ? numberToEnglish(deposit[1].amount) : '';
    case 'Deposit2Date':
      return deposit.length >= 2 ? convertAllDates(deposit[1].dueDate, mergeValue.format) : '';
    case 'Deposit2DateWords':
      return deposit.length >= 2 ? convertAllDates(deposit[1].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit2%':
      return deposit.length >= 2 ? `${(((deposit[1].amount + deposit[0].amount) / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit2Decimal':
      return deposit.length >= 2 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[1].amount).toString() : '';
    case 'Deposit2DaysDue':
      return deposit.length >= 2 ? deposit[1].daysDue.toString() : '';
    case 'Deposit3':
      return deposit.length >= 3 ? currencyFor(mergeValue, numToCurrency).format(deposit[2].amount).toString() : '0';
    case 'Deposit3W':
      return deposit.length >= 3 ? numberToEnglish(deposit[2].amount) : '';
    case 'Deposit3Date':
      return deposit.length >= 3 ? convertAllDates(deposit[2].dueDate, mergeValue.format) : '';
    case 'Deposit3DateWords':
      return deposit.length >= 3 ? convertAllDates(deposit[2].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit3%':
      return deposit.length >= 3 ? `${((deposit[2].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit3Decimal':
      return deposit.length >= 3 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[2].amount).toString() : '';
    case 'Deposit3DaysDue':
      return deposit.length >= 3 ? deposit[2].daysDue.toString() : '';
    case 'Deposit4':
      return deposit.length >= 4 ? currencyFor(mergeValue, numToCurrency).format(deposit[3].amount).toString() : '0';
    case 'Deposit4W':
      return deposit.length >= 4 ? numberToEnglish(deposit[3].amount) : '';
    case 'Deposit4Date':
      return deposit.length >= 4 ? convertAllDates(deposit[3].dueDate, mergeValue.format) : '';
    case 'Deposit4DateWords':
      return deposit.length >= 4 ? convertAllDates(deposit[3].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit4%':
      return deposit.length >= 4 ? `${((deposit[3].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit4Decimal':
      return deposit.length >= 4 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[3].amount).toString() : '';
    case 'Deposit4DaysDue':
      return deposit.length >= 4 ? deposit[3].daysDue.toString() : '';
    case 'Deposit5':
      return deposit.length >= 5 ? currencyFor(mergeValue, numToCurrency).format(deposit[4].amount).toString() : '';
    case 'Deposit5W':
      return deposit.length >= 5 ? numberToEnglish(deposit[4].amount) : '';
    case 'Deposit5Date':
      return deposit.length >= 5 ? convertAllDates(deposit[4].dueDate, mergeValue.format) : '';
    case 'Deposit5DateWords':
      return deposit.length >= 5 ? convertAllDates(deposit[4].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit5%':
      return deposit.length >= 5 ? `${((deposit[4].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit5Decimal':
      return deposit.length >= 5 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[4].amount).toString() : '';
    case 'Deposit5DaysDue':
      return deposit.length >= 5 ? deposit[4].daysDue.toString() : '';
    case 'Deposit6':
      return deposit.length >= 6 ? currencyFor(mergeValue, numToCurrency).format(deposit[5].amount).toString() : '';
    case 'Deposit6W':
      return deposit.length >= 6 ? numberToEnglish(deposit[5].amount) : '';
    case 'Deposit6Date':
      return deposit.length >= 6 ? convertAllDates(deposit[5].dueDate, mergeValue.format) : '';
    case 'Deposit6DateWords':
      return deposit.length >= 6 ? convertAllDates(deposit[5].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit6%':
      return deposit.length >= 6 ? `${((deposit[5].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit6Decimal':
      return deposit.length >= 6 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[5].amount).toString() : '';
    case 'Deposit6DaysDue':
      return deposit.length >= 6 ? deposit[5].daysDue.toString() : '';
    case 'Deposit7':
      return deposit.length >= 7 ? currencyFor(mergeValue, numToCurrency).format(deposit[6].amount).toString() : '';
    case 'Deposit7W':
      return deposit.length >= 7 ? numberToEnglish(deposit[6].amount) : '';
    case 'Deposit7Date':
      return deposit.length >= 7 ? convertAllDates(deposit[6].dueDate, mergeValue.format) : '';
    case 'Deposit7DateWords':
      return deposit.length >= 7 ? convertAllDates(deposit[6].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit7%':
      return deposit.length >= 7 ? `${((deposit[6].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit7Decimal':
      return deposit.length >= 7 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[6].amount).toString() : '';
    case 'Deposit7DaysDue':
      return deposit.length >= 7 ? deposit[6].daysDue.toString() : '';
    case 'Deposit8':
      return deposit.length >= 8 ? currencyFor(mergeValue, numToCurrency).format(deposit[7].amount).toString() : '';
    case 'Deposit8W':
      return deposit.length >= 8 ? numberToEnglish(deposit[7].amount) : '';
    case 'Deposit8Date':
      return deposit.length >= 8 ? convertAllDates(deposit[7].dueDate, mergeValue.format) : '';
    case 'Deposit8DateWords':
      return deposit.length >= 8 ? convertAllDates(deposit[7].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit8%':
      return deposit.length >= 8 ? `${((deposit[7].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit8Decimal':
      return deposit.length >= 8 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[7].amount).toString() : '';
    case 'Deposit9':
      return deposit.length >= 9 ? currencyFor(mergeValue, numToCurrency).format(deposit[8].amount).toString() : '';
    case 'Deposit9W':
      return deposit.length >= 9 ? numberToEnglish(deposit[8].amount) : '';
    case 'Deposit9Date':
      return deposit.length >= 9 ? convertAllDates(deposit[8].dueDate, mergeValue.format) : '';
    case 'Deposit9DateWords':
      return deposit.length >= 9 ? convertAllDates(deposit[8].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit9%':
      return deposit.length >= 9 ? `${((deposit[8].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit9Decimal':
      return deposit.length >= 9 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[8].amount).toString() : '';
    case 'Deposit10':
      return deposit.length >= 10 ? currencyFor(mergeValue, numToCurrency).format(deposit[9].amount).toString() : '';
    case 'Deposit10W':
      return deposit.length >= 10 ? numberToEnglish(deposit[9].amount) : '';
    case 'Deposit10Date':
      return deposit.length >= 10 ? convertAllDates(deposit[9].dueDate, mergeValue.format) : '';
    case 'Deposit10DateWords':
      return deposit.length >= 10 ? convertAllDates(deposit[9].dueDate, mergeValue.format) : convertAllDates(new Date(), mergeValue.format);
    case 'Deposit10%':
      return deposit.length >= 10 ? `${((deposit[9].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit10Decimal':
      return deposit.length >= 10 ? currencyFor(mergeValue, numToCurrencyDecimal).format(deposit[9].amount).toString() : '';
    case 'Deposit11':
      return deposit.length >= 11 ? currencyFor(mergeValue, numToCurrency).format(deposit[10].amount).toString() : '';
    case 'Deposit11W':
      return deposit.length >= 11 ? numberToEnglish(deposit[10].amount) : '';
    case 'Deposit11Date':
      return deposit.length >= 11 ? convertAllDates(deposit[10].dueDate, mergeValue.format) : '';
    case 'Deposit11%':
      return deposit.length >= 11 ? `${((deposit[10].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit12':
      return deposit.length >= 12 ? currencyFor(mergeValue, numToCurrency).format(deposit[11].amount).toString() : '';
    case 'Deposit12W':
      return deposit.length >= 12 ? numberToEnglish(deposit[11].amount) : '';
    case 'Deposit12Date':
      return deposit.length >= 12 ? convertAllDates(deposit[11].dueDate, mergeValue.format) : '';
    case 'Deposit12%':
      return deposit.length >= 12 ? `${((deposit[11].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit13':
      return deposit.length >= 13 ? currencyFor(mergeValue, numToCurrency).format(deposit[12].amount).toString() : '';
    case 'Deposit13W':
      return deposit.length >= 13 ? numberToEnglish(deposit[12].amount) : '';
    case 'Deposit13Date':
      return deposit.length >= 13 ? convertAllDates(deposit[12].dueDate, mergeValue.format) : '';
    case 'Deposit13%':
      return deposit.length >= 13 ? `${((deposit[12].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit14':
      return deposit.length >= 14 ? currencyFor(mergeValue, numToCurrency).format(deposit[13].amount).toString() : '';
    case 'Deposit14W':
      return deposit.length >= 14 ? numberToEnglish(deposit[13].amount) : '';
    case 'Deposit14Date':
      return deposit.length >= 14 ? convertAllDates(deposit[13].dueDate, mergeValue.format) : '';
    case 'Deposit14%':
      return deposit.length >= 14 ? `${((deposit[13].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit15':
      return deposit.length >= 15 ? currencyFor(mergeValue, numToCurrency).format(deposit[14].amount).toString() : '';
    case 'Deposit15W':
      return deposit.length >= 15 ? numberToEnglish(deposit[14].amount) : '';
    case 'Deposit15Date':
      return deposit.length >= 15 ? convertAllDates(deposit[14].dueDate, mergeValue.format) : '';
    case 'Deposit15%':
      return deposit.length >= 15 ? `${((deposit[14].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit16':
      return deposit.length >= 16 ? currencyFor(mergeValue, numToCurrency).format(deposit[15].amount).toString() : '';
    case 'Deposit16W':
      return deposit.length >= 16 ? numberToEnglish(deposit[15].amount) : '';
    case 'Deposit16Date':
      return deposit.length >= 16 ? convertAllDates(deposit[15].dueDate, mergeValue.format) : '';
    case 'Deposit16%':
      return deposit.length >= 16 ? `${((deposit[15].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit17':
      return deposit.length >= 17 ? currencyFor(mergeValue, numToCurrency).format(deposit[16].amount).toString() : '';
    case 'Deposit17W':
      return deposit.length >= 17 ? numberToEnglish(deposit[16].amount) : '';
    case 'Deposit17Date':
      return deposit.length >= 17 ? convertAllDates(deposit[16].dueDate, mergeValue.format) : '';
    case 'Deposit17%':
      return deposit.length >= 17 ? `${((deposit[16].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit18':
      return deposit.length >= 18 ? currencyFor(mergeValue, numToCurrency).format(deposit[17].amount).toString() : '';
    case 'Deposit18W':
      return deposit.length >= 18 ? numberToEnglish(deposit[17].amount) : '';
    case 'Deposit18Date':
      return deposit.length >= 18 ? convertAllDates(deposit[17].dueDate, mergeValue.format) : '';
    case 'Deposit18%':
      return deposit.length >= 18 ? `${((deposit[17].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit19':
      return deposit.length >= 19 ? currencyFor(mergeValue, numToCurrency).format(deposit[18].amount).toString() : '';
    case 'Deposit19W':
      return deposit.length >= 19 ? numberToEnglish(deposit[18].amount) : '';
    case 'Deposit19Date':
      return deposit.length >= 19 ? convertAllDates(deposit[18].dueDate, mergeValue.format) : '';
    case 'Deposit19%':
      return deposit.length >= 19 ? `${((deposit[18].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit20':
      return deposit.length >= 20 && deposit[19].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[19].amount).toString() : '';
    case 'Deposit20W':
      return deposit.length >= 20 && deposit[19].name !== 'Occupancy' ? numberToEnglish(deposit[19].amount) : '';
    case 'Deposit20Date':
      return deposit.length >= 20 && deposit[19].name !== 'Occupancy' ? convertAllDates(deposit[19].dueDate, mergeValue.format) : '';
    case 'Deposit20%':
      return deposit.length >= 20 && deposit[19].name !== 'Occupancy' ? `${((deposit[19].amount / totalOption) * 100).toFixed(1)}%` : '';

    case 'Deposit21':
      return deposit.length >= 21 && deposit[20].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[20].amount).toString() : '';
    case 'Deposit21W':
      return deposit.length >= 21 && deposit[20].name !== 'Occupancy' ? numberToEnglish(deposit[20].amount) : '';
    case 'Deposit21Date':
      return deposit.length >= 21 && deposit[20].name !== 'Occupancy' ? convertAllDates(deposit[20].dueDate, mergeValue.format) : '';
    case 'Deposit21%':
      return deposit.length >= 21 && deposit[20].name !== 'Occupancy' ? `${((deposit[20].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit22':
      return deposit.length >= 22 && deposit[21].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[21].amount).toString() : '';
    case 'Deposit22W':
      return deposit.length >= 22 && deposit[21].name !== 'Occupancy' ? numberToEnglish(deposit[21].amount) : '';
    case 'Deposit22Date':
      return deposit.length >= 22 && deposit[21].name !== 'Occupancy' ? convertAllDates(deposit[21].dueDate, mergeValue.format) : '';
    case 'Deposit22%':
      return deposit.length >= 22 && deposit[21].name !== 'Occupancy'
        ? `${(((deposit[21].amount + deposit[0].amount) / totalOption) * 100).toFixed(1)}%`
        : '';
    case 'Deposit23':
      return deposit.length >= 23 && deposit[22].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[22].amount).toString() : '';
    case 'Deposit23W':
      return deposit.length >= 23 && deposit[22].name !== 'Occupancy' ? numberToEnglish(deposit[22].amount) : '';
    case 'Deposit23Date':
      return deposit.length >= 23 && deposit[22].name !== 'Occupancy' ? convertAllDates(deposit[22].dueDate, mergeValue.format) : '';
    case 'Deposit23%':
      return deposit.length >= 23 && deposit[22].name !== 'Occupancy' ? `${((deposit[22].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit24':
      return deposit.length >= 24 && deposit[23].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[23].amount).toString() : '';
    case 'Deposit24W':
      return deposit.length >= 24 && deposit[23].name !== 'Occupancy' ? numberToEnglish(deposit[23].amount) : '';
    case 'Deposit24Date':
      return deposit.length >= 24 && deposit[23].name !== 'Occupancy' ? convertAllDates(deposit[23].dueDate, mergeValue.format) : '';
    case 'Deposit24%':
      return deposit.length >= 24 && deposit[23].name !== 'Occupancy' ? `${((deposit[23].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit25':
      return deposit.length >= 25 && deposit[24].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[24].amount).toString() : '';
    case 'Deposit25W':
      return deposit.length >= 25 && deposit[24].name !== 'Occupancy' ? numberToEnglish(deposit[24].amount) : '';
    case 'Deposit25Date':
      return deposit.length >= 25 && deposit[24].name !== 'Occupancy' ? convertAllDates(deposit[24].dueDate, mergeValue.format) : '';
    case 'Deposit25%':
      return deposit.length >= 25 && deposit[24].name !== 'Occupancy' ? `${((deposit[24].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit26':
      return deposit.length >= 26 && deposit[25].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[25].amount).toString() : '';
    case 'Deposit26W':
      return deposit.length >= 26 && deposit[25].name !== 'Occupancy' ? numberToEnglish(deposit[25].amount) : '';
    case 'Deposit26Date':
      return deposit.length >= 26 && deposit[25].name !== 'Occupancy' ? convertAllDates(deposit[25].dueDate, mergeValue.format) : '';
    case 'Deposit26%':
      return deposit.length >= 26 && deposit[25].name !== 'Occupancy' ? `${((deposit[25].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit27':
      return deposit.length >= 27 && deposit[26].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[26].amount).toString() : '';
    case 'Deposit27W':
      return deposit.length >= 27 && deposit[26].name !== 'Occupancy' ? numberToEnglish(deposit[26].amount) : '';
    case 'Deposit27Date':
      return deposit.length >= 27 && deposit[26].name !== 'Occupancy' ? convertAllDates(deposit[26].dueDate, mergeValue.format) : '';
    case 'Deposit27%':
      return deposit.length >= 27 && deposit[26].name !== 'Occupancy' ? `${((deposit[26].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit28':
      return deposit.length >= 28 && deposit[27].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[27].amount).toString() : '';
    case 'Deposit28W':
      return deposit.length >= 28 && deposit[27].name !== 'Occupancy' ? numberToEnglish(deposit[27].amount) : '';
    case 'Deposit28Date':
      return deposit.length >= 28 && deposit[27].name !== 'Occupancy' ? convertAllDates(deposit[27].dueDate, mergeValue.format) : '';
    case 'Deposit28%':
      return deposit.length >= 28 && deposit[27].name !== 'Occupancy' ? `${((deposit[27].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit29':
      return deposit.length >= 29 && deposit[28].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[28].amount).toString() : '';
    case 'Deposit29W':
      return deposit.length >= 29 && deposit[28].name !== 'Occupancy' ? numberToEnglish(deposit[28].amount) : '';
    case 'Deposit29Date':
      return deposit.length >= 29 && deposit[28].name !== 'Occupancy' ? convertAllDates(deposit[28].dueDate, mergeValue.format) : '';
    case 'Deposit29%':
      return deposit.length >= 29 && deposit[28].name !== 'Occupancy' ? `${((deposit[28].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit30':
      return deposit.length >= 30 && deposit[29].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[29].amount).toString() : '';
    case 'Deposit30W':
      return deposit.length >= 30 && deposit[29].name !== 'Occupancy' ? numberToEnglish(deposit[29].amount) : '';
    case 'Deposit30Date':
      return deposit.length >= 30 && deposit[29].name !== 'Occupancy' ? convertAllDates(deposit[29].dueDate, mergeValue.format) : '';
    case 'Deposit30%':
      return deposit.length >= 30 && deposit[29].name !== 'Occupancy' ? `${((deposit[29].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit31':
      return deposit.length >= 31 && deposit[30].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[30].amount).toString() : '';
    case 'Deposit31W':
      return deposit.length >= 31 && deposit[30].name !== 'Occupancy' ? numberToEnglish(deposit[30].amount) : '';
    case 'Deposit31Date':
      return deposit.length >= 31 && deposit[30].name !== 'Occupancy' ? convertAllDates(deposit[30].dueDate, mergeValue.format) : '';
    case 'Deposit31%':
      return deposit.length >= 31 && deposit[30].name !== 'Occupancy' ? `${((deposit[30].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit32':
      return deposit.length >= 32 && deposit[31].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[31].amount).toString() : '';
    case 'Deposit32W':
      return deposit.length >= 32 && deposit[31].name !== 'Occupancy' ? numberToEnglish(deposit[31].amount) : '';
    case 'Deposit32Date':
      return deposit.length >= 32 && deposit[31].name !== 'Occupancy' ? convertAllDates(deposit[31].dueDate, mergeValue.format) : '';
    case 'Deposit32%':
      return deposit.length >= 32 && deposit[31].name !== 'Occupancy' ? `${((deposit[31].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit33':
      return deposit.length >= 33 && deposit[32].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[32].amount).toString() : '';
    case 'Deposit33W':
      return deposit.length >= 33 && deposit[32].name !== 'Occupancy' ? numberToEnglish(deposit[32].amount) : '';
    case 'Deposit33Date':
      return deposit.length >= 33 && deposit[32].name !== 'Occupancy' ? convertAllDates(deposit[32].dueDate, mergeValue.format) : '';
    case 'Deposit33%':
      return deposit.length >= 33 && deposit[32].name !== 'Occupancy' ? `${((deposit[32].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit34':
      return deposit.length >= 34 && deposit[33].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[33].amount).toString() : '';
    case 'Deposit34W':
      return deposit.length >= 34 && deposit[33].name !== 'Occupancy' ? numberToEnglish(deposit[33].amount) : '';
    case 'Deposit34Date':
      return deposit.length >= 34 && deposit[33].name !== 'Occupancy' ? convertAllDates(deposit[33].dueDate, mergeValue.format) : '';
    case 'Deposit34%':
      return deposit.length >= 34 && deposit[33].name !== 'Occupancy' ? `${((deposit[33].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit35':
      return deposit.length >= 35 && deposit[34].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[34].amount).toString() : '';
    case 'Deposit35W':
      return deposit.length >= 35 && deposit[34].name !== 'Occupancy' ? numberToEnglish(deposit[34].amount) : '';
    case 'Deposit35Date':
      return deposit.length >= 35 && deposit[34].name !== 'Occupancy' ? convertAllDates(deposit[34].dueDate, mergeValue.format) : '';
    case 'Deposit35%':
      return deposit.length >= 35 && deposit[34].name !== 'Occupancy' ? `${((deposit[34].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit36':
      return deposit.length >= 36 && deposit[35].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[35].amount).toString() : '';
    case 'Deposit36W':
      return deposit.length >= 36 && deposit[35].name !== 'Occupancy' ? numberToEnglish(deposit[35].amount) : '';
    case 'Deposit36Date':
      return deposit.length >= 36 && deposit[35].name !== 'Occupancy' ? convertAllDates(deposit[35].dueDate, mergeValue.format) : '';
    case 'Deposit36%':
      return deposit.length >= 36 && deposit[35].name !== 'Occupancy' ? `${((deposit[35].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit37':
      return deposit.length >= 37 && deposit[36].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[36].amount).toString() : '';
    case 'Deposit37W':
      return deposit.length >= 37 && deposit[36].name !== 'Occupancy' ? numberToEnglish(deposit[36].amount) : '';
    case 'Deposit37Date':
      return deposit.length >= 37 && deposit[36].name !== 'Occupancy' ? convertAllDates(deposit[36].dueDate, mergeValue.format) : '';
    case 'Deposit37%':
      return deposit.length >= 37 && deposit[36].name !== 'Occupancy' ? `${((deposit[36].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit38':
      return deposit.length >= 38 && deposit[37].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[37].amount).toString() : '';
    case 'Deposit38W':
      return deposit.length >= 38 && deposit[37].name !== 'Occupancy' ? numberToEnglish(deposit[37].amount) : '';
    case 'Deposit38Date':
      return deposit.length >= 38 && deposit[37].name !== 'Occupancy' ? convertAllDates(deposit[37].dueDate, mergeValue.format) : '';
    case 'Deposit38%':
      return deposit.length >= 38 && deposit[37].name !== 'Occupancy' ? `${((deposit[37].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit39':
      return deposit.length >= 39 && deposit[38].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[38].amount).toString() : '';
    case 'Deposit39W':
      return deposit.length >= 39 && deposit[38].name !== 'Occupancy' ? numberToEnglish(deposit[38].amount) : '';
    case 'Deposit39Date':
      return deposit.length >= 39 && deposit[38].name !== 'Occupancy' ? convertAllDates(deposit[38].dueDate, mergeValue.format) : '';
    case 'Deposit39%':
      return deposit.length >= 39 && deposit[38].name !== 'Occupancy' ? `${((deposit[38].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit40':
      return deposit.length >= 40 && deposit[39].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[39].amount).toString() : '';
    case 'Deposit40W':
      return deposit.length >= 40 && deposit[39].name !== 'Occupancy' ? numberToEnglish(deposit[39].amount) : '';
    case 'Deposit40Date':
      return deposit.length >= 40 && deposit[39].name !== 'Occupancy' ? convertAllDates(deposit[39].dueDate, mergeValue.format) : '';
    case 'Deposit40%':
      return deposit.length >= 40 && deposit[39].name !== 'Occupancy' ? `${((deposit[39].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit41':
      return deposit.length >= 41 && deposit[40].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[40].amount).toString() : '';
    case 'Deposit41W':
      return deposit.length >= 41 && deposit[40].name !== 'Occupancy' ? numberToEnglish(deposit[40].amount) : '';
    case 'Deposit41Date':
      return deposit.length >= 41 && deposit[40].name !== 'Occupancy' ? convertAllDates(deposit[40].dueDate, mergeValue.format) : '';
    case 'Deposit41%':
      return deposit.length >= 41 && deposit[40].name !== 'Occupancy' ? `${((deposit[40].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Deposit42':
      return deposit.length >= 42 && deposit[41].name !== 'Occupancy' ? currencyFor(mergeValue, numToCurrency).format(deposit[41].amount).toString() : '';
    case 'Deposit42W':
      return deposit.length >= 42 && deposit[41].name !== 'Occupancy' ? numberToEnglish(deposit[41].amount) : '';
    case 'Deposit42Date':
      return deposit.length >= 42 && deposit[41].name !== 'Occupancy' ? convertAllDates(deposit[41].dueDate, mergeValue.format) : '';
    case 'Deposit42%':
      return deposit.length >= 42 && deposit[41].name !== 'Occupancy' ? `${((deposit[41].amount / totalOption) * 100).toFixed(1)}%` : '';
    case '4thLastDeposit':
      return deposit.length >= 4 ? currencyFor(mergeValue, numToCurrency).format(deposit[deposit.length - 4].amount).toString() : '';
    case '4thLastDepositW':
      return deposit.length >= 4 ? numberToEnglish(deposit[deposit.length - 4].amount) : '';
    case '4thLastDepositDate':
      return deposit.length >= 4 ? convertAllDates(deposit[deposit.length - 4].dueDate, mergeValue.format) : '';
    case '4thLastDeposit%':
      return deposit.length >= 4 ? `${((deposit[deposit.length - 4].amount / totalOption) * 100).toFixed(1)}%` : '';
    case '3rdLastDeposit':
      return deposit.length >= 3 ? currencyFor(mergeValue, numToCurrency).format(deposit[deposit.length - 3].amount).toString() : '';
    case '3rdLastDepositW':
      return deposit.length >= 3 ? numberToEnglish(deposit[deposit.length - 3].amount) : '';
    case '3rdLastDepositDate':
      return deposit.length >= 3 ? convertAllDates(deposit[deposit.length - 3].dueDate, mergeValue.format) : '';
    case '3rdLastDeposit%':
      return deposit.length >= 3 ? `${((deposit[deposit.length - 3].amount / totalOption) * 100).toFixed(1)}%` : '';
    case '2ndLastDeposit':
      return deposit.length >= 2 ? currencyFor(mergeValue, numToCurrency).format(deposit[deposit.length - 2].amount).toString() : '';
    case '2ndLastDepositW':
      return deposit.length >= 2 ? numberToEnglish(deposit[deposit.length - 2].amount) : '';
    case '2ndLastDepositDate':
      return deposit.length >= 2 ? convertAllDates(deposit[deposit.length - 2].dueDate, mergeValue.format) : '';
    case '2ndLastDeposit%':
      return deposit.length >= 2 ? `${((deposit[deposit.length - 2].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'LastDeposit':
      return deposit.length >= 1 ? currencyFor(mergeValue, numToCurrency).format(deposit[deposit.length - 1].amount).toString() : '';
    case 'LastDepositW':
      return deposit.length >= 1 ? numberToEnglish(deposit[deposit.length - 1].amount) : '';
    case 'LastDepositDate':
      return deposit.length >= 1 ? convertAllDates(deposit[deposit.length - 1].dueDate, mergeValue.format) : '';
    case 'LastDeposit%':
      return deposit.length >= 1 ? `${((deposit[deposit.length - 1].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'NewDepositBasePrice':
      return deal && deal.pendingBasePrice ? currencyFor(mergeValue, numToCurrency).format(deal.pendingBasePrice) : '';
    case 'NewDepositTotalPrice':
      return deal && deal.pendingBasePrice
        ? currencyFor(mergeValue, numToCurrency).format(
            options.reduce(
              (a: any, b: any) => {
                return a + b.amount;
              },
              deal.pendingBasePrice ? parseInt(deal.pendingBasePrice, 10) : unit.basePrice
            )
          )
        : '';
    case 'UPrice':
      return currencyFor(mergeValue, numToCurrency).format(unit.basePrice).toString();
    case 'UPriceW':
      return numberToEnglish(unit.basePrice);
    case 'NetUPrice':
      return unit.type === 'commercial'
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(parseFloat(unit.basePrice.toFixed(2)))
        : currencyFor(mergeValue, numToCurrency).format(netHST(unit.basePrice, project));
    case 'TPrice':
      return currencyFor(mergeValue, numToCurrencyDecimal).format(totalOption).toString();
    case 'TPriceW':
      return numberToEnglish(totalOption);
    case 'NetTPriceW':
      return numberToEnglish(totalOption);
    case 'EffectiveTPrice':
      return currencyFor(mergeValue, numToCurrencyDecimal).format(effectivePriceAfterRebate(totalOption, project)).toString();
    case 'EffectiveTPriceW':
      return numberToEnglish(Math.round(effectivePriceAfterRebate(totalOption, project) * 100) / 100);
    case "HSTRebateAmount":
      return currencyFor(mergeValue, numToCurrency).format(hstRebate(totalOption, project)).toString();
    case "NetHstNoRebateTPrice":
      return currencyFor(mergeValue, numToCurrency).format(netHSTnoRebate(totalOption)).toString();
    case "NetHstNoRebateUPrice":
      return currencyFor(mergeValue, numToCurrency).format(netHSTnoRebate(unit.basePrice)).toString();
    case 'GrossNetTPriceDifference': {
      // The HST on the net total price: netTotalPrice * 1.13 - netTotalPrice, with the net total built the same
      // way NetTPrice prints it
      const netTotalOption = totalOption - unit.basePrice + netHST(unit.basePrice, project);
      const netTPrice =
        unit.type === 'commercial'
          ? netTotalOption
          : project.netHst || project._id.toString() === '61f9934d3ace1c59fcb6c5de'
          ? Math.round(netTotalOption)
          : netHST(netTotalOption, project);
      return currencyFor(mergeValue, numToCurrencyDecimal).format(parseFloat((netTPrice * 1.13 - netTPrice).toFixed(2))).toString();
    }
    case 'NetTPrice':
      return unit.type === 'commercial'
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(parseFloat(totalOption.toFixed(2)))
        : project.netHst || project._id.toString() === '61f9934d3ace1c59fcb6c5de' ? currencyFor(mergeValue, numToCurrencyDecimal).format(parseFloat(Math.round(totalOption).toFixed(2))) : currencyFor(mergeValue, numToCurrencyDecimal).format(parseFloat(netHST(totalOption, project).toFixed(2)));
    case 'toPrice':
      return !toUnit && unit
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(unit.basePrice).toString()
        : currencyFor(mergeValue, numToCurrencyDecimal).format(toUnit.basePrice).toString();
    case 'toPriceW':
      return numberToEnglish(toUnit.basePrice);
    case 'toTotalP':
      return currencyFor(mergeValue, numToCurrencyDecimal).format(toTotalOption).toString();
    case 'toTotalPW':
      return numberToEnglish(toTotalOption);
    // Net-of-HST versions of toPrice / toTotalP, following NetUPrice (commercial units have no HST to remove)
    case 'toNetUPrice': {
      const priceUnit = !toUnit && unit ? unit : toUnit;
      return priceUnit.type === 'commercial'
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(priceUnit.basePrice).toString()
        : currencyFor(mergeValue, numToCurrencyDecimal).format(netHST(priceUnit.basePrice, project)).toString();
    }
    case 'toNetTotalP': {
      // toTotalOption is the transfer unit's (or current unit's) gross base price plus options and upgrades;
      // only the base price is netted, the same way GrossNetTPriceDifference builds the net total
      const toBaseUnit = toUnit && toUnit._id !== unit._id ? toUnit : unit;
      if (toBaseUnit.type === 'commercial') return currencyFor(mergeValue, numToCurrencyDecimal).format(toTotalOption).toString();
      const toNetTotal = toTotalOption - toBaseUnit.basePrice + netHST(toBaseUnit.basePrice, project);
      return currencyFor(mergeValue, numToCurrencyDecimal).format(toNetTotal).toString();
    }
    case 'toSuite':
      return toUnit.suite;
    case 'toUnit':
      return toUnit.unit;
    case 'toLevel':
      return toUnit.level;
    case 'toTier':
      return toUnit.tier;
    case 'toRental':
      return toUnit?.rental ? currencyFor(mergeValue, numToCurrency).format(toUnit.rental) : '';
    case 'RealFN':
      return realtor ? realtor.firstName : '';
    case 'RealLN':
      return realtor ? realtor.lastName : '';
    case 'RealFullName':
      return realtor ? `${realtor.firstName} ${realtor.lastName}` : '';
    case 'RealEmail':
      return realtor ? realtor.email : '';
    case 'RealFullAddress':
      return realtor ? `${realtor.streetAddress}, ${realtor.city}, ${realtor.province}, ${realtor.postalCode}` : '';
    case 'RealAddress':
      return realtor ? realtor.streetAddress : '';
    case 'RealCity':
      return realtor ? realtor.city : '';
    case 'RealProvince':
      return realtor ? realtor.province : '';
    case 'RealPhone':
      return realtor ? realtor.directPhone : '';
    case 'RealPostCode':
      return realtor ? realtor.postalCode : '';
    case 'BrokeragePhone':
      return realtor ? realtor.brokeragePhone : '';
    case 'Brokerage':
      return realtor ? realtor.brokerage : '';
    case 'BrokerageFax':
      return realtor && realtor.brokerageFax ? realtor.brokerageFax : '';
    case 'CommissionPercent1':
      return realtor && realtor.coopRates.length
        ? `${numberToEnglish(realtor.coopRates[0].percentage)} (${realtor.coopRates[0].percentage}%)`
        : '';
    case 'CommissionPercent1W':
      return realtor && realtor.coopRates.length ? `${numberToEnglish(realtor.coopRates[0].percentage)}` : '';
    case 'Commission1':
      return realtor && realtor.coopRates.length ? realtor.coopRates[0].percentage.toString() : '';
    case 'CommissionPercent2':
      return realtor && realtor.coopRates.length > 1
        ? `${numberToEnglish(realtor.coopRates[1].percentage)} (${realtor.coopRates[1].percentage}%)`
        : '';
    case 'CommissionPercent2W':
      return realtor && realtor.coopRates.length ? `${numberToEnglish(realtor.coopRates[1].percentage)}` : '';
    case 'Commission2':
      return realtor && realtor.coopRates.length ? realtor.coopRates[1].percentage.toString() : '';
    case 'CommissionPercent3':
      return realtor && realtor.coopRates.length > 2
        ? `${numberToEnglish(realtor.coopRates[2].percentage)} (${realtor.coopRates[2].percentage}%)`
        : '';
    case 'CommissionPercent3W':
      return realtor && realtor.coopRates.length ? `${numberToEnglish(realtor.coopRates[2].percentage)}` : '';
    case 'Commission3':
      return realtor && realtor.coopRates.length ? realtor.coopRates[2].percentage.toString() : '';
    case 'CommissionPercent4':
      return realtor && realtor.coopRates.length > 3
        ? `${numberToEnglish(realtor.coopRates[3].percentage)} (${realtor.coopRates[3].percentage}%)`
        : '';
    case 'CommissionPercent4W':
      return realtor && realtor.coopRates.length ? `${numberToEnglish(realtor.coopRates[3].percentage)}` : '';
    case 'Commission4':
      return realtor && realtor.coopRates.length ? realtor.coopRates[3].percentage.toString() : '';
    case 'CommissionPercent5':
      return realtor && realtor.coopRates.length > 4
        ? `${numberToEnglish(realtor.coopRates[4].percentage)} (${realtor.coopRates[4].percentage}%)`
        : '';
    case 'CommissionPercent5W':
      return realtor && realtor.coopRates.length ? `${numberToEnglish(realtor.coopRates[4].percentage)}` : '';
    case 'Commission5':
      return realtor && realtor.coopRates.length ? realtor.coopRates[4].percentage.toString() : '';
    case 'CommissionPercent6':
      return realtor && realtor.coopRates.length > 5
        ? `${numberToEnglish(realtor.coopRates[5].percentage)} (${realtor.coopRates[5].percentage}%)`
        : '';
    case 'CommissionPercent6W':
      return realtor && realtor.coopRates.length ? `${numberToEnglish(realtor.coopRates[5].percentage)}` : '';
    case 'Commission6':
      return realtor && realtor.coopRates.length ? realtor.coopRates[5].percentage.toString() : '';
    case 'CommissionPercentAmount1':
      return deal ? commission : '';
    case 'CommissionPercentAmount1NetHstNoRebate':
      return deal ? commission : '';
    case 'CommissionPercentAmount2':
      return deal ? commission : '';
    case 'CommissionPercentAmount3':
      return deal ? commission : '';
    case 'CommissionPercentAmount4':
      return deal ? commission : '';
    case 'CommissionPercentAmount5':
      return deal ? commission : '';
    case 'CommissionPercentAmount6':
      return deal ? commission : '';
    case 'CommissionFixed1':
      return deal ? commission : '';
    case 'CommissionFixed2':
      return deal ? commission : '';
    case 'CommissionFixed3':
      return deal ? commission : '';
    case 'CommissionFixed4':
      return deal ? commission : '';
    case 'CommissionFixed5':
      return deal ? commission : '';
    case 'CommissionFixed6':
      return deal ? commission : '';
    case 'OP1':
      return newOptions?.purchaseAmount;
    case 'OP2':
      return newOptions?.purchaseAmount;
    case 'OP3':
      return newOptions?.purchaseAmount;
    case 'OP4':
      return newOptions?.purchaseAmount;
    case 'OP5':
      return newOptions?.purchaseAmount;
    case 'OP6':
      return newOptions?.purchaseAmount;
    case 'OP7':
      return newOptions?.purchaseAmount;
    case 'OP8':
      return newOptions?.purchaseAmount;
    case 'OP9':
      return newOptions?.purchaseAmount;
    case 'OP10':
      return newOptions?.purchaseAmount;
    case 'toOP1':
      return newOptions?.purchaseAmount;
    case 'toOP2':
      return newOptions?.purchaseAmount;
    case 'toOP3':
      return newOptions?.purchaseAmount;
    case 'toOP4':
      return newOptions?.purchaseAmount;
    case 'toOP5':
      return newOptions?.purchaseAmount;
    case 'toOP6':
      return newOptions?.purchaseAmount;
    case 'toOP7':
      return newOptions?.purchaseAmount;
    case 'toOP8':
      return newOptions?.purchaseAmount;
    case 'toOP9':
      return newOptions?.purchaseAmount;
    case 'toOP10':
      return newOptions?.purchaseAmount;
    // Sum of the deal's option amounts on their own (TPrice etc. include the base price). A zero total prints the
    // same way the OP1Price-OP10Price fields print an option that wasn't purchased.
    case 'TotalOptionsAmount': {
      const totalOptionsAmount = (options || []).reduce((total: number, option: any) => total + (Number(option.amount) || 0), 0);
      return totalOptionsAmount ? currencyFor(mergeValue, numToCurrency).format(totalOptionsAmount) : project.defaultZeroValue ? project.defaultZeroValue : '$0';
    }
    case 'OP1Price':
      return newOptions?.amount;
    case 'OP2Price':
      return newOptions?.amount;
    case 'OP3Price':
      return newOptions?.amount;
    case 'OP4Price':
      return newOptions?.amount;
    case 'OP5Price':
      return newOptions?.amount;
    case 'OP6Price':
      return newOptions?.amount;
    case 'OP7Price':
      return newOptions?.amount;
    case 'OP8Price':
      return newOptions?.amount;
    case 'OP9Price':
      return newOptions?.amount;
    case 'OP10Price':
      return newOptions?.amount;
    case 'OP1Num':
      return newOptions?.purchaseNumber;
    case 'OP2Num':
      return newOptions?.purchaseNumber;
    case 'OP3Num':
      return newOptions?.purchaseNumber;
    case 'OP4Num':
      return newOptions?.purchaseNumber;
    case 'OP5Num':
      return newOptions?.purchaseNumber;
    case 'OP6Num':
      return newOptions?.purchaseNumber;
    case 'OP7Num':
      return newOptions?.purchaseNumber;
    case 'OP8Num':
      return newOptions?.purchaseNumber;
    case 'OP9Num':
      return newOptions?.purchaseNumber;
    case 'OP10Num':
      return newOptions?.purchaseNumber;
    case 'toOP1Price':
      return newOptions?.amount;
    case 'toOP2Price':
      return newOptions?.amount;
    case 'toOP3Price':
      return newOptions?.amount;
    case 'toOP4Price':
      return newOptions?.amount;
    case 'toOP5Price':
      return newOptions?.amount;
    case 'toOP6Price':
      return newOptions?.amount;
    case 'toOP7Price':
      return newOptions?.amount;
    case 'toOP8Price':
      return newOptions?.amount;
    case 'toOP9Price':
      return newOptions?.amount;
    case 'toOP10Price':
      return newOptions?.amount;
    case 'toOP1Num':
      return newOptions?.purchaseNumber;
    case 'toOP2Num':
      return newOptions?.purchaseNumber;
    case 'toOP3Num':
      return newOptions?.purchaseNumber;
    case 'toOP4Num':
      return newOptions?.purchaseNumber;
    case 'toOP5Num':
      return newOptions?.purchaseNumber;
    case 'toOP6Num':
      return newOptions?.purchaseNumber;
    case 'toOP7Num':
      return newOptions?.purchaseNumber;
    case 'toOP8Num':
      return newOptions?.purchaseNumber;
    case 'toOP9Num':
      return newOptions?.purchaseNumber;
    case 'toOP10Num':
      return newOptions?.purchaseNumber;
    case 'totalParkingPrice':
      return options.length && options.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking')).length
        ? currencyFor(mergeValue, numToCurrency).format(
            options
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
              .reduce((a: any, b: any) => a + b.amount, 0)
          )
        : '';
    case 'totalParkingNum':
      return options.length && options.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking')).length
        ? `${options
            .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
            .reduce((a: any, b: any) => a + b.purchaseAmount, 0)}`
        : '0';
    case 'totalParkingAmount':
      return options.length && options.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking')).length
        ? `${currencyFor(mergeValue, numToCurrency).format(
            options
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )} (${numberToEnglish(
            options
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )})`
        : '';
    case 'totalLockerPrice':
      return options.length && options.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker')).length
        ? currencyFor(mergeValue, numToCurrency).format(
            options
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
              .reduce((a: any, b: any) => a + b.amount, 0)
          )
        : '';
    case 'totalLockerNum':
      return options.length && options.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker')).length
        ? `${options
            .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
            .reduce((a: any, b: any) => a + b.purchaseAmount, 0)}`
        : '0';
    case 'totalLockerAmount':
      return options.length && options.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker')).length
        ? `${currencyFor(mergeValue, numToCurrency).format(
            options
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )} (${numberToEnglish(
            options
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )})`
        : '';

    case 'toTotalParkingPrice':
      return toOptions.length && toOptions.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking')).length
        ? currencyFor(mergeValue, numToCurrency).format(
            toOptions
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
              .reduce((a: any, b: any) => a + b.amount, 0)
          )
        : '';
    case 'toTotalParkingNum':
      return toOptions.length && toOptions.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking')).length
        ? `${toOptions
            .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
            .reduce((a: any, b: any) => a + b.purchaseAmount, 0)}`
        : '0';
    case 'toTotalParkingAmount':
      return toOptions.length && toOptions.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking')).length
        ? `${currencyFor(mergeValue, numToCurrency).format(
            toOptions
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )} (${numberToEnglish(
            toOptions
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('parking'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )})`
        : '';
    case 'toTotalLockerPrice':
      return toOptions.length && toOptions.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker')).length
        ? currencyFor(mergeValue, numToCurrency).format(
            toOptions
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
              .reduce((a: any, b: any) => a + b.amount, 0)
          )
        : '';
    case 'toTotalLockerNum':
      return toOptions.length && toOptions.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker')).length
        ? `${toOptions
            .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
            .reduce((a: any, b: any) => a + b.purchaseAmount, 0)}`
        : '0';
    case 'toTotalLockerAmount':
      return toOptions.length && toOptions.filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker')).length
        ? `${currencyFor(mergeValue, numToCurrency).format(
            toOptions
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )} (${numberToEnglish(
            toOptions
              .filter((option: IDealOption) => option.name?.toLocaleLowerCase().includes('locker'))
              .reduce((a: any, b: any) => a + b.purchaseAmount, 0)
          )})`
        : '';
    case 'Rental':
      return deal && deal.pendingRental ? currencyFor(mergeValue, numToCurrency).format(deal.pendingRental) : unit?.rental ? currencyFor(mergeValue, numToCurrency).format(unit?.rental) : '';
    case 'toDeposit1':
      return toDeposits.length >= 1 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[0].amount).toString() : '';
    case 'toDeposit1W':
      return toDeposits.length >= 1 ? numberToEnglish(toDeposits[0].amount) : '';
    case 'toDeposit1Date':
      return toDeposits.length >= 1
        ? convertAllDates(toDeposits[0].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit1DateWords':
      return toDeposits.length >= 1
        ? convertAllDates(toDeposits[0].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit1%':
      return toDeposits.length >= 1 ? `${((toDeposits[0].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit2':
      return toDeposits.length >= 2 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[1].amount).toString() : '';
    case 'toDeposit2W':
      return toDeposits.length >= 2 ? numberToEnglish(toDeposits[1].amount) : '';
    case 'toDeposit2Date':
      return toDeposits.length >= 2 ? convertAllDates(toDeposits[1].dueDate, mergeValue.format) : '';
    case 'toDeposit2DateWords':
      return toDeposits.length >= 2
        ? convertAllDates(toDeposits[1].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit2%':
      return toDeposits.length >= 2 ? `${(((toDeposits[1].amount + toDeposits[0].amount) / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit3':
      return toDeposits.length >= 3 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[2].amount).toString() : '';
    case 'toDeposit3W':
      return toDeposits.length >= 3 ? numberToEnglish(toDeposits[2].amount) : '';
    case 'toDeposit3Date':
      return toDeposits.length >= 3 ? convertAllDates(toDeposits[2].dueDate, mergeValue.format) : '';
    case 'toDeposit3DateWords':
      return toDeposits.length >= 3
        ? convertAllDates(toDeposits[2].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit3%':
      return toDeposits.length >= 3 ? `${((toDeposits[2].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit4':
      return toDeposits.length >= 4 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[3].amount).toString() : '';
    case 'toDeposit4W':
      return toDeposits.length >= 4 ? numberToEnglish(toDeposits[3].amount) : '';
    case 'toDeposit4Date':
      return toDeposits.length >= 4 ? convertAllDates(toDeposits[3].dueDate, mergeValue.format) : '';
    case 'toDeposit4DateWords':
      return toDeposits.length >= 4
        ? convertAllDates(toDeposits[3].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit4%':
      return toDeposits.length >= 4 ? `${((toDeposits[3].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit5':
      return toDeposits.length >= 5 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[4].amount).toString() : '';
    case 'toDeposit5W':
      return toDeposits.length >= 5 ? numberToEnglish(toDeposits[4].amount) : '';
    case 'toDeposit5Date':
      return toDeposits.length >= 5 ? convertAllDates(toDeposits[4].dueDate, mergeValue.format) : '';
    case 'toDeposit5DateWords':
      return toDeposits.length >= 5
        ? convertAllDates(toDeposits[4].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit5%':
      return toDeposits.length >= 5 ? `${((toDeposits[4].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit6':
      return toDeposits.length >= 6 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[5].amount).toString() : '';
    case 'toDeposit6W':
      return toDeposits.length >= 6 ? numberToEnglish(toDeposits[5].amount) : '';
    case 'toDeposit6Date':
      return toDeposits.length >= 6 ? convertAllDates(toDeposits[5].dueDate, mergeValue.format) : '';
    case 'toDeposit6DateWords':
      return toDeposits.length >= 6
        ? convertAllDates(toDeposits[5].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit6%':
      return toDeposits.length >= 6 ? `${((toDeposits[5].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit7':
      return toDeposits.length >= 7 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[6].amount).toString() : '';
    case 'toDeposit7W':
      return toDeposits.length >= 7 ? numberToEnglish(toDeposits[6].amount) : '';
    case 'toDeposit7Date':
      return toDeposits.length >= 7 ? convertAllDates(toDeposits[6].dueDate, mergeValue.format) : '';
    case 'toDeposit7DateWords':
      return toDeposits.length >= 7
        ? convertAllDates(toDeposits[6].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit7%':
      return toDeposits.length >= 7 ? `${((toDeposits[6].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit8':
      return toDeposits.length >= 8 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[7].amount).toString() : '';
    case 'toDeposit8W':
      return toDeposits.length >= 8 ? numberToEnglish(toDeposits[7].amount) : '';
    case 'toDeposit8Date':
      return toDeposits.length >= 8 ? convertAllDates(toDeposits[7].dueDate, mergeValue.format) : '';
    case 'toDeposit8DateWords':
      return toDeposits.length >= 8
        ? convertAllDates(toDeposits[7].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit8%':
      return toDeposits.length >= 8 ? `${((toDeposits[7].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit9':
      return toDeposits.length >= 9 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[8].amount).toString() : '';
    case 'toDeposit9W':
      return toDeposits.length >= 9 ? numberToEnglish(toDeposits[8].amount) : '';
    case 'toDeposit9Date':
      return toDeposits.length >= 9 ? convertAllDates(toDeposits[8].dueDate, mergeValue.format) : '';
    case 'toDeposit9DateWords':
      return toDeposits.length >= 9
        ? convertAllDates(toDeposits[8].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit9%':
      return toDeposits.length >= 9 ? `${((toDeposits[8].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit10':
      return toDeposits.length >= 10 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[9].amount).toString() : '';
    case 'toDeposit10W':
      return toDeposits.length >= 10 ? numberToEnglish(toDeposits[9].amount) : '';
    case 'toDeposit10Date':
      return toDeposits.length >= 10 ? convertAllDates(toDeposits[9].dueDate, mergeValue.format) : '';
    case 'toDeposit10DateWords':
      return toDeposits.length >= 10
        ? convertAllDates(toDeposits[9].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit10%':
      return toDeposits.length >= 10 ? `${((toDeposits[9].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit11':
      return toDeposits.length >= 11 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[10].amount).toString() : '';
    case 'toDeposit11W':
      return toDeposits.length >= 11 ? numberToEnglish(toDeposits[10].amount) : '';
    case 'toDeposit11Date':
      return toDeposits.length >= 11 ? convertAllDates(toDeposits[10].dueDate, mergeValue.format) : '';
    case 'toDeposit11DateWords':
      return toDeposits.length >= 11
        ? convertAllDates(toDeposits[10].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit11%':
      return toDeposits.length >= 11 ? `${((toDeposits[10].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit12':
      return toDeposits.length >= 12 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[11].amount).toString() : '';
    case 'toDeposit12W':
      return toDeposits.length >= 12 ? numberToEnglish(toDeposits[11].amount) : '';
    case 'toDeposit12Date':
      return toDeposits.length >= 12 ? convertAllDates(toDeposits[11].dueDate, mergeValue.format) : '';
    case 'toDeposit12DateWords':
      return toDeposits.length >= 12
        ? convertAllDates(toDeposits[11].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit12%':
      return toDeposits.length >= 12 ? `${((toDeposits[11].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit13':
      return toDeposits.length >= 13 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[12].amount).toString() : '';
    case 'toDeposit13W':
      return toDeposits.length >= 13 ? numberToEnglish(toDeposits[12].amount) : '';
    case 'toDeposit13Date':
      return toDeposits.length >= 13 ? convertAllDates(toDeposits[12].dueDate, mergeValue.format) : '';
    case 'toDeposit13DateWords':
      return toDeposits.length >= 13
        ? convertAllDates(toDeposits[12].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit13%':
      return toDeposits.length >= 13 ? `${((toDeposits[12].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit14':
      return toDeposits.length >= 14 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[13].amount).toString() : '';
    case 'toDeposit14W':
      return toDeposits.length >= 14 ? numberToEnglish(toDeposits[13].amount) : '';
    case 'toDeposit14Date':
      return toDeposits.length >= 14 ? convertAllDates(toDeposits[13].dueDate, mergeValue.format) : '';
    case 'toDeposit14DateWords':
      return toDeposits.length >= 14
        ? convertAllDates(toDeposits[13].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit14%':
      return toDeposits.length >= 14 ? `${((toDeposits[13].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit15':
      return toDeposits.length >= 15 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[14].amount).toString() : '';
    case 'toDeposit15W':
      return toDeposits.length >= 15 ? numberToEnglish(toDeposits[14].amount) : '';
    case 'toDeposit15Date':
      return toDeposits.length >= 15 ? convertAllDates(toDeposits[14].dueDate, mergeValue.format) : '';
    case 'toDeposit15DateWords':
      return toDeposits.length >= 15
        ? convertAllDates(toDeposits[14].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit15%':
      return toDeposits.length >= 15 ? `${((toDeposits[14].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit16':
      return toDeposits.length >= 16 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[15].amount).toString() : '';
    case 'toDeposit16W':
      return toDeposits.length >= 16 ? numberToEnglish(toDeposits[15].amount) : '';
    case 'toDeposit16Date':
      return toDeposits.length >= 16 ? convertAllDates(toDeposits[15].dueDate, mergeValue.format) : '';
    case 'toDeposit16DateWords':
      return toDeposits.length >= 16
        ? convertAllDates(toDeposits[15].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit16%':
      return toDeposits.length >= 16 ? `${((toDeposits[15].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit17':
      return toDeposits.length >= 17 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[16].amount).toString() : '';
    case 'toDeposit17W':
      return toDeposits.length >= 17 ? numberToEnglish(toDeposits[16].amount) : '';
    case 'toDeposit17Date':
      return toDeposits.length >= 17 ? convertAllDates(toDeposits[16].dueDate, mergeValue.format) : '';
    case 'toDeposit17DateWords':
      return toDeposits.length >= 17
        ? convertAllDates(toDeposits[16].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit17%':
      return toDeposits.length >= 17 ? `${((toDeposits[16].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit18':
      return toDeposits.length >= 18 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[17].amount).toString() : '';
    case 'toDeposit18W':
      return toDeposits.length >= 18 ? numberToEnglish(toDeposits[17].amount) : '';
    case 'toDeposit18Date':
      return toDeposits.length >= 18 ? convertAllDates(toDeposits[17].dueDate, mergeValue.format) : '';
    case 'toDeposit18DateWords':
      return toDeposits.length >= 18
        ? convertAllDates(toDeposits[17].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit18%':
      return toDeposits.length >= 18 ? `${((toDeposits[17].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit19':
      return toDeposits.length >= 19 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[18].amount).toString() : '';
    case 'toDeposit19W':
      return toDeposits.length >= 19 ? numberToEnglish(toDeposits[18].amount) : '';
    case 'toDeposit19Date':
      return toDeposits.length >= 19 ? convertAllDates(toDeposits[18].dueDate, mergeValue.format) : '';
    case 'toDeposit19DateWords':
      return toDeposits.length >= 19
        ? convertAllDates(toDeposits[18].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit19%':
      return toDeposits.length >= 19 ? `${((toDeposits[18].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit20':
      return toDeposits.length >= 20 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[19].amount).toString() : '';
    case 'toDeposit20W':
      return toDeposits.length >= 20 ? numberToEnglish(toDeposits[19].amount) : '';
    case 'toDeposit20Date':
      return toDeposits.length >= 20 ? convertAllDates(toDeposits[19].dueDate, mergeValue.format) : '';
    case 'toDeposit20DateWords':
      return toDeposits.length >= 20
        ? convertAllDates(toDeposits[19].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit20%':
      return toDeposits.length >= 20 ? `${((toDeposits[19].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'toDeposit21':
      return toDeposits.length >= 21 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[20].amount).toString() : '';
    case 'toDeposit21W':
      return toDeposits.length >= 21 ? numberToEnglish(toDeposits[20].amount) : '';
    case 'toDeposit21Date':
      return toDeposits.length >= 21 ? convertAllDates(toDeposits[20].dueDate, mergeValue.format) : '';
    case 'toDeposit21DateWords':
      return toDeposits.length >= 21
        ? convertAllDates(toDeposits[20].dueDate, mergeValue.format)
        : convertAllDates(new Date(), mergeValue.format);
    case 'toDeposit21%':
      return toDeposits.length >= 21 ? `${((toDeposits[20].amount / toTotalOption) * 100).toFixed(1)}%` : '';
    case 'to4thLastDeposit':
      return toDeposits.length >= 4 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[toDeposits.length - 4].amount).toString() : '';
    case 'to4thLastDepositW':
      return toDeposits.length >= 4 ? numberToEnglish(toDeposits[toDeposits.length - 4].amount) : '';
    case 'to4thLastDepositDate':
      return toDeposits.length >= 4 ? convertAllDates(toDeposits[toDeposits.length - 4].dueDate, mergeValue.format) : '';
    case 'to4thLastDeposit%':
      return toDeposits.length >= 4 ? `${((toDeposits[toDeposits.length - 4].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'to3rdLastDeposit':
      return toDeposits.length >= 3 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[toDeposits.length - 3].amount).toString() : '';
    case 'to3rdLastDepositW':
      return toDeposits.length >= 3 ? numberToEnglish(toDeposits[toDeposits.length - 3].amount) : '';
    case 'to3rdLastDepositDate':
      return toDeposits.length >= 3 ? convertAllDates(toDeposits[toDeposits.length - 3].dueDate, mergeValue.format) : '';
    case 'to3rdLastDeposit%':
      return toDeposits.length >= 3 ? `${((toDeposits[toDeposits.length - 3].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'to2ndLastDeposit':
      return toDeposits.length >= 2 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[toDeposits.length - 2].amount).toString() : '';
    case 'to2ndLastDepositW':
      return toDeposits.length >= 2 ? numberToEnglish(toDeposits[toDeposits.length - 2].amount) : '';
    case 'to2ndLastDepositDate':
      return toDeposits.length >= 2 ? convertAllDates(toDeposits[toDeposits.length - 2].dueDate, mergeValue.format) : '';
    case 'to2ndLastDeposit%':
      return toDeposits.length >= 2 ? `${((toDeposits[toDeposits.length - 2].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'toLastDeposit':
      return toDeposits.length >= 1 ? currencyFor(mergeValue, numToCurrency).format(toDeposits[toDeposits.length - 1].amount).toString() : '';
    case 'toLastDepositW':
      return toDeposits.length >= 1 ? numberToEnglish(toDeposits[toDeposits.length - 1].amount) : '';
    case 'toLastDepositDate':
      return toDeposits.length >= 1 ? convertAllDates(toDeposits[toDeposits.length - 1].dueDate, mergeValue.format) : '';
    case 'toLastDeposit%':
      return toDeposits.length >= 1 ? `${((toDeposits[toDeposits.length - 1].amount / totalOption) * 100).toFixed(1)}%` : '';
    case 'Schedule A':
      return mergeValue.key;
    case 'H':
      return mergeValue.key;
    case 'J':
      return mergeValue.key;
    case 'K':
      return mergeValue.key;
    case 'firstOccupancy':
      return unit?.firstTentativeOccupancy
        ? convertAllDates(unit?.firstTentativeOccupancy, mergeValue.format)
        : project && project.firstTentativeOccupancy
          ? convertAllDates(project.firstTentativeOccupancy, mergeValue.format)
          : '';
    case 'firstOccupancyDay':
    case 'firstOccupancyMonth':
    case 'firstOccupancyYear':
      return unit?.firstTentativeOccupancy
        ? convertAllDates(unit?.firstTentativeOccupancy, mergeValue.format)
        : project && project.firstTentativeOccupancy
          ? convertAllDates(project.firstTentativeOccupancy, mergeValue.format)
          : '';
    case 'finalOccupancy':
      return unit?.finalTentativeOccupancy
        ? convertAllDates(unit?.finalTentativeOccupancy, mergeValue.format)
        : project && project.finalTentativeOccupancy
          ? convertAllDates(project.finalTentativeOccupancy, mergeValue.format)
          : '';
    case 'finalOccupancyDay':
    case 'finalOccupancyMonth':
    case 'finalOccupancyYear':
      return unit?.finalTentativeOccupancy
        ? convertAllDates(unit?.finalTentativeOccupancy, mergeValue.format)
        : project && project.finalTentativeOccupancy
          ? convertAllDates(project.finalTentativeOccupancy, mergeValue.format)
          : '';
    case 'firmOccupancy':
      return unit?.firmOccupancy
        ? convertAllDates(unit?.firmOccupancy, mergeValue.format)
        : project && project.firmOccupancy
          ? convertAllDates(project.firmOccupancy, mergeValue.format)
          : '';
    case 'firmOccupancyDay':
    case 'firmOccupancyMonth':
    case 'firmOccupancyYear':
      return unit?.firmOccupancy
        ? convertAllDates(unit?.firmOccupancy, mergeValue.format)
        : project && project.firmOccupancy
          ? convertAllDates(project.firmOccupancy, mergeValue.format)
          : '';
    case 'outsideOccupancy':
      return unit?.outsideOccupancy
        ? convertAllDates(unit?.outsideOccupancy, mergeValue.format)
        : project && project.outsideOccupancy
          ? convertAllDates(project.outsideOccupancy, mergeValue.format)
          : '';
    case 'outsideOccupancyDay':
    case 'outsideOccupancyMonth':
    case 'outsideOccupancyYear':
      return unit?.outsideOccupancy
        ? convertAllDates(unit?.outsideOccupancy, mergeValue.format)
        : project && project.outsideOccupancy
          ? convertAllDates(project.outsideOccupancy, mergeValue.format)
          : '';
    case 'noticePeriod':
      return unit?.firstTentativeOccupancy
        ? convertAllDates(subDays(new Date(unit?.firstTentativeOccupancy), 90), mergeValue.format)
        : project && project.firstTentativeOccupancy
          ? convertAllDates(subDays(new Date(project.firstTentativeOccupancy), 90), mergeValue.format)
          : '';
    case 'noticePeriodDay':
    case 'noticePeriodMonth':
    case 'noticePeriodYear':
      return unit?.firstTentativeOccupancy
        ? convertAllDates(subDays(new Date(unit?.firstTentativeOccupancy), 90), mergeValue.format)
        : project && project.firstTentativeOccupancy
          ? convertAllDates(subDays(new Date(project.firstTentativeOccupancy), 90), mergeValue.format)
          : '';
    case 'terminationPeriod':
      return unit?.outsideOccupancy
        ? convertAllDates(addDays(new Date(unit?.outsideOccupancy), 30), mergeValue.format)
        : project && project.outsideOccupancy
          ? convertAllDates(addDays(new Date(project.outsideOccupancy), 30), mergeValue.format)
          : '';
    case 'terminationPeriodDay':
    case 'terminationPeriodMonth':
    case 'terminationPeriodYear':
      return unit?.outsideOccupancy
        ? convertAllDates(addDays(new Date(unit?.outsideOccupancy), 30), mergeValue.format)
        : project && project.outsideOccupancy
          ? convertAllDates(addDays(new Date(project.outsideOccupancy), 30), mergeValue.format)
          : '';
    case '3rdPartyName':
      return deal.thirdParty.length > 0 ? deal.thirdParty[0].fullName : '';
    case '3rdPartyAddress':
      return deal.thirdParty.length > 0 ? deal.thirdParty[0].streetAddress : '';
    case '3rdPartyPhone':
      return deal.thirdParty.length > 0 ? deal.thirdParty[0].primaryPhone : '';
    case '3rdPartyDob':
      return deal.thirdParty.length > 0 ? convertAllDates(deal.thirdParty[0].dob, mergeValue.format) : '';
    case '3rdPartyOccupation':
      return deal.thirdParty.length > 0 ? deal.thirdParty[0].occupation : '';
    case '3rdPartyCorp':
      return deal.thirdParty.length > 0 ? deal.thirdParty[0].corpNumber : '';
    case '3rdPartyRelationship':
      return deal.thirdParty.length > 0 ? deal.thirdParty[0].relationship : '';
    case 'Adjustment1%':
      return deal && deal.adjustments.length >= 1 ? `${deal.adjustments[0].percentage}%` : '';
    case 'Adjustment1%Words':
      return deal && deal.adjustments.length >= 1 ? `${numberToEnglish(deal.adjustments[0].percentage)} PERCENT` : '';
    case 'Adjustment1$':
      return deal && deal.adjustments.length >= 1 ? `${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[0].value)}` : '';
    case 'Adjustment1$Words':
      return deal && deal.adjustments.length >= 1 ? `${numberToEnglish(deal.adjustments[0].value)} DOLLARS` : '';
    case 'Adjustment1$WordsAndNumber':
      return deal && deal.adjustments.length >= 1
        ? `${numberToEnglish(deal.adjustments[0].value)} DOLLARS (${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[0].value)})`
        : '';
    case 'Adjustment2%':
      return deal && deal.adjustments.length >= 2 ? `${deal.adjustments[1].percentage}%` : '';
    case 'Adjustment2%Words':
      return deal && deal.adjustments.length >= 2 ? `${numberToEnglish(deal.adjustments[1].percentage)} PERCENT` : '';
    case 'Adjustment2$':
      return deal && deal.adjustments.length >= 2 ? `${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[1].value)}` : '';
    case 'Adjustment2$Words':
      return deal && deal.adjustments.length >= 2 ? `${numberToEnglish(deal.adjustments[1].value)} DOLLARS` : '';
    case 'Adjustment2$WordsAndNumber':
      return deal && deal.adjustments.length >= 2
        ? `${numberToEnglish(deal.adjustments[1].value)} DOLLARS (${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[1].value)})`
        : '';
    case 'Adjustment3%':
      return deal && deal.adjustments.length >= 3 ? `${deal.adjustments[2].percentage}%` : '';
    case 'Adjustment3%Words':
      return deal && deal.adjustments.length >= 3 ? `${numberToEnglish(deal.adjustments[2].percentage)} PERCENT` : '';
    case 'Adjustment3$':
      return deal && deal.adjustments.length >= 3 ? `${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[2].value)}` : '';
    case 'Adjustment3$Words':
      return deal && deal.adjustments.length >= 3 ? `${numberToEnglish(deal.adjustments[2].value)} DOLLARS` : '';
    case 'Adjustment3$WordsAndNumber':
      return deal && deal.adjustments.length >= 3
        ? `${numberToEnglish(deal.adjustments[2].value)} DOLLARS (${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[2].value)})`
        : '';
    case 'Adjustment4%':
      return deal && deal.adjustments.length >= 4 ? `${deal.adjustments[3].percentage}%` : '';
    case 'Adjustment4%Words':
      return deal && deal.adjustments.length >= 4 ? `${numberToEnglish(deal.adjustments[3].percentage)} PERCENT` : '';
    case 'Adjustment4$':
      return deal && deal.adjustments.length >= 4 ? `${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[3].value)}` : '';
    case 'Adjustment4$Words':
      return deal && deal.adjustments.length >= 4 ? `${numberToEnglish(deal.adjustments[3].value)} DOLLARS` : '';
    case 'Adjustment4$WordsAndNumber':
      return deal && deal.adjustments.length >= 4
        ? `${numberToEnglish(deal.adjustments[3].value)} DOLLARS (${currencyFor(mergeValue, numToCurrency).format(deal.adjustments[3].value)})`
        : '';
    case 'P1Passport#':
      return purchaser[0].idType === 'Passport'
        ? purchaser[0].idNumber
        : purchaser[0].proofType === 'Passport'
          ? purchaser[0].proofNumber
          : '';
    case 'P1PermanentResidence#':
      return purchaser[0].proofType === 'Permanent Resident Card' ? purchaser[0].proofNumber : '';
    case 'P1BirthCertificate#':
      return purchaser[0].proofType === 'Birth Certificate' ? purchaser[0].proofNumber : '';
    case 'P1IndianStatus#':
      return purchaser[0].proofType === 'Indian Status' ? purchaser[0].proofNumber : '';
    case 'P1WorkPermit#':
      return purchaser[0].proofType === 'Work Permit' ? purchaser[0].proofNumber : '';
    case 'P1WorkPermitExpiry':
      return purchaser[0].proofType === 'Work Permit' && purchaser[0].proofExpiry
        ? convertAllDates(purchaser[0].proofExpiry, mergeValue.format)
        : '';
    case 'P2Passport#':
      return purchaser.length >= 2 && purchaser[1].idType === 'Passport'
        ? purchaser[1].idNumber
        : purchaser[1].proofType === 'Passport'
          ? purchaser[1].proofNumber
          : '';
    case 'P2PermanentResidence#':
      return purchaser.length >= 2 && purchaser[1].proofType === 'Permanent Resident Card' ? purchaser[1].proofNumber : '';
    case 'P2BirthCertificate#':
      return purchaser.length >= 2 && purchaser[1].proofType === 'Birth Certificate' ? purchaser[1].proofNumber : '';
    case 'P2IndianStatus#':
      return purchaser.length >= 2 && purchaser[1].proofType === 'Indian Status' ? purchaser[1].proofNumber : '';
    case 'P2WorkPermit#':
      return purchaser.length >= 2 && purchaser[1].proofType === 'Work Permit' ? purchaser[1].proofNumber : '';
    case 'P2WorkPermitExpiry':
      return purchaser.length >= 2 && purchaser[1].proofType === 'Work Permit' && purchaser[1].proofExpiry
        ? convertAllDates(purchaser[1].proofExpiry, mergeValue.format)
        : '';
    case 'PadName':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].name}` : '';
    case 'PadInstitution':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].institution}` : '';
    case 'PadBranch':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].branch}` : '';
    case 'PadAccount':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].account}` : '';
    case 'PadBranchAddress':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].branchAddress}` : '';
    case 'PadAccountHolderFullAddress':
      return deal && deal.debit.length >= 1
        ? `${deal.debit[0].accountHolder[0].address}, ${deal.debit[0].accountHolder[0].city}, ${deal.debit[0].accountHolder[0].province}, ${deal.debit[0].accountHolder[0].postalCode}`
        : '';
    case 'PadAccountHolderAddress':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].accountHolder[0].address}` : '';
    case 'PadAccountHolderCity':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].accountHolder[0].city}` : '';
    case 'PadAccountHolderProvince':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].accountHolder[0].province}` : '';
    case 'PadAccountHolderPostalCode':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].accountHolder[0].postalCode}` : '';
    case 'PadAccountHolderPhone':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].accountHolder[0].primaryPhone}` : '';
    case 'PadAccountHolderEmail':
      return deal && deal.debit.length >= 1 ? `${deal.debit[0].accountHolder[0].email}` : '';
    case 'MaintenanceFeeByMonths':
      return unit && unit.maintenanceFee && unit.days ? `${currencyFor(mergeValue, numToCurrencyDecimal).format(unit.maintenanceFee * unit?.days)}` : '';
    case 'MaintenanceFeeByMonthsWords':
      return unit && unit.maintenanceFee && unit.days ? `${numberToCents(unit.maintenanceFee * unit?.days)}` : '';
    case 'MaintenanceTermByMonths':
      return unit && unit.days ? `${unit.days}` : '';
    case 'MaintenanceTermByMonthsWords':
      return unit && unit.days ? `${numberToEnglish(parseInt(unit.days, 10))}` : '';
    case 'MaintenanceFeeByAdjustment':
      return maintenance ? `${currencyFor(mergeValue, numToCurrencyDecimal).format(maintenance.value)}` : '';
    case 'MaintenanceFeeByAdjustmentWords':
      return maintenance ? `${numberToCents(maintenance.value)}` : '';
    case 'MaintenanceFeeTotal':
      return maintenance
        ? `${currencyFor(mergeValue, numToCurrencyDecimal).format(maintenance.value)}`
        : unit && unit.maintenanceFee && projectMaintenance
          ? `${currencyFor(mergeValue, numToCurrencyDecimal).format(unit.maintenanceFee * projectMaintenance.conditions[0].amount)}`
          : '';
    case 'toMaintenanceFeeTotal':
      return maintenance
        ? `${currencyFor(mergeValue, numToCurrencyDecimal).format(maintenance.value)}`
        : toUnit && toUnit.maintenanceFee && projectMaintenance
          ? `${currencyFor(mergeValue, numToCurrencyDecimal).format(toUnit.maintenanceFee * projectMaintenance.conditions[0].amount)}`
          : '';
    case 'toP1FName':
      return toPurchaser.length >= 1 ? toPurchaser[0].firstName : '';
    case 'toP1LName':
      return toPurchaser.length >= 1 ? toPurchaser[0].lastName : '';
    case 'toP1FullName':
      return toPurchaser.length >= 1 ? `${toPurchaser[0].firstName} ${toPurchaser[0].lastName}` : '';
    case 'toP1Sin':
      return toPurchaser.length >= 1 ? toPurchaser[0].sin : '';
    case 'toP1Dob':
      return toPurchaser.length >= 1 ? convertAllDates(toPurchaser[0].dob, mergeValue.format) : '';
    case 'toP1Address':
      return toPurchaser.length >= 1 ? toPurchaser[0].streetAddress : '';
    case 'toP1Phone':
      return toPurchaser.length >= 1 ? toPurchaser[0].primaryPhone : '';
    case 'toP1Email':
      return toPurchaser.length >= 1 ? toPurchaser[0].email : '';
    case 'toP2FName':
      return toPurchaser.length >= 2 ? toPurchaser[1].firstName : '';
    case 'toP2LName':
      return toPurchaser.length >= 2 ? toPurchaser[1].lastName : '';
    case 'toP2FullName':
      return toPurchaser.length >= 2 ? `${toPurchaser[1].firstName} ${toPurchaser[1].lastName}` : '';
    case 'toP2Sin':
      return toPurchaser.length >= 2 ? toPurchaser[1].sin : '';
    case 'toP2Dob':
      return toPurchaser.length >= 2 ? convertAllDates(toPurchaser[1].dob, mergeValue.format) : '';
    case 'toP2Address':
      return toPurchaser.length >= 2 ? toPurchaser[1].streetAddress : '';
    case 'toP2Phone':
      return toPurchaser.length >= 2 ? toPurchaser[1].primaryPhone : '';
    case 'toP2Email':
      return toPurchaser.length >= 2 ? toPurchaser[1].email : '';
    case 'oldPurchaserInformation1':
      return differences.length > 0
        ? `Purchaser ${differences[0].purchaserNumber} ${capitalizeDifference(differences[0].type)}: ${getPurchaserInfo(
            purchaser[differences[0].purchaserNumber - 1],
            differences[0].type
          )}`
        : '';
    case 'newPurchaserInformation1':
      return differences.length > 0
        ? `Purchaser ${differences[0].purchaserNumber} ${capitalizeDifference(differences[0].type)}: ${getPurchaserInfo(
            toPurchaser[differences[0].purchaserNumber - 1],
            differences[0].type
          )}`
        : '';
    case 'oldPurchaserInformation2':
      return differences.length > 1
        ? `Purchaser ${differences[1].purchaserNumber} ${capitalizeDifference(differences[1].type)}: ${getPurchaserInfo(
            purchaser[differences[1].purchaserNumber - 1],
            differences[1].type
          )}`
        : '';
    case 'newPurchaserInformation2':
      return differences.length > 1
        ? `Purchaser ${differences[1].purchaserNumber} ${capitalizeDifference(differences[1].type)}: ${getPurchaserInfo(
            toPurchaser[differences[1].purchaserNumber - 1],
            differences[1].type
          )}`
        : '';
    case 'oldPurchaserInformation3':
      return differences.length > 2
        ? `Purchaser ${differences[2].purchaserNumber} ${capitalizeDifference(differences[2].type)}: ${getPurchaserInfo(
            purchaser[differences[2].purchaserNumber - 1],
            differences[2].type
          )}`
        : '';
    case 'newPurchaserInformation3':
      return differences.length > 2
        ? `Purchaser ${differences[2].purchaserNumber} ${capitalizeDifference(differences[2].type)}: ${getPurchaserInfo(
            toPurchaser[differences[2].purchaserNumber - 1],
            differences[2].type
          )}`
        : '';
    case 'oldPurchaserInformation4':
      return differences.length > 3
        ? `Purchaser ${differences[3].purchaserNumber} ${capitalizeDifference(differences[3].type)}: ${getPurchaserInfo(
            purchaser[differences[3].purchaserNumber - 1],
            differences[3].type
          )}`
        : '';
    case 'newPurchaserInformation4':
      return differences.length > 3
        ? `Purchaser ${differences[3].purchaserNumber} ${capitalizeDifference(differences[3].type)}: ${getPurchaserInfo(
            toPurchaser[differences[3].purchaserNumber - 1],
            differences[3].type
          )}`
        : '';
    case 'oldPurchaserInformation5':
      return differences.length > 4
        ? `Purchaser ${differences[4].purchaserNumber} ${capitalizeDifference(differences[4].type)}: ${getPurchaserInfo(
            purchaser[differences[4].purchaserNumber - 1],
            differences[4].type
          )}`
        : '';
    case 'newPurchaserInformation5':
      return differences.length > 4
        ? `Purchaser ${differences[4].purchaserNumber} ${capitalizeDifference(differences[4].type)}: ${getPurchaserInfo(
            toPurchaser[differences[4].purchaserNumber - 1],
            differences[4].type
          )}`
        : '';
    // Add Purchaser
    case 'AddAllFullName':
      return [...purchaser, ...addPurchasers]
        .map((purchaser: IPurchaserInfo) => `${purchaser.firstName} ${purchaser.lastName}`)
        .join(' & ');
    case 'Add1PFN':
      return addPurchasers.length ? addPurchasers[0].firstName : '';
    case 'Add1PLN':
      return addPurchasers.length ? addPurchasers[0].lastName : '';
    case 'Add1PLNCaps':
      return addPurchasers.length ? addPurchasers[0].lastName.toUpperCase() : '';
    case 'Add1PfullName':
      return addPurchasers.length ? `${addPurchasers[0].firstName} ${addPurchasers[0].lastName}` : '';
    case 'Add1PfullNameLCaps':
      return addPurchasers.length ? `${addPurchasers[0].firstName} ${addPurchasers[0].lastName.toUpperCase()}` : '';
    case 'Add1PDob':
      return addPurchasers.length ? convertAllDates(addPurchasers[0].dob, mergeValue.format) : '';
    case 'Add1PSIN':
      return addPurchasers.length ? addPurchasers[0].sin : '';
    case 'Add1PPhone':
      return addPurchasers.length ? addPurchasers[0].primaryPhone : '';
    case 'Add1PFullAddress':
      return addPurchasers.length
        ? `${addPurchasers[0].unit ? addPurchasers[0].unit : ''} ${addPurchasers[0].streetAddress}, ${addPurchasers[0].city}, ${
            addPurchasers[0].province
          }, ${addPurchasers[0].postalCode ? addPurchasers[0].postalCode : ''}`
        : '';
    case 'Add1PAddress':
      return addPurchasers.length ? `${addPurchasers[0].unit ? addPurchasers[0].unit : ''} ${addPurchasers[0].streetAddress}` : '';
    case 'Add1PCity':
      return addPurchasers.length ? `${addPurchasers[0].city}` : '';
    case 'Add1PProvince':
      return addPurchasers.length ? `${addPurchasers[0].province}` : '';
    case 'Add1PCode':
      return addPurchasers.length ? `${addPurchasers[0].postalCode}` : '';
    case 'Add1PCountry':
      return addPurchasers.length ? `${addPurchasers[0].country}` : '';
    case 'Add1PEmail':
      return addPurchasers.length ? `${addPurchasers[0].email}` : '';
    case 'Add1PIDNumber':
      return addPurchasers.length ? `${addPurchasers[0].idNumber}` : '';
    case 'Add1PSO':
      return addPurchasers.length && addPurchasers[0].signingOfficers.length ? `${addPurchasers[0].signingOfficers[0].fullName}` : '';
    case 'Add1PBusinessNumber':
      return addPurchasers.length && addPurchasers[0].businessNumber ? `${addPurchasers[0].businessNumber}` : '';
    case 'Add2PFN':
      return addPurchasers.length && addPurchasers.length > 1 ? addPurchasers[1].firstName : '';
    case 'Add2PLN':
      return addPurchasers.length && addPurchasers.length > 1 ? addPurchasers[1].lastName : '';
    case 'Add2PLNCaps':
      return addPurchasers.length && addPurchasers.length > 1 ? addPurchasers[1].lastName.toUpperCase() : '';
    case 'Add2PfullName':
      return addPurchasers.length && addPurchasers.length > 1 ? `${addPurchasers[1].firstName} ${addPurchasers[1].lastName}` : '';
    case 'Add2PfullNameLCaps':
      return addPurchasers.length && addPurchasers.length > 1
        ? `${addPurchasers[1].firstName} ${addPurchasers[1].lastName.toUpperCase()}`
        : '';
    case 'Add2PDob':
      return addPurchasers.length && addPurchasers.length > 1 ? convertAllDates(addPurchasers[1].dob, mergeValue.format) : '';
    case 'Add2PSIN':
      return addPurchasers.length && addPurchasers.length > 1 ? addPurchasers[1].sin : '';
    case 'Add2PPhone':
      return addPurchasers.length && addPurchasers.length > 1 ? addPurchasers[1].primaryPhone : '';
    case 'Add2PFullAddress':
      return addPurchasers.length && addPurchasers.length > 1
        ? `${addPurchasers[1].unit ? addPurchasers[1].unit : ''} ${addPurchasers[1].streetAddress}, ${addPurchasers[1].city}, ${
            addPurchasers[1].province
          }, ${addPurchasers[1].postalCode ? addPurchasers[1].postalCode : ''}`
        : '';
    case 'Add2PAddress':
      return addPurchasers.length && addPurchasers.length > 1
        ? `${addPurchasers[1].unit ? addPurchasers[1].unit : ''} ${addPurchasers[1].streetAddress}`
        : '';
    case 'Add2PCity':
      return addPurchasers.length && addPurchasers.length > 1 ? `${addPurchasers[1].city}` : '';
    case 'Add2PProvince':
      return addPurchasers.length && addPurchasers.length > 1 ? `${addPurchasers[1].province}` : '';
    case 'Add2PCode':
      return addPurchasers.length && addPurchasers.length > 1 ? `${addPurchasers[1].postalCode}` : '';
    case 'Add2PCountry':
      return addPurchasers.length && addPurchasers.length > 1 ? `${addPurchasers[1].country}` : '';
    case 'Add2PEmail':
      return addPurchasers.length && addPurchasers.length > 1 ? `${addPurchasers[1].email}` : '';
    case 'Add2PIDNumber':
      return addPurchasers.length && addPurchasers.length > 1 ? `${addPurchasers[1].idNumber}` : '';
    case 'Add2PSO':
      return addPurchasers.length && addPurchasers.length > 1 && addPurchasers[1].signingOfficers.length
        ? `${addPurchasers[1].signingOfficers[0].fullName}`
        : '';
    case 'Add2PBusinessNumber':
      return addPurchasers.length && addPurchasers.length > 1 && addPurchasers[1].businessNumber
        ? `${addPurchasers[1].businessNumber}`
        : '';
    // Delete Purchaser
    case 'Delete1PFN':
      return deletePurchasers.length ? deletePurchasers[0].firstName : '';
    case 'Delete1PLN':
      return deletePurchasers.length ? deletePurchasers[0].lastName : '';
    case 'Delete1PLNCaps':
      return deletePurchasers.length ? deletePurchasers[0].lastName.toUpperCase() : '';
    case 'Delete1PfullName':
      return deletePurchasers.length ? `${deletePurchasers[0].firstName} ${deletePurchasers[0].lastName}` : '';
    case 'Delete1PfullNameLCaps':
      return deletePurchasers.length ? `${deletePurchasers[0].firstName} ${deletePurchasers[0].lastName.toUpperCase()}` : '';
    case 'Delete1PDob':
      return deletePurchasers.length ? convertAllDates(deletePurchasers[0].dob, mergeValue.format) : '';
    case 'Delete1PSIN':
      return deletePurchasers.length ? deletePurchasers[0].sin : '';
    case 'Delete1PPhone':
      return deletePurchasers.length ? deletePurchasers[0].primaryPhone : '';
    case 'Delete1PFullAddress':
      return deletePurchasers.length
        ? `${deletePurchasers[0].unit ? deletePurchasers[0].unit : ''} ${deletePurchasers[0].streetAddress}, ${deletePurchasers[0].city}, ${
            deletePurchasers[0].province
          }, ${deletePurchasers[0].postalCode ? deletePurchasers[0].postalCode : ''}`
        : '';
    case 'Delete1PAddress':
      return deletePurchasers.length
        ? `${deletePurchasers[0].unit ? deletePurchasers[0].unit : ''} ${deletePurchasers[0].streetAddress}`
        : '';
    case 'Delete1PCity':
      return deletePurchasers.length ? `${deletePurchasers[0].city}` : '';
    case 'Delete1PProvince':
      return deletePurchasers.length ? `${deletePurchasers[0].province}` : '';
    case 'Delete1PCode':
      return deletePurchasers.length ? `${deletePurchasers[0].postalCode}` : '';
    case 'Delete1PCountry':
      return deletePurchasers.length ? `${deletePurchasers[0].country}` : '';
    case 'Delete1PEmail':
      return deletePurchasers.length ? `${deletePurchasers[0].email}` : '';
    case 'Delete1PIDNumber':
      return deletePurchasers.length ? `${deletePurchasers[0].idNumber}` : '';
    case 'Delete1PSO':
      return deletePurchasers.length && deletePurchasers[0].signingOfficers.length
        ? `${deletePurchasers[0].signingOfficers[0].fullName}`
        : '';
    case 'Delete2PFN':
      return deletePurchasers.length && deletePurchasers.length > 1 ? deletePurchasers[1].firstName : '';
    case 'Delete2PLN':
      return deletePurchasers.length && deletePurchasers.length > 1 ? deletePurchasers[1].lastName : '';
    case 'Delete2PLNCaps':
      return deletePurchasers.length && deletePurchasers.length > 1 ? deletePurchasers[1].lastName.toUpperCase() : '';
    case 'Delete2PfullName':
      return deletePurchasers.length && deletePurchasers.length > 1
        ? `${deletePurchasers[1].firstName} ${deletePurchasers[1].lastName}`
        : '';
    case 'Delete2PfullNameLCaps':
      return deletePurchasers.length && deletePurchasers.length > 1
        ? `${deletePurchasers[1].firstName} ${deletePurchasers[1].lastName.toUpperCase()}`
        : '';
    case 'Delete2PDob':
      return deletePurchasers.length && deletePurchasers.length > 1 ? convertAllDates(deletePurchasers[1].dob, mergeValue.format) : '';
    case 'Delete2PSIN':
      return deletePurchasers.length && deletePurchasers.length > 1 ? deletePurchasers[1].sin : '';
    case 'Delete2PPhone':
      return deletePurchasers.length && deletePurchasers.length > 1 ? deletePurchasers[1].primaryPhone : '';
    case 'Delete2PFullAddress':
      return deletePurchasers.length && deletePurchasers.length > 1
        ? `${deletePurchasers[1].unit ? deletePurchasers[1].unit : ''} ${deletePurchasers[1].streetAddress}, ${deletePurchasers[1].city}, ${
            deletePurchasers[1].province
          }, ${deletePurchasers[1].postalCode ? deletePurchasers[1].postalCode : ''}`
        : '';
    case 'Delete2PAddress':
      return deletePurchasers.length && deletePurchasers.length > 1
        ? `${deletePurchasers[1].unit ? deletePurchasers[1].unit : ''} ${deletePurchasers[1].streetAddress}`
        : '';
    case 'Delete2PCity':
      return deletePurchasers.length && deletePurchasers.length > 1 ? `${deletePurchasers[1].city}` : '';
    case 'Delete2PProvince':
      return deletePurchasers.length && deletePurchasers.length > 1 ? `${deletePurchasers[1].province}` : '';
    case 'Delete2PCode':
      return deletePurchasers.length && deletePurchasers.length > 1 ? `${deletePurchasers[1].postalCode}` : '';
    case 'Delete2PCountry':
      return deletePurchasers.length && deletePurchasers.length > 1 ? `${deletePurchasers[1].country}` : '';
    case 'Delete2PEmail':
      return deletePurchasers.length && deletePurchasers.length > 1 ? `${deletePurchasers[1].email}` : '';
    case 'Delete2PIDNumber':
      return deletePurchasers.length && deletePurchasers.length > 1 ? `${deletePurchasers[1].idNumber}` : '';
    case 'Delete2PSO':
      return deletePurchasers.length && deletePurchasers.length > 1 && deletePurchasers[1].signingOfficers.length
        ? `${deletePurchasers[1].signingOfficers[0].fullName}`
        : '';
    case 'UPG1TPrice':
      return deal.upgradeSetOne ? currencyFor(mergeValue, numToCurrencyDecimal).format((upgradeSetOne - upgradeCreditOne) * 1.13).toString() : '';
    case 'UPG1TPriceNetHST':
      return deal.upgradeSetOne ? currencyFor(mergeValue, numToCurrencyDecimal).format(upgradeSetOne).toString() : '';
    case 'UPG1Credit':
      return upgradeCreditOne
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(upgradeCreditOne).toString()
        : project.defaultZeroValue
          ? project.defaultZeroValue
          : '$0';
    case 'UPG2TPrice':
      return deal.upgradeSetTwo ? currencyFor(mergeValue, numToCurrencyDecimal).format((upgradeSetTwo - upgradeCreditTwo) * 1.13).toString() : '';
    case 'UPG2TPriceNetHST':
      return deal.upgradeSetTwo ? currencyFor(mergeValue, numToCurrencyDecimal).format(upgradeSetTwo).toString() : '';
    case 'UPG2Credit':
      return upgradeCreditTwo
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(upgradeCreditTwo).toString()
        : project.defaultZeroValue
          ? project.defaultZeroValue
          : '$0';
    case 'UPG3TPrice':
      return deal.upgradeSetThree ? currencyFor(mergeValue, numToCurrencyDecimal).format((upgradeSetThree - upgradeCreditThree) * 1.13).toString() : '';
    case 'UPG3TPriceNetHST':
      return deal.upgradeSetThree ? currencyFor(mergeValue, numToCurrencyDecimal).format(upgradeSetThree).toString() : '';
    case 'UPG3Credit':
      return upgradeCreditThree
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(upgradeCreditThree).toString()
        : project.defaultZeroValue
          ? project.defaultZeroValue
          : '$0';
    case 'UPG1UPG2Total':
      return deal.upgradeSetOne && deal.upgradeSetTwo
        ? currencyFor(mergeValue, numToCurrencyDecimal).format((upgradeSetOne - upgradeCreditOne) * 1.13 + (upgradeSetTwo - upgradeCreditTwo) * 1.13).toString()
        : '';
    case 'UPG1UPG2TotalNet':
      return deal.upgradeSetOne && deal.upgradeSetTwo
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(upgradeSetOne - upgradeCreditOne + (upgradeSetTwo - upgradeCreditTwo)).toString()
        : '';
    case 'UPG1UPG2UPG3Total':
      return deal.upgradeSetOne && deal.upgradeSetTwo && deal.upgradeSetThree
        ? numToCurrencyDecimal
            .format(
              (upgradeSetOne - upgradeCreditOne) * 1.13 +
                (upgradeSetTwo - upgradeCreditTwo) * 1.13 +
                (upgradeSetThree - upgradeCreditThree) * 1.13
            )
            .toString()
        : '';
    case 'UPG1UPG2UPG3TotalNet':
      return deal.upgradeSetOne && deal.upgradeSetTwo && deal.upgradeSetThree
        ? numToCurrencyDecimal
            .format(upgradeSetOne - upgradeCreditOne + (upgradeSetTwo - upgradeCreditTwo) + (upgradeSetThree - upgradeCreditThree))
            .toString()
        : '';
    case 'DecorSelection1TPrice':
      return deal.upgradeSetOne ? currencyFor(mergeValue, numToCurrencyDecimal).format(decorTotalSetOne) : '';
    case 'DecorSelection1ToTPrice':
      return deal.upgradeSetOne ? currencyFor(mergeValue, numToCurrencyDecimal).format(toDecorTotalSetOne) : '';
    case 'DecorSelection2TPrice':
      return deal.upgradeSetTwo ? currencyFor(mergeValue, numToCurrencyDecimal).format(decorTotalSetTwo) : '';
    case 'DecorSelection2ToTPrice':
      return deal.upgradeSetTwo ? currencyFor(mergeValue, numToCurrencyDecimal).format(toDecorTotalSetTwo) : '';
    case 'DecorSelection3TPrice':
      return deal.upgradeSetThree ? currencyFor(mergeValue, numToCurrencyDecimal).format(decorTotalSetThree) : '';
    case 'DecorSelection3ToTPrice':
      return deal.upgradeSetThree ? currencyFor(mergeValue, numToCurrencyDecimal).format(toDecorTotalSetThree) : '';
    case 'DecorSelectionTotalNetHST':
      return colourSelectionChoices.length
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(colourSelectionChoices.reduce((a: any, b: any) => a + b.price, 0))
        : '';
    case 'DecorSelectionTotal':
      return colourSelectionChoices.length
        ? currencyFor(mergeValue, numToCurrencyDecimal).format(colourSelectionChoices.reduce((a: any, b: any) => a + b.price, 0) * 1.13)
        : '';
    // Lease Management
    case 'LeaseOP1':
      return lease && lease.management ? lease.management.options[0] : '';
    case 'LeaseOP2':
      return lease && lease.management ? lease.management.options[1] : '';
    case 'LeaseOP3':
      return lease && lease.management ? lease.management.options[2] : '';
    case 'LeaseOP4':
      return lease && lease.management ? lease.management.options[3] : '';
    case 'ManagementFee':
      return lease && lease.management ? lease.management.managementFee : '';
    case 'ReRentalFee':
      return lease && lease.management ? lease.management.reRentalFee : '';
    case 'Inspection':
      return lease && lease.management ? lease.management.inspection : '';
    case 'LeaseRenewal':
      return lease && lease.management ? lease.management.leaseRenewal : '';
    case 'StartUpFee':
      return lease && lease.management ? lease.management.startupFee : '';
    case 'ResidentP1':
      return lease && lease.landlords.length >= 1 ? 'Y' : '';
    case 'ResidentP2':
      return lease && lease.landlords.length >= 2 ? 'Y' : '';
    // Lease Listing
    case 'AllLfullNameListing':
      return lease && lease.landlords.length
        ? lease.landlords.map((purchaser: IPurchaserInfo) => `${purchaser.firstName} ${purchaser.lastName}`).join(' & ')
        : '';
    case 'ListingProjectAddress':
      return lease && lease.address ? lease.address : '';
    case 'ListingFullAddress':
      return lease && unit && lease.address ? `${unit.suite}, ${lease.address}` : '';
    case 'ListingAddressNumber':
      return lease && unit && lease.address ? `${lease.address.split(',')[0].match(/\d+/)[0]}` : '';
    case 'ListingAddress':
      return lease && unit && lease.address ? `${lease.address.split(',')[0].replace(/[^a-zA-Z]+/g, ' ')}` : '';
    case 'ListingCity':
      return lease && unit && lease.address ? `${lease.address.split(',')[1].trim()}` : '';
    case 'ListingPostalCode':
      return lease && unit && lease.address ? `${lease.address.split(',')[3].trim()}` : '';
    case 'ListingStartDateDay':
      return lease && lease.listing ? convertAllDates(lease.listing.dateStart, mergeValue.format) : '';
    case 'ListingStartDateMonth':
      return lease && lease.listing ? convertAllDates(lease.listing.dateStart, mergeValue.format) : '';
    case 'ListingStartDateYear':
      return lease && lease.listing ? convertAllDates(lease.listing.dateStart, mergeValue.format) : '';
    case 'ListingStartDate':
      return lease && lease.listing ? convertAllDates(lease.listing.dateStart, mergeValue.format) : '';
    case 'ListingEndDateDay':
      return lease && lease.listing ? convertAllDates(lease.listing.dateEnd, mergeValue.format) : '';
    case 'ListingEndDateMonth':
      return lease && lease.listing ? convertAllDates(lease.listing.dateEnd, mergeValue.format) : '';
    case 'ListingEndDateYear':
      return lease && lease.listing ? convertAllDates(lease.listing.dateEnd, mergeValue.format) : '';
    case 'ListingEndDate':
      return lease && lease.listing ? convertAllDates(lease.listing.dateEnd, mergeValue.format) : '';
    case 'ListingAmount':
      return lease && lease.listing ? currencyFor(mergeValue, numToCurrency).format(lease.listing.amount) : '';
    case 'ListingAmountWords':
      return lease && lease.listing ? numberToEnglish(lease.listing.amount) : '';
    case 'ListingCommission':
      return lease && lease.listing ? lease.listing.listing : '';
    case 'CooperatingCommission':
      return lease && lease.listing ? lease.listing.cooperating : '';
    case 'Holdover':
      return lease && lease.listing ? `${lease.listing.holdover}` : '';
    case 'MlsNumber':
      return lease && lease.listing ? lease.listing.mls : '';
    case 'ListingPendingAmount':
      return lease && lease.listing && lease.listing.pendingAmount ? `${currencyFor(mergeValue, numToCurrency).format(lease.listing.pendingAmount)}` : '';
    case 'ListingPendingExpiryDate':
      return lease && lease.listing && lease.listing.pendingExpiryDate
        ? `${convertAllDates(lease.listing.pendingExpiryDate, mergeValue.format)}`
        : '';
    case 'ListingReason':
      return lease && lease.listing && lease.listing.reason ? lease.listing.reason : '';
    // MLS Data Form
    case 'MLSListingStartDateDay':
      return lease && lease.listing ? convertAllDates(lease.listing.dateStart, mergeValue.format) : '';
    case 'MLSListingStartDateMonth':
      return lease && lease.listing ? convertAllDates(lease.listing.dateStart, mergeValue.format) : '';
    case 'MLSListingStartDateYear':
      return lease && lease.listing ? convertAllDates(lease.listing.dateStart, mergeValue.format) : '';
    case 'MLSListingEndDateDay':
      return lease && lease.listing ? convertAllDates(lease.listing.dateEnd, mergeValue.format) : '';
    case 'MLSListingEndDateMonth':
      return lease && lease.listing ? convertAllDates(lease.listing.dateEnd, mergeValue.format) : '';
    case 'MLSListingEndDateYear':
      return lease && lease.listing ? convertAllDates(lease.listing.dateEnd, mergeValue.format) : '';
    case 'MLSPossessionDateDay':
      return lease && lease.listing ? convertAllDates(lease.listing.dateAvailable, mergeValue.format) : '';
    case 'MLSPossessionDateMonth':
      return lease && lease.listing ? convertAllDates(lease.listing.dateAvailable, mergeValue.format) : '';
    case 'MLSPossessionDateYear':
      return lease && lease.listing ? convertAllDates(lease.listing.dateAvailable, mergeValue.format) : '';
    case 'PossessionRemarks':
      return lease && lease.listing ? lease.listing.possessionRemarks : '';
    case 'GarageParking':
      if (lease && lease.listing && lease.listing.parking && lease.listing.parking.length) {
        let parking = lease.listing.parking.find((parking: any) => parking.space === 'Garage');
        if (parking) {
          return `${parking.count}`;
        } else return '0';
      } else return '0';
    case 'DriveParking':
      if (lease && lease.listing && lease.listing.parking && lease.listing.parking.length) {
        let parking = lease.listing.parking.find((parking: any) => parking.space === 'Drive');
        if (parking) {
          return `${parking.count}`;
        } else return '0';
      } else return '0';
    case 'TotalParking':
      return lease && lease.listing && lease.listing.parking && lease.listing.parking.length
        ? `${lease.listing.parking.reduce((a: any, b: any) => a + b.count, 0)}`
        : '0';
    case 'SqftSource':
      return lease && lease.listing ? lease.listing.sqftSource : '';
    case 'TotalRooms':
      return lease && lease.listing ? lease.listing.totalRooms[0].count : '';
    case 'TotalRoomsAdditional':
      return lease && lease.listing ? lease.listing.totalRooms[0].additional : '';
    case 'NumberOfBedrooms':
      return lease && lease.listing ? lease.listing.totalRooms[1].count : '';
    case 'NumberOfBedroomsAdditional':
      return lease && lease.listing ? lease.listing.totalRooms[1].additional : '';
    case 'NumberOfKitchens':
      return lease && lease.listing ? lease.listing.totalRooms[2].count : '';
    case 'NumberOfKitchensAdditional':
      return lease && lease.listing ? lease.listing.totalRooms[2].additional : '';
    case 'Washroom1':
      return lease && lease.listing && lease.listing.washrooms.length ? lease.listing.washrooms[0].count : '';
    case 'Pieces1':
      return lease && lease.listing && lease.listing.washrooms.length ? lease.listing.washrooms[0].pieces : '';
    case 'Washroom2':
      return lease && lease.listing && lease.listing.washrooms.length && lease.listing.washrooms.length > 1
        ? lease.listing.washrooms[1].count
        : '';
    case 'Pieces2':
      return lease && lease.listing && lease.listing.washrooms.length && lease.listing.washrooms.length > 1
        ? lease.listing.washrooms[1].pieces
        : '';
    case 'Washroom3':
      return lease && lease.listing && lease.listing.washrooms.length && lease.listing.washrooms.length > 2
        ? lease.listing.washrooms[2].count
        : '';
    case 'Pieces3':
      return lease && lease.listing && lease.listing.washrooms.length && lease.listing.washrooms.length > 2
        ? lease.listing.washrooms[2].pieces
        : '';
    case 'Room1Level':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].level : '';
    case 'Room1Name':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].name : '';
    case 'Room1Length':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].length : '';
    case 'Room1Width':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].width : '';
    case 'Room1Height':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].height : '';
    case 'Room1Description1':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].description1 : '';
    case 'Room1Description2':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].description2 : '';
    case 'Room1Description3':
      return lease && lease.listing && lease.listing.rooms.length ? lease.listing.rooms[0].description3 : '';
    case 'Room2Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1 ? lease.listing.rooms[1].level : '';
    case 'Room2Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1 ? lease.listing.rooms[1].name : '';
    case 'Room2Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1 ? lease.listing.rooms[1].length : '';
    case 'Room2Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1 ? lease.listing.rooms[1].width : '';
    case 'Room2Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1 ? lease.listing.rooms[1].height : '';
    case 'Room2Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1
        ? lease.listing.rooms[1].description1
        : '';
    case 'Room2Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1
        ? lease.listing.rooms[1].description2
        : '';
    case 'Room2Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 1
        ? lease.listing.rooms[1].description3
        : '';
    case 'Room3Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2 ? lease.listing.rooms[2].level : '';
    case 'Room3Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2 ? lease.listing.rooms[2].name : '';
    case 'Room3Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2 ? lease.listing.rooms[2].length : '';
    case 'Room3Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2 ? lease.listing.rooms[2].width : '';
    case 'Room3Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2 ? lease.listing.rooms[2].height : '';
    case 'Room3Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2
        ? lease.listing.rooms[2].description1
        : '';
    case 'Room3Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2
        ? lease.listing.rooms[2].description2
        : '';
    case 'Room3Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 2
        ? lease.listing.rooms[2].description3
        : '';
    case 'Room4Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3 ? lease.listing.rooms[3].level : '';
    case 'Room4Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3 ? lease.listing.rooms[3].name : '';
    case 'Room4Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3 ? lease.listing.rooms[3].length : '';
    case 'Room4Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3 ? lease.listing.rooms[3].width : '';
    case 'Room4Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3 ? lease.listing.rooms[3].height : '';
    case 'Room4Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3
        ? lease.listing.rooms[3].description1
        : '';
    case 'Room4Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3
        ? lease.listing.rooms[3].description2
        : '';
    case 'Room4Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 3
        ? lease.listing.rooms[3].description3
        : '';
    case 'Room5Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4 ? lease.listing.rooms[4].level : '';
    case 'Room5Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4 ? lease.listing.rooms[4].name : '';
    case 'Room5Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4 ? lease.listing.rooms[4].length : '';
    case 'Room5Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4 ? lease.listing.rooms[4].width : '';
    case 'Room5Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4 ? lease.listing.rooms[4].height : '';
    case 'Room5Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4
        ? lease.listing.rooms[4].description1
        : '';
    case 'Room5Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4
        ? lease.listing.rooms[4].description2
        : '';
    case 'Room5Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 4
        ? lease.listing.rooms[4].description3
        : '';
    case 'Room6Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5 ? lease.listing.rooms[5].level : '';
    case 'Room6Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5 ? lease.listing.rooms[5].name : '';
    case 'Room6Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5 ? lease.listing.rooms[5].length : '';
    case 'Room6Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5 ? lease.listing.rooms[5].width : '';
    case 'Room6Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5 ? lease.listing.rooms[5].height : '';
    case 'Room6Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5
        ? lease.listing.rooms[5].description1
        : '';
    case 'Room6Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5
        ? lease.listing.rooms[5].description2
        : '';
    case 'Room6Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 5
        ? lease.listing.rooms[5].description3
        : '';
    case 'Room7Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6 ? lease.listing.rooms[6].level : '';
    case 'Room7Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6 ? lease.listing.rooms[6].name : '';
    case 'Room7Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6 ? lease.listing.rooms[6].length : '';
    case 'Room7Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6 ? lease.listing.rooms[6].width : '';
    case 'Room7Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6 ? lease.listing.rooms[6].height : '';
    case 'Room7Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6
        ? lease.listing.rooms[6].description1
        : '';
    case 'Room7Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6
        ? lease.listing.rooms[6].description2
        : '';
    case 'Room7Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 6
        ? lease.listing.rooms[6].description3
        : '';
    case 'Room8Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7 ? lease.listing.rooms[7].level : '';
    case 'Room8Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7 ? lease.listing.rooms[7].name : '';
    case 'Room8Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7 ? lease.listing.rooms[7].length : '';
    case 'Room8Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7 ? lease.listing.rooms[7].width : '';
    case 'Room8Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7 ? lease.listing.rooms[7].height : '';
    case 'Room8Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7
        ? lease.listing.rooms[7].description1
        : '';
    case 'Room8Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7
        ? lease.listing.rooms[7].description2
        : '';
    case 'Room8Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 7
        ? lease.listing.rooms[7].description3
        : '';
    case 'Room9Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8 ? lease.listing.rooms[8].level : '';
    case 'Room9Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8 ? lease.listing.rooms[8].name : '';
    case 'Room9Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8 ? lease.listing.rooms[8].length : '';
    case 'Room9Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8 ? lease.listing.rooms[8].width : '';
    case 'Room9Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8 ? lease.listing.rooms[8].height : '';
    case 'Room9Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8
        ? lease.listing.rooms[8].description1
        : '';
    case 'Room9Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8
        ? lease.listing.rooms[8].description2
        : '';
    case 'Room9Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 8
        ? lease.listing.rooms[8].description3
        : '';
    case 'Room10Level':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9 ? lease.listing.rooms[9].level : '';
    case 'Room10Name':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9 ? lease.listing.rooms[9].name : '';
    case 'Room10Length':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9 ? lease.listing.rooms[9].length : '';
    case 'Room10Width':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9 ? lease.listing.rooms[9].width : '';
    case 'Room10Height':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9 ? lease.listing.rooms[9].height : '';
    case 'Room10Description1':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9
        ? lease.listing.rooms[9].description1
        : '';
    case 'Room10Description2':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9
        ? lease.listing.rooms[9].description2
        : '';
    case 'Room10Description3':
      return lease && lease.listing && lease.listing.rooms.length && lease.listing.rooms.length > 9
        ? lease.listing.rooms[9].description3
        : '';
    // Lease Tenancy
    case 'T1FirstNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length ? `${lease.tenancy.tenants[0].firstName}` : '';
    case 'T1LastNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length ? `${lease.tenancy.tenants[0].lastName}` : '';
    case 'T1FullNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length
        ? `${lease.tenancy.tenants[0].firstName} ${lease.tenancy.tenants[0].lastName}`
        : '';
    case 'T1EmailTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length ? `${lease.tenancy.tenants[0].email}` : '';
    case 'T2FirstNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 2 ? `${lease.tenancy.tenants[1].firstName}` : '';
    case 'T2LastNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 2 ? `${lease.tenancy.tenants[1].lastName}` : '';
    case 'T2FullNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 2
        ? `${lease.tenancy.tenants[1].firstName} ${lease.tenancy.tenants[1].lastName}`
        : '';
    case 'T2EmailTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 2 ? `${lease.tenancy.tenants[1].email}` : '';
    case 'T3FirstNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 3 ? `${lease.tenancy.tenants[2].firstName}` : '';
    case 'T3LastNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 3 ? `${lease.tenancy.tenants[2].lastName}` : '';
    case 'T3FullNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 3
        ? `${lease.tenancy.tenants[2].firstName} ${lease.tenancy.tenants[2].lastName}`
        : '';
    case 'T3EmailTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 3 ? `${lease.tenancy.tenants[2].email}` : '';
    case 'T4FirstNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 4 ? `${lease.tenancy.tenants[3].firstName}` : '';
    case 'T4LastNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 4 ? `${lease.tenancy.tenants[3].lastName}` : '';
    case 'T4FullNameTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 4
        ? `${lease.tenancy.tenants[3].firstName} ${lease.tenancy.tenants[3].lastName}`
        : '';
    case 'T4EmailTenancy':
      return lease && lease.tenancy && lease.tenancy.tenants.length >= 4 ? `${lease.tenancy.tenants[3].email}` : '';
    case 'L1FirstNameTenancy':
      return lease && lease.tenancy && lease.landlords.length ? `${lease.landlords[0].firstName}` : '';
    case 'L1LastNameTenancy':
      return lease && lease.tenancy && lease.landlords.length ? `${lease.landlords[0].lastName}` : '';
    case 'L1FullNameTenancy':
      return lease && lease.tenancy && lease.landlords.length ? `${lease.landlords[0].firstName} ${lease.landlords[0].lastName}` : '';
    case 'L1EmailTenancy':
      return lease && lease.tenancy && lease.landlords.length
        ? lease.management
          ? 'leasing@portfolioproperties.ca'
          : `${lease.landlords[0].email}`
        : '';
    case 'L1PhoneTenancy':
      return lease && lease.tenancy && lease.landlords.length ? `${lease.landlords[0].primaryPhone}` : '';
    case 'L2PhoneTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 2 ? `${lease.landlords[1].primaryPhone}` : '';
    case 'L2FirstNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 2 ? `${lease.landlords[1].firstName}` : '';
    case 'L2LastNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 2 ? `${lease.landlords[1].lastName}` : '';
    case 'L2FullNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 2 ? `${lease.landlords[1].firstName} ${lease.landlords[1].lastName}` : '';
    case 'L2EmailTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 2 ? (lease.management ? '' : `${lease.landlords[1].email}`) : '';
    case 'L2yPhoneTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 2 ? `${lease.landlords[1].primaryPhone}` : '';
    case 'L3FirstNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 3 ? `${lease.landlords[2].firstName}` : '';
    case 'L3LastNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 3 ? `${lease.landlords[2].lastName}` : '';
    case 'L3FullNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 3 ? `${lease.landlords[2].firstName} ${lease.landlords[2].lastName}` : '';
    case 'L3EmailTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 3 ? (lease.management ? '' : `${lease.landlords[2].email}`) : '';
    case 'L3PhoneTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 3 ? `${lease.landlords[2].primaryPhone}` : '';
    case 'L4FirstNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 4 ? `${lease.landlords[3].firstName}` : '';
    case 'L4LastNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 4 ? `${lease.landlords[3].lastName}` : '';
    case 'L4FullNameTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 4 ? `${lease.landlords[3].firstName} ${lease.landlords[3].lastName}` : '';
    case 'L4EmailTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 4 ? (lease.management ? '' : `${lease.landlords[3].email}`) : '';
    case 'L4PhoneTenancy':
      return lease && lease.tenancy && lease.landlords.length >= 4 ? `${lease.landlords[3].primaryPhone}` : '';
    case 'LandlordUnitNumber':
      return lease && lease.tenancy && lease.landlords.length
        ? lease.management
          ? '15A'
          : `${lease.landlords[0].unit ? lease.landlords[0].unit : ''}`
        : '';
    case 'LandlordStreetNumber':
      return lease && lease.tenancy && lease.landlords.length
        ? lease.management
          ? '3220'
          : `${lease.landlords[0].streetAddress.match(/\d+/)[0]}`
        : '';
    case 'LandlordStreetAddress':
      return lease && lease.tenancy && lease.landlords.length
        ? lease.management
          ? 'Dufferin Street'
          : `${lease.landlords[0].streetAddress.replace(/[^a-zA-Z]+/g, '')}`
        : '';
    case 'LandlordCity':
      return lease && lease.tenancy && lease.landlords.length ? (lease.management ? 'Toronto' : `${lease.landlords[0].city}`) : '';
    case 'LandlordProvince':
      return lease && lease.tenancy && lease.landlords.length ? (lease.management ? 'Ontario' : `${lease.landlords[0].province}`) : '';
    case 'LandlordPCode':
      return lease && lease.tenancy && lease.landlords.length ? (lease.management ? 'M6A 2T3' : `${lease.landlords[0].postalCode}`) : '';
    case 'TenancyStartDate':
      return lease && lease.tenancy ? `${convertAllDates(lease.tenancy.dateStart, mergeValue.format)}` : '';
    case 'TenancyEndDate':
      return lease && lease.tenancy ? `${convertAllDates(lease.tenancy.dateEnd, mergeValue.format)}` : '';
    case 'TenancyTypeFixedCheck':
      return lease && lease.tenancy && lease.tenancy.tenancyType === 'Fixed Length of Time' ? 'X' : '';
    case 'TenancyTypeMonthlyCheck':
      return lease && lease.tenancy && lease.tenancy.tenancyType === 'Monthly' ? 'X' : '';
    case 'TenancyTypeOtherCheck':
      return lease && lease.tenancy && lease.tenancy.tenancyType === 'Other' ? 'X' : '';
    case 'PaymentDate':
      return lease && lease.tenancy && lease.tenancy.paymentDay ? lease.tenancy.paymentDay : '';
    case 'PaymentTypeMonthlyCheck':
      return lease && lease.tenancy && lease.tenancy.paymentType === 'Monthly' ? 'X' : '';
    case 'PaymentTypeOtherCheck':
      return lease && lease.tenancy && lease.tenancy.paymentType === 'Other' ? 'X' : '';
    case 'LeaseAmount':
      return lease && lease.tenancy && lease.tenancy.amount ? `${currencyFor(mergeValue, numToCurrency).format(lease.tenancy.amount)}` : '';
    case 'PayableTo':
      return lease && lease.tenancy && lease.tenancy.payableTo ? lease.tenancy.payableTo : '';
    case 'PaymentMethod':
      return lease && lease.tenancy && lease.tenancy.paymentMethod ? lease.tenancy.paymentMethod : '';
    case 'PartialRentAmount':
      return lease && lease.tenancy && lease.tenancy.partialRentAmount
        ? `${currencyFor(mergeValue, numToCurrency).format(lease.tenancy.partialRentAmount).replace('$', '')}`
        : '';
    case 'PartialRentPaymentDate':
      return lease && lease.tenancy && lease.tenancy.partialRentPaymentDate
        ? `${convertAllDates(lease.tenancy.partialRentPaymentDate, mergeValue.format)}`
        : '';
    case 'PartialRentStartDate':
      return lease && lease.tenancy && lease.tenancy.partialRentStartDate
        ? `${convertAllDates(lease.tenancy.partialRentStartDate, mergeValue.format)}`
        : '';
    case 'PartialRentEndDate':
      return lease && lease.tenancy && lease.tenancy.partialRentEndDate
        ? `${convertAllDates(lease.tenancy.partialRentEndDate, mergeValue.format)}`
        : '';
    case 'GasCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length && lease.tenancy.utilities[0].answer.includes('Yes') ? 'X' : '';
    case 'GasNoCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length && lease.tenancy.utilities[0].answer.includes('No') ? 'X' : '';
    case 'ACCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 2 && lease.tenancy.utilities[1].answer.includes('Yes') ? 'X' : '';
    case 'ACNoCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 2 && lease.tenancy.utilities[1].answer.includes('No') ? 'X' : '';
    case 'StorageCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 3 && lease.tenancy.utilities[2].answer.includes('Yes') ? 'X' : '';
    case 'StorageNoCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 3 && lease.tenancy.utilities[2].answer.includes('No') ? 'X' : '';
    case 'LaundryCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 4 && lease.tenancy.utilities[3].answer.includes('Yes') ? 'X' : '';
    case 'LaundryNoCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 4 && lease.tenancy.utilities[3].answer.includes('No') ? 'X' : '';
    case 'LaundryNoCharge':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 4 && lease.tenancy.utilities[3].answer.includes('No Charge')
        ? 'X'
        : '';
    case 'LaundryPayPerUse':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 4 && lease.tenancy.utilities[3].answer.includes('Pay Per Use')
        ? 'X'
        : '';
    case 'GuestParkingCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 5 && lease.tenancy.utilities[4].answer.includes('Yes') ? 'X' : '';
    case 'GuestParkingNoCheck':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 5 && lease.tenancy.utilities[4].answer.includes('No') ? 'X' : '';
    case 'GuestParkingNoCharge':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 5 && lease.tenancy.utilities[4].answer.includes('No Charge')
        ? 'X'
        : '';
    case 'GuestParkingPayPerUse':
      return lease && lease.tenancy && lease.tenancy.utilities.length >= 5 && lease.tenancy.utilities[4].answer.includes('Pay Per Use')
        ? 'X'
        : '';
    case 'ElectricityLandlordCheck':
      return lease && lease.tenancy && lease.tenancy.electricity === 'Landlord' ? 'X' : '';
    case 'ElectricityTenantCheck':
      return lease && lease.tenancy && lease.tenancy.electricity === 'Tenant' ? 'X' : '';
    case 'HeatLandlordCheck':
      return lease && lease.tenancy && lease.tenancy.heat === 'Landlord' ? 'X' : '';
    case 'HeatTenantCheck':
      return lease && lease.tenancy && lease.tenancy.heat === 'Tenant' ? 'X' : '';
    case 'WaterLandlordCheck':
      return lease && lease.tenancy && lease.tenancy.water === 'Landlord' ? 'X' : '';
    case 'WaterTenantCheck':
      return lease && lease.tenancy && lease.tenancy.water === 'Tenant' ? 'X' : '';
    case 'AddressName':
      return;
    case 'AddressNumber':
      return;
    case 'ParkingDescription':
      return lease && lease.tenancy && lease.tenancy.parkingDescription ? lease.tenancy.parkingDescription : '';
    case 'LockerDescription':
      return lease && lease.tenancy && lease.tenancy.lockerDescription ? lease.tenancy.lockerDescription : '';
    case 'NoRentDiscountCheck':
      return lease && lease.tenancy && !lease.tenancy.rentDiscount ? 'X' : '';
    case 'RentDiscountCheck':
      return lease && lease.tenancy && lease.tenancy.rentDiscount ? 'X' : '';
    case 'RentDiscountDescription':
      return lease && lease.tenancy && lease.tenancy.rentDiscount ? lease.tenancy.rentDiscount : '';
    case 'NoRentDepositCheck':
      return lease && lease.tenancy && !lease.tenancy.rentDepositAmount ? 'X' : '';
    case 'RentDepositCheck':
      return lease && lease.tenancy && lease.tenancy.rentDepositAmount ? 'X' : '';
    case 'RentDepositAmount':
      return lease && lease.tenancy && lease.tenancy.rentDepositAmount
        ? `${currencyFor(mergeValue, numToCurrency).format(lease.tenancy.rentDepositAmount).replace('$', '')}`
        : '';
    case 'NoKeyDepositCheck':
      return lease && lease.tenancy && !lease.tenancy.keyDepositAmount ? 'X' : '';
    case 'KeyDepositCheck':
      return lease && lease.tenancy && lease.tenancy.keyDepositAmount ? 'X' : '';
    case 'KeyDepositAmount':
      return lease && lease.tenancy && lease.tenancy.keyDepositAmount
        ? `${currencyFor(mergeValue, numToCurrency).format(lease.tenancy.keyDepositAmount).replace('$', '')}`
        : '';
    case 'KeyDepositDescription':
      return lease && lease.tenancy && lease.tenancy.keyDeposit ? lease.tenancy.keyDeposit : '';
    case 'NoSmokingRulesCheck':
      return lease && lease.tenancy && !lease.tenancy.smokingRules ? 'X' : '';
    case 'SmokingRulesCheck':
      return lease && lease.tenancy && lease.tenancy.smokingRules ? 'X' : '';
    case 'SmokingRulesDescription':
      return lease && lease.tenancy && lease.tenancy.smokingRulesDescription ? lease.tenancy.smokingRulesDescription : '';
    case 'P1Check':
      return purchaser.length ? 'X' : '';
    case 'P2Check':
      return purchaser.length >= 2 ? 'X' : '';
    case 'P3Check':
      return purchaser.length >= 3 ? 'X' : '';
    case 'P4Check':
      return purchaser.length >= 4 ? 'X' : '';
    case 'Purchaser1Check':
      return purchaser.length && !purchaser[0].corp ? 'X' : '';
    case 'Corp1Check':
      return purchaser.length && purchaser[0].corp ? 'X' : '';
    case 'ResidentialCheck':
      return unit?.type && unit?.type === 'residential' ? 'X' : '';
    case 'CommercialCheck':
      return unit?.type && unit?.type === 'commercial' ? 'X' : '';
    case 'Checkmark':
      return 'X';
    case 'PropertyInsurance':
      return lease && lease.management && lease.management.insurance ? 'X' : '';
    case 'AllAssignorFullName':
      return assignment && assignment.assignors.length
        ? assignment.assignors.map((purchaser: IPurchaserInfo) => `${purchaser.firstName} ${purchaser.lastName}`).join(' & ')
        : '';
    case 'Assignor1FirstName':
      return assignment && assignment.assignors.length ? `${assignment.assignors[0].firstName}` : '';
    case 'Assignor1LastName':
      return assignment && assignment.assignors.length ? `${assignment.assignors[0].lastName}` : '';
    case 'Assignor1FullName':
      return assignment && assignment.assignors.length ? `${assignment.assignors[0].firstName} ${assignment.assignors[0].lastName}` : '';
    case 'Assignor2FirstName':
      return assignment && assignment.assignors.length > 1 ? `${assignment.assignors[1].firstName}` : '';
    case 'Assignor2LastName':
      return assignment && assignment.assignors.length > 1 ? `${assignment.assignors[1].lastName}` : '';
    case 'Assignor2FullName':
      return assignment && assignment.assignors.length > 1
        ? `${assignment.assignors[1].firstName} ${assignment.assignors[0].lastName}`
        : '';
    case 'Assignor3FirstName':
      return assignment && assignment.assignors.length > 2 ? `${assignment.assignors[2].firstName}` : '';
    case 'Assignor3LastName':
      return assignment && assignment.assignors.length > 2 ? `${assignment.assignors[2].lastName}` : '';
    case 'Assignor3FullName':
      return assignment && assignment.assignors.length > 2
        ? `${assignment.assignors[2].firstName} ${assignment.assignors[0].lastName}`
        : '';
    case 'Assignor4FirstName':
      return assignment && assignment.assignors.length > 3 ? `${assignment.assignors[3].firstName}` : '';
    case 'Assignor4LastName':
      return assignment && assignment.assignors.length > 3 ? `${assignment.assignors[3].lastName}` : '';
    case 'Assignor4FullName':
      return assignment && assignment.assignors.length > 3
        ? `${assignment.assignors[3].firstName} ${assignment.assignors[0].lastName}`
        : '';
    default:
      return mergeValue.key;
  }
};

export const exportCsv = (fileName: string, columnList: any, initialData: any) => {
  const columns = columnList.filter((columnDef: any) => {
    return !columnDef.hidden && columnDef.field && columnDef.export !== false;
  });

  const data = initialData.map((rowData: any) =>
    columns.map((columnDef: any) => {
      if (fileName === 'rangeReport.csv') {
        if (columnDef.field === 'range') {
          return `${numToCurrency.format(rowData.rangeMin)} to ${numToCurrency.format(rowData.rangeMax)}`;
        }
      }
      if (fileName === 'DailySummary.csv') {
        if (columnDef.field === 'status') {
          return `${rowData.status}`;
        }
      }
      if (fileName === 'allOutstandingRows.csv') {
        if (columnDef.field === 'suite') {
          return `${rowData.unit.suite}`;
        }
      }
      if (fileName === 'worksheets.csv') {
        if (columnDef.field === 'suite') {
          return `${rowData.unit.suite}`;
        }
        if (columnDef.field === 'purchaser') {
          let purchasers = rowData.purchasers.map((purchaser: any) => `${purchaser.firstName} ${purchaser.lastName}`);
          return purchasers.join(', ');
        }
        if (columnDef.field === 'options') {
          let options = rowData.options.map((option: any) => `${option.name} - x${option.purchaseAmount}`);
          return options.join(', ');
        }
        if (columnDef.field === 'approval') {
          if (rowData.managerApproval) {
            return 'Approved';
          } else return 'Not Approved';
        }
        if (!columnDef.field) {
          return '';
        }
      }
      if (fileName === 'dealWorksheets.csv') {
        if (columnDef.field === 'suite') {
          return `${rowData.unit.suite}`;
        }
        if (columnDef.field === 'purchaser') {
          let purchasers = rowData.purchasers.map((purchaser: any) => `${purchaser.firstName} ${purchaser.lastName}`);
          return purchasers.join(', ');
        }
        if (columnDef.field === 'options') {
          let options = rowData.options.map((option: any) => `${option.name} - x${option.purchaseAmount}`);
          return options.join(', ');
        }
        if (columnDef.field === 'joinWaitlist') {
          if (rowData.joinWaitlist) {
            return true;
          } else return false;
        }
        if (!columnDef.field) {
          return '';
        }
      }
      if (fileName === 'wishlists.csv') {
        if (columnDef.field === 'wishlistChoices') {
          let wishlistChoices = rowData.wishlistChoices.map((wishlist: any) => {
            let wishlistModelTypes = wishlist.modelTypes.map((models: string) => models);
            wishlistModelTypes.join(', ');
            return `${wishlist.unitType} - ${wishlistModelTypes} - ${wishlist.level} - ${wishlist.exposure}`;
          });
          return wishlistChoices.join(', ');
        }
        if (columnDef.field === 'purchaser') {
          let purchasers = rowData.purchasers.map((purchaser: any) => `${purchaser.firstName} ${purchaser.lastName}`);
          return purchasers.join(', ');
        }
        if (columnDef.field === 'options') {
          let options = rowData.options.map((option: any) => `${option.name} - x${option.purchaseAmount}`);
          return options.join(', ');
        }
        if (columnDef.field === 'joinWaitlist') {
          if (rowData.joinWaitlist) {
            return 'Yes';
          } else return 'No';
        }
      }
      return columnDef.render ? columnDef.render(rowData) : rowData[columnDef.field];
    })
  );

  const builder = new CsvBuilder(fileName);
  builder
    .setDelimeter(',')
    .setColumns(columns.map((columnDef: any) => columnDef.title))
    .addRows(data)
    .exportFile();
};

export const exportClosing = (fileName: string, columnList: any, initialData: any) => {
  const columns = columnList.filter((columnDef: any) => {
    return !columnDef.hidden && columnDef.export !== false;
  });

  const data = initialData.map((rowData: any, index: number) =>
    columns.map((columnDef: any) => {
      if (columnDef.Header === 'Suite') {
        return rowData.suite;
      }
      if (columnDef.Header === 'Count') {
        return index + 1;
      }
      return columnDef.accessor ? columnDef.accessor(rowData) : rowData[columnDef.Header];
    })
  );

  const builder = new CsvBuilder(fileName);
  builder
    .setDelimeter(',')
    .setColumns(columns.map((columnDef: any) => columnDef.Header))
    .addRows(data)
    .exportFile();
};

export const exportAllSelectedRows = (fileName: string, allColumns: any, selection: any) => {
  new CsvBuilder(fileName)
    .setColumns(allColumns.map((col: any) => col.title))
    .addRows(
      selection.map((rowData: any) =>
        allColumns.map((columnDef: any) => {
          return columnDef.render ? columnDef.render(rowData) : rowData[columnDef.field];
        })
      )
    )
    .exportFile();
};

export const unitContent = (updatedUnit: IUnit, unit: IUnit) => {
  let difference: any = Object.keys(updatedUnit).filter(
    (k: any) => updatedUnit[k as keyof typeof updatedUnit] !== unit[k as keyof typeof unit]
  );
  let string = '';

  if (difference.includes('status')) {
    string += `Status has been changed from ${unit.status} to ${updatedUnit.status}.\n`;
  }
  if (difference.includes('suite')) {
    string += `Suite has been changed from ${unit.suite} to ${updatedUnit.suite}.\n`;
  }
  if (difference.includes('level')) {
    string += `Level has been changed from ${unit.level} to ${updatedUnit.level}.\n`;
  }
  if (difference.includes('basePrice')) {
    string += `Base Price has been changed from ${numToCurrency.format(unit.basePrice)} to ${numToCurrency.format(
      updatedUnit.basePrice
    )}.\n`;
  }
  if (difference.includes('modelType')) {
    string += `Model Type has been changed from ${unit.modelType} to ${updatedUnit.modelType}.\n`;
  }
  if (difference.includes('unitType')) {
    string += `Unit Type has been changed from ${unit.unitType} to ${updatedUnit.unitType}.\n`;
  }
  if (difference.includes('bathroom')) {
    string += `Bathroom has been changed from ${unit.bathroom} to ${updatedUnit.bathroom}.\n`;
  }
  if (difference.includes('size')) {
    string += `Size has been changed from ${unit.size} to ${updatedUnit.size}.\n`;
  }
  if (difference.includes('outdoor')) {
    string += `Outdoor Size has been changed from ${getOutdoorSize(unit.outdoor)} to ${getOutdoorSize(updatedUnit.outdoor)}.\n`;
    string += `Outdoor Type has been changed from ${getOutdoorType(unit.outdoor)} to ${getOutdoorType(updatedUnit.outdoor)}.\n`;
  }
  if (difference.includes('tempAllocation')) {
    string += `Temporary Allocation has been change from ${unit.tempAllocation ? unit.tempAllocation : 'None'} to ${
      updatedUnit.tempAllocation
    }.\n`;
  }
  if (difference.includes('allocation')) {
    let unitAllocation = '';
    let updatedAllocation = '';
    if (unit.allocation) {
      unitAllocation = unit?.allocation?.fullName!;
    } else {
      unitAllocation = 'None';
    }
    if (updatedUnit.allocation) {
      updatedAllocation = updatedUnit?.allocation?.fullName!;
    } else {
      updatedAllocation = 'None';
    }

    string += `Allocation has been changed from ${unitAllocation} to ${updatedAllocation ? updatedAllocation : 'None'}.\n`;
  }

  return string;
};

export const dealContent = (updatedDeal: IDeal, deal: IDeal) => {
  let difference: any = Object.keys(updatedDeal).filter(
    (k: any) => updatedDeal[k as keyof typeof updatedDeal] !== deal[k as keyof typeof deal]
  );
  let string = '';

  if (updatedDeal.salesRep && deal.salesRep.fullName !== updatedDeal.salesRep.fullName) {
    string += `Sales Rep has been changed from ${deal?.salesRep?.fullName!} to ${updatedDeal?.salesRep?.fullName!}.\n\n`;
  }
  if (difference.includes('signDate')) {
    string += `Sign Date has been changed from ${convertAllDates(deal.signDate, 'PPPP')} to ${convertAllDates(
      updatedDeal.signDate,
      'PPPP'
    )}.\n\n`;
  }
  if (difference.includes('executeDate')) {
    string += `Execute Date has been changed from ${convertAllDates(deal.executeDate, 'PPPP')} to ${convertAllDates(
      updatedDeal.executeDate,
      'PPPP'
    )}.\n\n`;
  }
  if (difference.includes('firmDate')) {
    string += `Firm Date has been changed from ${convertAllDates(deal.firmDate, 'PPPP')} to ${convertAllDates(
      updatedDeal.firmDate,
      'PPPP'
    )}.\n\n`;
  }
  if (difference.includes('basePrice')) {
    string += `Deal Base Price has been changed from ${numToCurrency.format(deal.basePrice)} to ${numToCurrency.format(
      updatedDeal.basePrice
    )}.\n\n`;
  }

  if (!string) {
    string = 'No Change';
  }

  return string;
};

export const purchaserContent = (updatedPurchaser: IPurchaserInfo, purchaser: IPurchaserInfo) => {
  let difference: any = Object.keys(updatedPurchaser).filter(
    (k: any) => updatedPurchaser[k as keyof typeof updatedPurchaser] !== purchaser[k as keyof typeof purchaser]
  );
  let string = '';

  if (difference.includes('firstName')) {
    string += `First Name has been changed from ${purchaser.firstName} to ${updatedPurchaser.firstName}.\n`;
  }
  if (difference.includes('lastName')) {
    string += `Last Name has been changed from ${purchaser.lastName} to ${updatedPurchaser.lastName}.\n`;
  }
  if (difference.includes('email')) {
    string += `Email has been changed from ${purchaser.email} to ${updatedPurchaser.email}.\n`;
  }
  if (difference.includes('dob')) {
    string += `Date of Birth has been changed from ${convertAllDates(purchaser.dob, 'P')} to ${convertAllDates(
      updatedPurchaser.dob,
      'P'
    )}.\n`;
  }
  if (difference.includes('streetAddress')) {
    string += `Street Address has been changed from ${purchaser.streetAddress} to ${updatedPurchaser.streetAddress}.\n`;
  }
  if (difference.includes('city')) {
    string += `City has been changed from ${purchaser.city} to ${updatedPurchaser.city}.\n`;
  }
  if (difference.includes('province')) {
    string += `Province has been changed from ${purchaser.province} to ${updatedPurchaser.province}.\n`;
  }
  if (difference.includes('postalCode')) {
    string += `Postal Code has been changed from ${purchaser.postalCode} to ${updatedPurchaser.postalCode}.\n`;
  }
  if (difference.includes('occupation')) {
    string += `Occupation has been changed from ${purchaser.occupation} to ${updatedPurchaser.occupation}.\n`;
  }
  if (difference.includes('employer')) {
    string += `Employer has been changed from ${purchaser.employer} to ${updatedPurchaser.employer}.\n`;
  }
  if (difference.includes('purchaserType')) {
    string += `PurchaserType has been changed from ${purchaser.purchaserType} to ${updatedPurchaser.purchaserType}.\n`;
  }
  if (difference.includes('primaryPhone')) {
    string += `Primary Phone has been changed from ${purchaser.primaryPhone} to ${updatedPurchaser.primaryPhone}.\n`;
  }
  if (difference.includes('idType')) {
    string += `ID Type has been changed from ${purchaser.idType} to ${updatedPurchaser.idType}.\n`;
  }
  if (difference.includes('idNumber')) {
    string += `ID Number has been changed from ${purchaser.idNumber} to ${updatedPurchaser.idNumber}.\n`;
  }
  if (difference.includes('idExpiry')) {
    string += `ID Expiry has been changed from ${purchaser.idExpiry} to ${updatedPurchaser.idExpiry}.\n`;
  }
  if (difference.includes('idJurisdiction')) {
    string += `ID Jurisdiction has been changed from ${purchaser.idJurisdiction} to ${updatedPurchaser.idJurisdiction}.\n`;
  }
  if (difference.includes('sin')) {
    string += `SIN has been changed from ${purchaser.sin} to ${updatedPurchaser.sin}.\n`;
  }

  if (!string) {
    string = 'No Change';
  }

  return string;
};

export const realtorContent = (updatedRealtor: IRealtorInfo, realtor: IRealtorInfo) => {
  let difference: any = Object.keys(updatedRealtor).filter(
    (k: any) => updatedRealtor[k as keyof typeof updatedRealtor] !== realtor[k as keyof typeof realtor]
  );
  let string = '';

  if (difference.includes('firstName')) {
    string += `First Name has been changed from ${realtor.firstName} to ${updatedRealtor.firstName}.\n`;
  }
  if (difference.includes('lastName')) {
    string += `Last Name has been changed from ${realtor.lastName} to ${updatedRealtor.lastName}.\n`;
  }
  if (difference.includes('email')) {
    string += `Email has been changed from ${realtor.email} to ${updatedRealtor.email}.\n`;
  }
  if (difference.includes('streetAddress')) {
    string += `Street Address has been changed from ${realtor.streetAddress} to ${updatedRealtor.streetAddress}.\n`;
  }
  if (difference.includes('city')) {
    string += `City has been changed from ${realtor.city} to ${updatedRealtor.city}.\n`;
  }
  if (difference.includes('province')) {
    string += `Province has been changed from ${realtor.province} to ${updatedRealtor.province}.\n`;
  }
  if (difference.includes('postalCode')) {
    string += `Postal Code has been changed from ${realtor.postalCode} to ${updatedRealtor.postalCode}.\n`;
  }
  if (difference.includes('brokeragePhone')) {
    string += `Brokerage Phone has been changed from ${realtor.brokeragePhone} to ${updatedRealtor.brokeragePhone}.\n`;
  }
  if (difference.includes('directPhone')) {
    string += `Direct Phone has been changed from ${realtor.directPhone} to ${updatedRealtor.directPhone}.\n`;
  }
  if (difference.includes('brokerageFax')) {
    string += `Brokerage Fax has been changed from ${realtor.brokerageFax} to ${updatedRealtor.brokerageFax}.\n`;
  }

  if (!string) {
    string = 'No Change';
  }

  return string;
};

export const depositContent = (updatedDeposit: IDealDeposit, deposit: IDealDeposit) => {
  let difference: any = Object.keys(updatedDeposit).filter(
    (k: any) => updatedDeposit[k as keyof typeof updatedDeposit] !== deposit[k as keyof typeof deposit]
  );
  let string = '';

  if (difference.includes('accountNumber')) {
    string += `Account Number has been changed from ${deposit.accountNumber} to ${updatedDeposit.accountNumber}.\n`;
  }
  if (difference.includes('chequeDate')) {
    let chequeDate: string = '';
    if (deposit.chequeDate) {
      chequeDate = `${convertDate(deposit.chequeDate)}`;
    } else {
      chequeDate = 'None';
    }
    string += `Cheque Date has been changed from ${chequeDate} to ${convertDate(updatedDeposit.chequeDate)}.\n`;
  }
  if (difference.includes('chequeType')) {
    let chequeType: string = '';
    if (deposit.chequeDate) {
      chequeType = `${deposit.chequeType}`;
    } else {
      chequeType = 'None';
    }
    string += `Cheque Type has been changed from ${chequeType} to ${updatedDeposit.chequeType}.\n`;
  }
  if (difference.includes('chequeNumber')) {
    string += `Cheque Number has been changed from ${deposit.chequeNumber} to ${updatedDeposit.chequeNumber}.\n`;
  }
  if (difference.includes('chequeAmount')) {
    string += `Cheque Amount has been changed from ${numToCurrency.format(deposit.chequeAmount!)} to ${numToCurrency.format(
      updatedDeposit.chequeAmount!
    )}.\n`;
  }

  return string;
};

export function numberToEnglish(n: number) {
  let number = n.toString();
  if (number.includes('.')) {
    let beginningNumber = number.split('.')[0];
    let endNumber = number.split('.')[1];

    let text: string = '';
    if (beginningNumber.length > 2) {
      text = `${numberToEnglish(parseInt(beginningNumber, 10))} AND ${numberToEnglish(parseInt(endNumber, 10))}`;
    } else {
      text = `${numberToEnglish(parseInt(beginningNumber, 10))} POINT ${numberToEnglish(parseInt(endNumber, 10))}`;
    }

    return text;
  } else {
    let string = n.toString(),
      units,
      tens,
      scales,
      start,
      end,
      chunks,
      chunksLen,
      chunk,
      ints,
      i,
      word,
      words;

    let and = 'AND';

    /* Is number zero? */
    if (string === '0.25') {
      return 'A QUARTER';
    }
    if (string === '0.5' || string === '0.50') {
      return 'A HALF';
    }
    if (parseInt(string) === 0) {
      return 'ZERO';
    }

    /* Array of units as words */
    units = [
      '',
      'ONE',
      'TWO',
      'THREE',
      'FOUR',
      'FIVE',
      'SIX',
      'SEVEN',
      'EIGHT',
      'NINE',
      'TEN',
      'ELEVEN',
      'TWELVE',
      'THIRTEEN',
      'FOURTEEN',
      'FIFTEEN',
      'SIXTEEN',
      'SEVENTEEN',
      'EIGHTEEN',
      'NINETEEN',
    ];

    /* Array of tens as words */
    tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

    /* Array of scales as words */
    scales = [
      '',
      'THOUSAND',
      'MILLION',
      'BILLION',
      'TRILLION',
      'QUADRILLION',
      'QUINTILLION',
      'SEXTILLION',
      'SEPTILLION',
      'OCTILLION',
      'NONILLION',
      'DECILLION',
      'UNDECILLION',
      'DUODECILLION',
      'TREDECILLION',
    ];

    /* Split user arguemnt into 3 digit chunks from right to left */
    start = string.length;
    chunks = [];
    while (start > 0) {
      end = start;
      chunks.push(string.slice((start = Math.max(0, start - 3)), end));
    }

    /* Check if function has enough scale words to be able to stringify the user argument */
    chunksLen = chunks.length;
    if (chunksLen > scales.length) {
      return '';
    }

    /* Stringify each integer in each chunk */
    words = [];
    for (i = 0; i < chunksLen; i++) {
      chunk = parseInt(chunks[i]);

      if (chunk) {
        /* Split chunk into array of individual integers */
        ints = chunks[i].split('').reverse().map(parseFloat);

        /* If tens integer is 1, i.e. 10, then add 10 to units integer */
        if (ints[1] === 1) {
          ints[0] += 10;
        }

        /* Add scale word if chunk is not zero and array item exists */
        if ((word = scales[i])) {
          words.push(word);
        }

        /* Add unit word if array item exists */
        if ((word = units[ints[0]])) {
          words.push(word);
        }

        /* Add tens word if array item exists */
        if ((word = tens[ints[1]])) {
          words.push(word);
        }

        /* Add 'and' string after units or tens integer if: */
        if (ints.length > 1) {
          if (ints[0] || ints[1]) {
            /* Chunk has a hundreds integer or chunk is the first of multiple chunks */
            if (ints[2] || (!i && chunksLen)) {
              words.push(and);
            }
          }
        }

        /* Add hundreds word if array item exists */
        if ((word = units[ints[2]])) {
          words.push(word + ' HUNDRED');
        }
      }
    }

    if (words[words.length - 1] === 'AND') {
      words.pop();
    }

    return words.reverse().join(' ');
  }
}

export function numberToCents(n: number) {
  const ones = ['', 'one ', 'two ', 'three ', 'four ', 'five ', 'six ', 'seven ', 'eight ', 'nine '];
  const teen = ['ten ', 'eleven ', 'twelve ', 'thirteen ', 'fourteen ', 'fifteen ', 'sixteen ', 'seventeen ', 'eighteen ', 'nineteen '];
  const tens = ['twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const high = ['hundred ', 'thousand ', 'million ', 'billion '];

  const tensOnes = (t: any, o: any) =>
    +t == 0 ? ones[+o] : +t == 1 ? teen[+o] : +t > 1 && +o == 0 ? tens[+t - 2] : tens[+t - 2] + ' ' + ones[+o];
  const fltN = (float: any) => [...parseFloat(float).toFixed(2)];

  const stepper = (array: any) => {
    const D = array[0];
    const C = array[1];
    let size = D.length;
    let word;
    switch (size) {
      case 0:
        word = C;
        break;
      case 1:
        word = tensOnes(0, D[0]) + 'dollars ' + C;
        break;
      case 2:
        word = tensOnes(D[1], D[0]) + 'dollars ' + C;
        break;
      case 3:
        word = tensOnes(0, D[2]) + high[0] + 'and ' + tensOnes(D[1], D[0]) + 'dollars ' + C;
        break;
      case 4:
        word = tensOnes(0, D[3]) + high[1] + tensOnes(0, D[2]) + high[0] + 'and ' + tensOnes(D[1], D[0]) + 'dollars ' + C;
        break;
      case 5:
        word = tensOnes(D[4], D[3]) + high[1] + tensOnes(0, D[2]) + high[0] + 'and ' + tensOnes(D[1], D[0]) + 'dollars ' + C;
        break;
      case 6:
        word =
          tensOnes(0, D[5]) +
          high[0] +
          tensOnes(D[4], D[3]) +
          high[1] +
          tensOnes(0, D[2]) +
          high[0] +
          'and ' +
          tensOnes(D[1], D[0]) +
          'dollars ' +
          C;
        break;
      case 7:
        word =
          tensOnes(0, D[6]) +
          high[2] +
          tensOnes(0, D[5]) +
          high[0] +
          tensOnes(D[4], D[3]) +
          high[1] +
          tensOnes(0, D[2]) +
          high[0] +
          'and ' +
          tensOnes(D[1], D[0]) +
          'dollars ' +
          C;
        break;
      case 8:
        word =
          tensOnes(D[7], D[6]) +
          high[2] +
          tensOnes(0, D[5]) +
          high[0] +
          tensOnes(D[4], D[3]) +
          high[1] +
          tensOnes(0, D[2]) +
          high[0] +
          'and ' +
          tensOnes(D[1], D[0]) +
          'dollars ' +
          C;
        break;
      case 9:
        word =
          tensOnes(0, D[8]) +
          high[0] +
          tensOnes(D[7], D[6]) +
          high[2] +
          tensOnes(0, D[5]) +
          high[0] +
          tensOnes(D[4], D[3]) +
          high[1] +
          tensOnes(0, D[2]) +
          high[0] +
          'and ' +
          tensOnes(D[1], D[0]) +
          'dollars ' +
          C;
        break;
      case 10:
        word =
          tensOnes(0, D[9]) +
          high[3] +
          tensOnes(0, D[8]) +
          high[0] +
          tensOnes(D[7], D[6]) +
          high[2] +
          tensOnes(0, D[5]) +
          high[0] +
          tensOnes(D[4], D[3]) +
          high[1] +
          tensOnes(0, D[2]) +
          high[0] +
          'and ' +
          tensOnes(D[1], D[0]) +
          'dollars ' +
          C;
        break;
      default:
        break;
    }
    word = word.trim();
    word =
      word == 'one dollars'
        ? 'one dollar'
        : word == 'dollars and one cent'
          ? 'one cent'
          : word == 'one dollars and one cent'
            ? 'one dollar and one cent'
            : word == 'and undefined-undefinedcents'
              ? ''
              : word;
    word = word
      .replace(/(thousand|million)\s(hundred)/g, '$1')
      .replace(/(million)\s(thousand)/g, '$1')
      .replace(/(tycents)/g, 'ty cents')
      .replace(/(tydollars)/g, 'ty dollars');
    return word;
  };

  let R = fltN(n);
  let dec, c, cents;
  dec = R.splice(-3, 3);
  c = tensOnes(dec[1], dec[2]);
  cents = c == 'one ' ? 'and one cent' : c == '' ? '' : `and ${c}cents`;
  return stepper([R.reverse(), cents]).toUpperCase();
}

export const optionsLeft = (deals: any, option: any, project?: IProject) => {
  const salesStatusCodes = getSalesStatusCodes(project);
  const validDeals = deals.filter(
    (deal: any) => !deal.draft && !deal.cancelled.dateCancelled && !deal.rescission.dateRescinded && salesStatusCodes.includes(deal.unit.status)
  );
  const validDealOptions = Array.prototype.concat.apply(
    [],
    validDeals.map((deal: any) => deal.options)
  );

  const spent = validDealOptions
    .filter((dealOption) => dealOption.name === option)
    .map((dealOption) => parseInt(dealOption.purchaseAmount))
    .reduce((a, b) => a + b, 0);

  return spent;
};

export const createPdf = async (
  merge: any,
  project: any,
  unit: any,
  purchasers: any,
  addPurchasers: any = [],
  deletePurchasers: any = [],
  dealDeposits: any,
  dealOptions: any,
  realtor: any = null,
  toUnit: any = null,
  options: any = null,
  deposits: any = null,
  deal: any = null,
  toPurchasers: any = null,
  fintrac: boolean = false,
  merges: any = [],
  colourSelectionChoices: any = [],
  removeFloorPlan: boolean = false,
  lease: ILease | null = null,
  assignment = null
) => {
  const mergedPdf = await PDFDocument.create();

  const url = merge?.getUrl!;

  let pdf = await fetch(url).then(async (res) => await res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(pdf);
  let helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

  let pages = pdfDoc.getPages();

  // Draw Merge Fields

  await merge?.mergeFields.forEach(async (mergeField: any) => {
    let mergePages = pages[mergeField.pageNumber];

    let mergeValue = getMergeValues(
      mergeField,
      project,
      unit,
      merge,
      purchasers,
      addPurchasers,
      deletePurchasers,
      dealDeposits,
      dealOptions,
      realtor,
      toUnit,
      options,
      deposits,
      null,
      deal,
      toPurchasers,
      colourSelectionChoices,
      lease,
      assignment
    );

    let checkMediaBox = mergePages.getMediaBox();
    let x = mergeField.x;
    let y = mergePages.getHeight() - mergeField.y - 3;

    if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
      x = mergeField.x - Math.abs(checkMediaBox.x);
      y = mergePages.getHeight() - mergeField.y - 3 - Math.abs(checkMediaBox.y);
    }

    let textProperties: any = {
      x: x,
      y: y,
      size: mergeField.fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0),
    };

    if (mergeField.wrap) {
      mergeValue = wrapText(mergeValue, mergeField.wrap * 2 + 75, helveticaFont, 9);
      if (mergeValue.lineHeight) {
        textProperties.lineHeight = mergeValue.lineHeight;
      }
      mergeValue = mergeValue.text;
    }

    mergePages.drawText(mergeValue, textProperties);
  });

  // Lease Details

  // Fintrac Details
  if (fintrac) {
    if (!purchasers[0].corp) {
      // B1 Fintrac
      pages[1].drawLine(drawCheckbox({ x: 68, y: 424 }, { x: 73, y: 429 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 65, y: 426 }, { x: 68, y: 424 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      // Draw CheckBoxes
      if (!checkHighRisk(purchasers[0].country)) {
        pages[2].drawLine(drawCheckbox({ x: 75, y: 584 }, { x: 80, y: 589 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
        pages[2].drawLine(drawCheckbox({ x: 72, y: 586 }, { x: 75, y: 584 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      } else {
        pages[2].drawLine(drawCheckbox({ x: 75, y: 243 }, { x: 80, y: 248 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
        pages[2].drawLine(drawCheckbox({ x: 72, y: 245 }, { x: 75, y: 243 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      }

      if (unit.type === 'residential') {
        // Page 3 Residential
        pages[3].drawLine(drawCheckbox({ x: 68, y: 613 }, { x: 73, y: 618 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
        pages[3].drawLine(drawCheckbox({ x: 65, y: 615 }, { x: 68, y: 613 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      } else if (unit.type === 'commercial') {
        // Page 3 Commercial
        pages[3].drawLine(drawCheckbox({ x: 68, y: 598 }, { x: 73, y: 603 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
        pages[3].drawLine(drawCheckbox({ x: 65, y: 600 }, { x: 68, y: 598 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      } else {
        // Page 3 Other
        pages[3].drawLine(drawCheckbox({ x: 68, y: 583 }, { x: 73, y: 588 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
        pages[3].drawLine(drawCheckbox({ x: 65, y: 585 }, { x: 68, y: 583 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      }
    } else {
      pages[1].drawLine(drawCheckbox({ x: 68, y: 399 }, { x: 73, y: 404 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 65, y: 401 }, { x: 68, y: 399 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 68, y: 369 }, { x: 73, y: 374 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 65, y: 371 }, { x: 68, y: 369 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 68, y: 312 }, { x: 73, y: 317 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 65, y: 314 }, { x: 68, y: 312 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 68, y: 272 }, { x: 73, y: 277 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[1].drawLine(drawCheckbox({ x: 65, y: 274 }, { x: 68, y: 272 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[2].drawLine(drawCheckbox({ x: 75, y: 598 }, { x: 80, y: 603 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
      pages[2].drawLine(drawCheckbox({ x: 72, y: 600 }, { x: 75, y: 598 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
    }
  }

  let copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
  copiedPages.forEach((page) => mergedPdf.addPage(page));

  if (toUnit) {
    if (!removeFloorPlan) {
      if (!toUnit.floorPlan) {
        return;
      }

      let floorPlan = await fetch(toUnit.getUrl!).then(async (res) => await res.arrayBuffer());
      const floorPlanDoc = await PDFDocument.load(floorPlan);
      helveticaFont = await floorPlanDoc.embedFont(StandardFonts.Helvetica);

      let floorPlanPages = floorPlanDoc.getPages();
      toUnit.floorPlan.mergeFields.forEach(async (mergeField: any) => {
        let mergePages = floorPlanPages[0];

        let pageType = mergePages.getRotation();

        let pageRotationValue = pageRotate(pageType, mergeField.x, mergeField.y, 1, mergePages.getSize());

        let pageRotation = mergePages.getRotation();
        if (pageRotation.angle === 0) {
          pageRotationValue.y = mergePages.getHeight() - mergeField.y - 3;
        }

        let checkMediaBox = mergePages.getMediaBox();
        let x = pageRotationValue.x;
        let y = pageRotationValue.y;

        if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
          x = pageRotationValue.x - Math.abs(checkMediaBox.x);
          y = pageRotationValue.y - Math.abs(checkMediaBox.y);
        }

        let mergeValue = getMergeValues(
          mergeField,
          project,
          toUnit,
          merge,
          purchasers,
          [],
          [],
          dealDeposits,
          dealOptions,
          realtor,
          toUnit,
          options,
          deposits,
          null,
          deal,
          toPurchasers
        );

        mergePages.drawText(mergeValue!, {
          x: x,
          y: y,
          size: mergeField.fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          rotate: mergePages.getRotation(),
        });
      });

      copiedPages = await mergedPdf.copyPages(floorPlanDoc, floorPlanDoc.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }
  }

  if (merges.length > 0) {
    let arrayOfPdfs: Array<any> = [];
    for (let file of merges) {
      let templateUrl = await fetch(file!.getUrl!).then(async (res) => await res.arrayBuffer());
      arrayOfPdfs.push(templateUrl);
    }

    for (let [index, document] of arrayOfPdfs.entries()) {
      document = await PDFDocument.load(document);
      let pages = document.getPages();
      const helveticaFont = await document.embedFont(StandardFonts.Helvetica);

      await merges[index].mergeFields.forEach(async (mergeField: any) => {
        let mergePages = pages[mergeField.pageNumber];

        let mergeValue = getMergeValues(
          mergeField,
          project,
          unit && toUnit && unit.suite === toUnit.suite ? toUnit : unit,
          merge,
          purchasers,
          [],
          [],
          dealDeposits,
          dealOptions,
          realtor,
          toUnit,
          options,
          deposits,
          null,
          deal,
          toPurchasers
        );

        mergePages.drawText(mergeValue!, {
          x: mergeField.x,
          y: mergePages.getHeight() - mergeField.y - 3,
          size: mergeField.fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
      });

      let copiedPages = await mergedPdf.copyPages(document, document.getPageIndices());
      copiedPages.forEach((page: any) => mergedPdf.addPage(page));
    }
  }

  const pdfBytes: any = await mergedPdf.save();

  const blob = new Blob([pdfBytes]);
  const file = new File([blob], `${merge.name}.pdf`, { type: 'application/pdf' });

  return file;
};

export function download(file: any, filename: string, type: string) {
  const element = document.createElement('a');
  element.download = filename;
  let binaryData = [];
  binaryData.push(file);
  element.href = URL.createObjectURL(new Blob(binaryData, { type: type }));
  element.click();
}

// Deep links that hand text off to WhatsApp or the user's own mail client - they can't attach a
// file, only reference a URL to it in the message. The WhatsApp one uses the whatsapp:// scheme
// rather than https://wa.me so the app opens directly instead of going through a browser page
// first; it needs the consumer WhatsApp app installed (a blank phone opens WhatsApp's own contact
// picker). Pass `universal` to get the https://wa.me link instead, for a real tap on iOS: it opens
// WhatsApp or WhatsApp Business directly if either is installed, and falls back to WhatsApp's web
// page if neither is, where whatsapp:// would just fail with "address is invalid".
export const buildWhatsAppShareUrl = (phone: string, message: string, universal: boolean = false) => {
  const digitsOnly = phone.replace(/\D/g, '');
  if (universal) {
    return `https://wa.me/${digitsOnly}?text=${encodeURIComponent(message)}`;
  }
  return `whatsapp://send?${digitsOnly ? `phone=${digitsOnly}&` : ''}text=${encodeURIComponent(message)}`;
};

// iPadOS 13+ reports itself as a Mac, so it's told apart by being a touch device.
export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const buildEmailShareUrl = (email: string, subject: string, body: string, bcc?: string) => {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}${bcc ? `&bcc=${encodeURIComponent(bcc)}` : ''}`;
};

export const combinePdfs = async (files: any[]) => {
  const mergedPdf = await PDFDocument.create();
  await Promise.all(
    await files.map(async (url: any) => {
      let pdfDoc: any = url;
      if (pdfDoc.name) {
        pdfDoc = await readFileAsync(url);
      }
      const fintracDoc = await PDFDocument.load(pdfDoc);
      let copiedPages = await mergedPdf.copyPages(fintracDoc, fintracDoc.getPageIndices());
      for (const page of copiedPages) {
        await mergedPdf.addPage(page);
      }
    })
  );
  const mergedPdfFile = await mergedPdf.save();
  return mergedPdfFile;
};

const readFileAsync = (file: any) => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Maps a fetched blob's Content-Type to a file extension for the distribution zip. Falls back to
// pdf rather than dropping the file - a strict match against exact MIME strings (the old
// behaviour) silently skipped any file whose blob.type didn't match one of the three known
// strings verbatim (e.g. "application/pdf;charset=binary", "application/octet-stream", or an
// empty type from a missing Content-Type header), which is why PDF identifications in particular
// were vanishing from the download while images went through fine.
const getDistributionFileExtension = (mimeType: string) => {
  if (mimeType?.includes('jpeg') || mimeType?.includes('jpg')) return 'jpeg';
  if (mimeType?.includes('png')) return 'png';
  return 'pdf';
};

// What a file really is, from its first bytes: 'pdf', 'png' or 'jpeg', or null for anything else (including an error
// page saved in place of the file). Purchaser IDs are only accepted as one of these.
export const sniffFileType = async (blob: Blob): Promise<'pdf' | 'png' | 'jpeg' | null> => {
  try {
    const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
    if (head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46) return 'pdf';
    if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return 'png';
    if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'jpeg';
  } catch (err) {
    console.log(err, 'err');
  }
  return null;
};

// Uploads are often stored with a generic content type (binary/octet-stream), so the reported type can't be trusted
// for the file extension - a JPG ID would be zipped as ".pdf" and fail to open. Work it out from the file's own
// first bytes, then the uploaded file name, then the reported type.
const detectDistributionFileExtension = async (blob: Blob, fileName?: string) => {
  try {
    const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
    const text = String.fromCharCode(...Array.from(head));
    if (text.startsWith('%PDF')) return 'pdf';
    if (head[0] === 0x89 && text.slice(1, 4) === 'PNG') return 'png';
    if (head[0] === 0xff && head[1] === 0xd8) return 'jpeg';
    if (text.startsWith('GIF8')) return 'gif';
    if (text.startsWith('RIFF') && text.slice(8, 12) === 'WEBP') return 'webp';
    if (text.slice(4, 8) === 'ftyp') return /ftyp(heic|heix|hevc|mif1|msf1)/.test(text) ? 'heic' : 'mp4';
  } catch (err) {
    console.log(err, 'err');
  }
  const fromName = fileName?.match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toLowerCase();
  if (fromName) return fromName === 'jpg' ? 'jpeg' : fromName;
  return getDistributionFileExtension(blob.type);
};

export const downloadDistributionFiles = async (files: any, project: string, setLoading: any = null) => {
  const zip = new JSZip();

  //fetches the files and saves the files as blobs in an array

  const addFilesToZip: any = async (fileBlobs: any) => {
    let suiteZip = zip.folder(`${fileBlobs.suite}`);
    for (const deposit of fileBlobs.depositImages) {
      const extension = await detectDistributionFileExtension(deposit.pdf, deposit.fileName);
      suiteZip?.file(`${fileBlobs.suite} - ${deposit.name}.${extension}`, deposit.pdf);
    }

    if (fileBlobs.documents.length) {
      await fileBlobs.documents.forEach((document: any) => {
        if (document.pdf) {
          let blob = new Blob([document.pdf]);
          suiteZip?.file(`${fileBlobs.suite} - ${document.name}.pdf`, blob);
        }
      });
    }

    // Liberty projects: miscellaneous uploads, named without their own extension so the detected one isn't doubled up
    for (const miscellaneous of fileBlobs.miscellaneous || []) {
      if (!miscellaneous || !miscellaneous.pdf) continue;
      const extension = await detectDistributionFileExtension(miscellaneous.pdf, miscellaneous.name);
      suiteZip?.file(`${fileBlobs.suite} - ${`${miscellaneous.name}`.replace(/\.[a-z0-9]{2,4}$/i, '')}.${extension}`, miscellaneous.pdf);
    }

    for (const purchaser of fileBlobs.purchasers) {
      if (!purchaser || !purchaser.pdf) continue;
      const extension = await detectDistributionFileExtension(purchaser.pdf, purchaser.fileName);
      suiteZip?.file(`${fileBlobs.suite} - ${purchaser.name}.${extension}`, purchaser.pdf);
    }

    if (fileBlobs.mortgage && fileBlobs.mortgage.pdf) {
      const extension = await detectDistributionFileExtension(fileBlobs.mortgage.pdf);
      suiteZip?.file(`${fileBlobs.suite} - Mortgage.${extension}`, fileBlobs.mortgage.pdf);
    }
  };

  //downloads the zip
  const downloadZip = (zip: JSZip) => {
    // Returns the generateAsync/saveAs chain so the caller's `await` actually waits for the save
    // to finish (and a failure here - eg. JSZip choking on a bad blob - rejects up to the caller
    // instead of becoming an unhandled rejection while the rest of the app assumes it succeeded).
    return zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${project}.zip`);
    });
  };

  for (let file of files) {
    await addFilesToZip(file);
  }

  await downloadZip(zip);
};

export const downloadDocuments = async (files: any, project: string, setLoading: any = null) => {
  const zip = new JSZip();

  //fetches the files and saves the files as blobs in an array

  const addFilesToZip: any = async (fileBlobs: any) => {
    await fileBlobs.forEach((fileBlob: any, index: number) => {
      let blob = new Blob([fileBlob.pdf]);
      zip.file(`${fileBlob.suite}.pdf`, blob);
    });
  };
  //downloads the zip
  const downloadZip = (zip: JSZip) => {
    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${project}.zip`);
    });
    if (setLoading) {
      setLoading(false);
    }
  };

  await addFilesToZip(files);
  await downloadZip(zip);
};

export const netHST = (price: number, project?: IProject | null) => {
  if (project?.netHst) {
    return (price) / 1.13;
  } else if (price > 484500) {
    if (project?._id === '61f9934d3ace1c59fcb6c5de' || project?._id === '61d506dbaa581c623018e244' || project?._id === '63ed601560dfae53efa92b99') {
      return Math.round((price + 24000) / 1.13);
    } else {
      return (price + 24000) / 1.13;
    }
  } else if (price > 424850 && price < 484500) {
    if (project?._id === '61f9934d3ace1c59fcb6c5de' || project?._id === '61d506dbaa581c623018e244' || project?._id === '63ed601560dfae53efa92b99') {
      return Math.round((price + 52350) / 1.193);
    } else {
      return (price + 52350) / 1.193;
    }
  } else if (price > 368200 && price < 424850) {
    return (price + 28350) / 1.133;
  } else return price / 1.052;
};

// Inverse of netHST - solves each branch's formula for `price` given the net HST amount, then
// checks the result actually falls in that branch's gross-price domain (the same thresholds
// netHST branches on), since which branch applies depends on the gross price we don't have yet.
export const grossPriceFromNetHST = (netPrice: number) => {
  const fromAbove484500 = netPrice * 1.13 - 24000;
  if (fromAbove484500 > 484500) {
    return fromAbove484500;
  }

  const from424850To484500 = netPrice * 1.193 - 52350;
  if (from424850To484500 > 424850 && from424850To484500 < 484500) {
    return from424850To484500;
  }

  const from368200To424850 = netPrice * 1.133 - 28350;
  if (from368200To424850 > 368200 && from368200To424850 < 424850) {
    return from368200To424850;
  }

  return netPrice * 1.052;
};

export const netHSTnoRebate = (price: number) => {
  return price / 1.13;
};

export const hstRebate = (price: number, project?: IProject | null) => {
  if (project?.netHst) {
    return price - netHST(price, project);
  } else if (
    project?._id === '63ed601560dfae53efa92b99' ||
    project?._id === '61535b00cb68b4620011d555' ||
    project?._id === '61d506dbaa581c623018e244' ||
    project?._id === '61f9934d3ace1c59fcb6c5de'
  ) {
    let netHstAmount = netHSTnoRebate(price);
    if (netHstAmount < 1000000) {
      return netHstAmount * 0.13 - (netHST(price, project) - netHSTnoRebate(price));
    } else if (netHstAmount > 1000000 && netHstAmount < 1500000) {
      return 130000 - (netHST(price, project) - netHSTnoRebate(price));
    } else if (netHstAmount > 1500000 && netHstAmount < 1850000) {
      return -0.302857 * netHstAmount + 584286 - (netHST(price, project) - netHSTnoRebate(price));
    } else return 24000 - (netHST(price, project) - netHSTnoRebate(price));
  } else {
    let netHstAmount = netHST(price, project);
    if (netHstAmount < 1000000) {
      return netHstAmount * 0.13;
    } else if (netHstAmount > 1000000 && netHstAmount < 1500000) {
      return 130000;
    } else if (netHstAmount > 1500000 && netHstAmount < 1850000) {
      return -0.302857 * netHstAmount + 584286;
    } else return 24000;
  }
};

export const effectivePriceAfterRebate = (price: number, project?: IProject | null) => {
  if (project?.netHst) {
    return netHST(price, project);
  } else if (
    project?._id === '63ed601560dfae53efa92b99' ||
    project?._id === '61535b00cb68b4620011d555' ||
    project?._id === '61d506dbaa581c623018e244' ||
    project?._id === '61f9934d3ace1c59fcb6c5de'
  ) {
    let newHstRebate = hstRebate(price, project);
    return price - newHstRebate;
  } else {
    let netHstAmount = netHST(price, project);
    let rebate = hstRebate(price, project);
    return netHstAmount * 1.13 - rebate;
  }
};

export const pageRotate = (pageRotation: any, x: any, y: any, scale: any, dimensions: any) => {
  let rotationRads = (pageRotation.angle * Math.PI) / 180;
  //These coords are now from bottom/left
  let coordsFromBottomLeft: any = {
    x: x / scale,
  };
  if (pageRotation.angle === 90 || pageRotation.angle === 270) {
    coordsFromBottomLeft.y = dimensions.width - y / scale;
  } else {
    coordsFromBottomLeft.y = dimensions.height - y / scale;
  }

  var drawX = null;
  var drawY = null;
  if (pageRotation.angle === 90) {
    drawX = coordsFromBottomLeft.x * Math.cos(rotationRads) - coordsFromBottomLeft.y * Math.sin(rotationRads) + dimensions.width;
    drawY = coordsFromBottomLeft.x * Math.sin(rotationRads) + coordsFromBottomLeft.y * Math.cos(rotationRads);
  } else if (pageRotation.angle === 180) {
    drawX = coordsFromBottomLeft.x * Math.cos(rotationRads) - coordsFromBottomLeft.y * Math.sin(rotationRads) + dimensions.width;
    drawY = coordsFromBottomLeft.x * Math.sin(rotationRads) + coordsFromBottomLeft.y * Math.cos(rotationRads) + dimensions.height;
  } else if (pageRotation.angle === 270) {
    drawX = coordsFromBottomLeft.x * Math.cos(rotationRads) - coordsFromBottomLeft.y * Math.sin(rotationRads);
    drawY = coordsFromBottomLeft.x * Math.sin(rotationRads) + coordsFromBottomLeft.y * Math.cos(rotationRads) + dimensions.height;
  } else {
    //no rotation
    drawX = coordsFromBottomLeft.x;
    drawY = coordsFromBottomLeft.y;
  }

  return { x: drawX, y: drawY };
};

export function typedArrayToBuffer(array: any): ArrayBuffer {
  return array.buffer.slice(array.byteOffset, array.byteLength + array.byteOffset);
}

export const downloadExcel = (
  data: any[],
  headers: any[],
  backgrounds: any[],
  columnWidths: any[],
  sheetName: string[],
  fileName: string
) => {
  const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const workbook = new ExcelJS.Workbook(); // Create Workbook

  data.forEach((values, index) => {
    const worksheet = workbook.addWorksheet(sheetName[index]); // Create Worksheet

    let headerIds = headers[index].map((header: any) => header.id);
    let headerLabels = headers[index].map((header: any) => header.label);

    worksheet.columns = headerIds.map((header: any, index: number) => ({
      header: headerLabels[index],
      key: header,
      width: columnWidths[0][0][header],
    })); //

    const thickBorder = {
      top: { style: 'thick' as const },
      left: { style: 'thick' as const },
      bottom: { style: 'thick' as const },
      right: { style: 'thick' as const },
    };

    // Headers
    const headerRow = worksheet.getRow(1);
    headerIds.forEach((header: any) => {
      headerRow.getCell(header).fill = {
        // Add the background color if it is defined
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '2e453d' },
      };
      headerRow.getCell(header).font = {
        color: { argb: 'ffffff' },
      };
      // Applied per-cell (not via column.border) so the border stops at the last data row
      // instead of a fixed column style bleeding down the rest of the sheet in Excel.
      headerRow.getCell(header).border = thickBorder;
    });

    values.forEach((value: any, index: any) => {
      const row = worksheet.getRow(index + 2);
      row.values = value;
      headerIds.forEach((header: any) => {
        row.getCell(header).fill = {
          // Add the background color if it is defined
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ffffff' },
        };
        row.getCell(header).alignment = { wrapText: true };
        row.getCell(header).border = thickBorder;
      });
      row.height = 20;
      row.commit();
    });
  });

  workbook.xlsx.writeBuffer().then((file: any) => {
    const blob = new Blob([file], { type: EXCEL_TYPE });
    saveAs(blob, fileName);
  });
};

// Columns for the Counts tab's two tables - one row per unit, Count meaning either "times shared
// today" or "times shared all-time" depending on the table. Mirrors COUNTS_COLUMNS in the API's
// cron job (api/src/cron/sharedUnits.ts) exactly.
const UNITS_SHARED_COUNTS_COLUMNS = [
  { header: 'Suite', key: 'suite' },
  { header: 'Count', key: 'count' },
  { header: 'Unit Type', key: 'unitType' },
  { header: 'Model Type', key: 'modelType' },
  { header: 'Exposure', key: 'exposure' },
  { header: 'Bathrooms', key: 'bathroom' },
  { header: 'Ceiling Height', key: 'ceilingHeight' },
  { header: 'Size', key: 'size' },
  { header: 'Price', key: 'price' },
];

// Adds a 2nd "Counts" tab with two stacked tables: one ranking units shared today by how many
// times each was shared today, the other ranking EVERY unit that appears anywhere in the whole
// fetched "Units Shared" history for this project (the closest client-side analog of the cron
// job's unbounded all-time count) by their all-time share count. Unlike the cron job - whose own
// dataset is already scoped to a single day, so its two tables happen to share the same unit set -
// this button's dataset spans the project's full history, so the Total table intentionally is NOT
// restricted to units also shared today: it must count every activity that shared a unit, not just
// today's overlap. Mirrors addCountsWorksheet in the API's cron job otherwise (columns, labels,
// table styling). Price applies the same libertyProjects-keyed net-of-HST rule the cron uses for
// this tab specifically, rather than the project's own netHst toggle (used by the main tab).
const addUnitsSharedCountsWorksheet = (workbook: ExcelJS.Workbook, rows: any[], project: IProject) => {
  const today = new Date().toDateString();
  const byUnitId = new Map<string, { detail: any; dailyCount: number; totalCount: number }>();

  rows.forEach((row) => {
    const isToday = new Date(row.timestamp).toDateString() === today;
    const existing = byUnitId.get(row.unitId);
    if (existing) {
      existing.totalCount += 1;
      if (isToday) existing.dailyCount += 1;
    } else {
      byUnitId.set(row.unitId, { detail: row, dailyCount: isToday ? 1 : 0, totalCount: 1 });
    }
  });

  if (!byUnitId.size) return;

  const countsPrice = (rawBasePrice: number) =>
    numToCurrency.format(
      libertyProjects.includes(project._id) ? netHST(rawBasePrice, { netHst: true } as IProject) : rawBasePrice
    );

  const toCountRows = (summaries: { detail: any; dailyCount: number; totalCount: number }[], countKey: 'dailyCount' | 'totalCount') =>
    [...summaries]
      .sort((a, b) => b[countKey] - a[countKey] || a.detail.suite.localeCompare(b.detail.suite, 'en', { numeric: true }))
      .map((summary) => ({
        suite: summary.detail.suite,
        count: summary[countKey],
        unitType: summary.detail.unitType,
        modelType: summary.detail.modelType,
        exposure: summary.detail.exposure,
        bathroom: summary.detail.bathroom,
        ceilingHeight: summary.detail.ceilingHeight,
        size: summary.detail.size,
        price: countsPrice(summary.detail.rawBasePrice),
      }));

  const allSummaries = [...byUnitId.values()];
  const dailyRows = toCountRows(
    allSummaries.filter((summary) => summary.dailyCount > 0),
    'dailyCount'
  );
  const totalRows = toCountRows(allSummaries, 'totalCount');

  const worksheet = workbook.addWorksheet('Counts');

  const addCountsTable = (name: string, ref: string, tableRows: any[]) => {
    worksheet.addTable({
      name,
      ref,
      headerRow: true,
      style: { showRowStripes: true },
      columns: UNITS_SHARED_COUNTS_COLUMNS.map((column) => ({ name: column.header })),
      rows: tableRows.map((row) => UNITS_SHARED_COUNTS_COLUMNS.map((column) => row[column.key])),
    });
  };

  let cursor = 1;
  worksheet.getCell(`A${cursor}`).value = 'Daily Cumulative Count (shared today)';
  worksheet.getCell(`A${cursor}`).font = { bold: true };
  cursor += 1;
  addCountsTable('Counts_Daily', `A${cursor}`, dailyRows);

  cursor += 1 /* header row */ + dailyRows.length + 1 /* blank spacer row */;
  worksheet.getCell(`A${cursor}`).value = 'Total Cumulative Count (all time)';
  worksheet.getCell(`A${cursor}`).font = { bold: true };
  cursor += 1;
  addCountsTable('Counts_Total', `A${cursor}`, totalRows);
};

// Same layout/styling as downloadExcel's single-sheet case, plus a 2nd "Counts" tab (see
// addUnitsSharedCountsWorksheet) matching the one the API's nightly Units Shared cron job emails
// out. `rows` must include the extra unitId/rawBasePrice/timestamp fields the Counts tab needs,
// alongside whatever fields `headers` displays on the main tab.
export const downloadUnitsSharedExcel = (
  rows: any[],
  headers: { label: string; id: string }[],
  widths: { [key: string]: number },
  project: IProject
) => {
  const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet('Units Shared');
  const headerIds = headers.map((header) => header.id);
  const headerLabels = headers.map((header) => header.label);

  worksheet.columns = headerIds.map((header, index) => ({
    header: headerLabels[index],
    key: header,
    width: widths[header],
  }));

  const thickBorder = {
    top: { style: 'thick' as const },
    left: { style: 'thick' as const },
    bottom: { style: 'thick' as const },
    right: { style: 'thick' as const },
  };

  const headerRow = worksheet.getRow(1);
  headerIds.forEach((header) => {
    headerRow.getCell(header).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2e453d' } };
    headerRow.getCell(header).font = { color: { argb: 'ffffff' } };
    headerRow.getCell(header).border = thickBorder;
  });

  rows.forEach((row, index) => {
    const excelRow = worksheet.getRow(index + 2);
    excelRow.values = row;
    headerIds.forEach((header) => {
      excelRow.getCell(header).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ffffff' } };
      excelRow.getCell(header).alignment = { wrapText: true };
      excelRow.getCell(header).border = thickBorder;
    });
    excelRow.height = 20;
    excelRow.commit();
  });

  addUnitsSharedCountsWorksheet(workbook, rows, project);

  workbook.xlsx.writeBuffer().then((file: any) => {
    const blob = new Blob([file], { type: EXCEL_TYPE });
    saveAs(blob, 'Units Shared.xlsx');
  });
};

export const downloadDecorExcel = (data: any[], headers: any[], fileName: string) => {
  const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const workbook = new ExcelJS.Workbook(); // Create Workbook

  for (const [index, header] of headers.entries()) {
    if (header.length) {
      let upgradeType;
      if (index === 0) {
        upgradeType = 'Upgrade Set One';
      }
      if (index === 1) {
        upgradeType = 'Upgrade Set Two';
      }
      if (index === 2) {
        upgradeType = 'Upgrade Set Three';
      }
      const worksheet: any = workbook.addWorksheet(upgradeType);
      worksheet.addRow(['', '', '', '', ...header.map((header: any) => header.mainCategory)]);
      let excelMainHeaderLetter = 5;
      let excelSubHeaderLetter = 5;

      let mainCategories: any = Object.entries(
        header.reduce((acc: any, o: any) => ((acc[o.mainCategory] = (acc[o.mainCategory] || 0) + 1), acc), {})
      );
      for (const [index, data] of mainCategories.entries()) {
        worksheet.mergeCells(`${columnToLetter(excelMainHeaderLetter)}1:${columnToLetter(excelMainHeaderLetter + data[1] - 1)}1`);
        let cell = worksheet.getCell(`${columnToLetter(excelMainHeaderLetter)}1`);
        cell.value = {
          richText: [{ font: { bold: true }, text: `${data[0]}` }],
        };
        cell.alignment = {
          horizontal: 'center',
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {
            argb: randomColors[index].substring(1),
          },
        };
        excelMainHeaderLetter += data[1];
      }
      let subCategoryHeaders = [...new Set(data.map((item) => item.group))];
      worksheet.getRow(2).values = ['', '', '', ...subCategoryHeaders];
      let subCategories: any = {};
      for (let { mainCategory, subCategory } of header)
        subCategories[`${mainCategory}${subCategory}`] = {
          mainCategory,
          subCategory,
          count: subCategories[`${mainCategory}${subCategory}`] ? subCategories[`${mainCategory}${subCategory}`].count + 1 : 1,
        };

      let result: any = Object.values(subCategories);
      for (const data of result) {
        worksheet.mergeCells(`${columnToLetter(excelSubHeaderLetter)}2:${columnToLetter(excelSubHeaderLetter + data.count - 1)}2`);
        let cell = worksheet.getCell(`${columnToLetter(excelSubHeaderLetter)}2`);
        cell.value = {
          richText: [{ font: { bold: true }, text: `${data.subCategory}` }],
        };
        cell.alignment = {
          horizontal: 'center',
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {
            argb: 'dfdfdf',
          },
        };
        cell.border = {
          left: { style: 'thin' },
          right: { style: 'thin' },
          bottom: { style: 'thin' },
          top: { style: 'thin' },
        };
        excelSubHeaderLetter += data.count;
      }
      let upgradeRow = worksheet.getRow(3);
      let values = ['Suite', 'Date', 'Sales Rep', 'Total', ...header.map((header: any) => header.name)];
      upgradeRow.values = values;
      values.forEach((header: any, index) => {
        upgradeRow.getCell(index + 1).alignment = {
          horizontal: 'center',
        };
        upgradeRow.getCell(index + 1).fill = {
          // Add the background color if it is defined
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '2e453d' },
        };
        upgradeRow.getCell(index + 1).font = {
          color: { argb: 'ffffff' },
        };
      });
      if (index === 0) {
        data.forEach((value: any, index: any) => {
          const row = worksheet.getRow(index + 4);
          row.getCell(1).value = value.suite;
          row.getCell(2).value = value.upgradeSetOne.upgrades.length ? convertAllDates(new Date(value.upgradeSetOne.date), 'PPpp') : '-';
          row.getCell(3).value = value.upgradeSetOne.upgrades.length ? value.upgradeSetOne.salesRep : '-';
          row.getCell(4).value = value.upgradeSetOne.upgrades.length ? numToCurrency.format(value.upgradeSetOne.totalPrice) : '-';
          header.forEach((header: any, numIndex: number) => {
            let mainCategory: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}1`).value.richText[0].text;
            let subCategory: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}2`).value.richText[0].text;
            let name: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}3`).value;
            let upgrade = value.upgradeSetOne.upgrades.find(
              (upgrade: IUpgrade) => upgrade.mainCategory === mainCategory && upgrade.subCategory === subCategory && upgrade.name === name
            );
            if (upgrade) {
              row.getCell(numIndex + 5).value = `${numToCurrency.format(upgrade.price)} (${upgrade.quantity})`;
            } else {
              row.getCell(numIndex + 5).value = '-';
            }
          });
          row.height = 20;
          row.commit();
        });
        worksheet.getCell(`D1`).value = numToCurrency.format(
          data.filter((data: any) => data.upgradeSetOne.totalPrice !== '-').reduce((a: any, b: any) => a + b.upgradeSetOne.totalPrice, 0)
        );
      }
      if (index === 1) {
        data.forEach((value: any, index: any) => {
          const row = worksheet.getRow(index + 4);
          row.getCell(1).value = value.suite;
          row.getCell(2).value = value.upgradeSetTwo.upgrades.length ? convertAllDates(new Date(value.upgradeSetTwo.date), 'PPpp') : '-';
          row.getCell(3).value = value.upgradeSetTwo.upgrades.length ? value.upgradeSetTwo.salesRep : '-';
          row.getCell(4).value = value.upgradeSetTwo.upgrades.length ? numToCurrency.format(value.upgradeSetTwo.totalPrice) : '-';
          header.forEach((header: any, numIndex: number) => {
            let mainCategory: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}1`).value.richText[0].text;
            let subCategory: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}2`).value.richText[0].text;
            let name: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}3`).value;
            let upgrade = value.upgradeSetTwo.upgrades.find(
              (upgrade: IUpgrade) => upgrade.mainCategory === mainCategory && upgrade.subCategory === subCategory && upgrade.name === name
            );
            if (upgrade) {
              row.getCell(numIndex + 5).value = `${numToCurrency.format(upgrade.price)} (${upgrade.quantity})`;
            } else {
              row.getCell(numIndex + 5).value = '-';
            }
          });
          row.height = 20;
          row.commit();
        });
        worksheet.getCell(`D1`).value = numToCurrency.format(
          data.filter((data: any) => data.upgradeSetTwo.totalPrice !== '-').reduce((a: any, b: any) => a + b.upgradeSetTwo.totalPrice, 0)
        );
      }
      if (index === 2) {
        data.forEach((value: any, index: any) => {
          const row = worksheet.getRow(index + 4);
          row.getCell(1).value = value.suite;
          row.getCell(2).value = value.upgradeSetThree.upgrades.length
            ? convertAllDates(new Date(value.upgradeSetThree.date), 'PPpp')
            : '-';
          row.getCell(3).value = value.upgradeSetThree.upgrades.length ? value.upgradeSetThree.salesRep : '-';
          row.getCell(4).value = value.upgradeSetThree.upgrades.length ? numToCurrency.format(value.upgradeSetThree.totalPrice) : '-';
          header.forEach((header: any, numIndex: number) => {
            let mainCategory: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}1`).value.richText[0].text;
            let subCategory: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}2`).value.richText[0].text;
            let name: any = worksheet.getCell(`${columnToLetter(numIndex + 5)}3`).value;
            let upgrade = value.upgradeSetThree.upgrades.find(
              (upgrade: IUpgrade) => upgrade.mainCategory === mainCategory && upgrade.subCategory === subCategory && upgrade.name === name
            );
            if (upgrade) {
              row.getCell(numIndex + 5).value = `${numToCurrency.format(upgrade.price)} (${upgrade.quantity})`;
            } else {
              row.getCell(numIndex + 5).value = '-';
            }
          });
          row.height = 20;
          row.commit();
        });
        worksheet.getCell(`D1`).value = numToCurrency.format(
          data
            .filter((data: any) => data.upgradeSetThree.totalPrice !== '-')
            .reduce((a: any, b: any) => a + b.upgradeSetThree.totalPrice, 0)
        );
      }

      excelAutoWidth(worksheet);
    }
  }

  workbook.xlsx.writeBuffer().then((file: any) => {
    const blob = new Blob([file], { type: EXCEL_TYPE });
    saveAs(blob, fileName);
  });
};

const hexToPdfRgb = (hex: string | undefined) => {
  if (!hex || hex.length !== 7) return rgb(1, 1, 1);
  return rgb(parseInt(hex.substring(1, 3), 16) / 255, parseInt(hex.substring(3, 5), 16) / 255, parseInt(hex.substring(5, 7), 16) / 255);
};

/**
 * Downloads an excel spreadsheet from data
 * @param data: an array of objects
 * @param headers: an array of the keys of the object
 * @param formattedHeaders: an array of the formatted keys of the object
 * @param backgrounds: color codes for the backgrounds of each cell
 * @param columnWidths: widths of each column
 * @param fileName: name of the downloaded file
 *
 */

export const downloadPdf = async (
  data: any[],
  headers: any[],
  backgrounds: any[],
  columnWidths: any,
  pdfTitles: string[],
  fileName: string,
  projectLogo: any = null,
  downloadPdf: boolean = true
) => {
  if (!data.length) return;
  // Constants
  const padding = 20;
  const fontSize = 12;
  const realtorFontSize = 100;
  const textOffset = 5;
  const cellPadding = 5;
  const rowHeight = cellPadding * 2 + fontSize * 3 + textOffset;
  const headerHeight = cellPadding * 2 + fontSize + textOffset;

  // Get text width
  const context = document.createElement('canvas').getContext('2d');
  context && (context.font = fontSize + 'px sans-serif');
  const realtorContext = document.createElement('canvas').getContext('2d');
  realtorContext && (realtorContext.font = realtorFontSize + 'px sans-serif');

  // Get Page Dimensions

  const pdf = await PDFDocument.create();

  for (const [index, values] of data.entries()) {
    let headerIds = headers[index].map((header: any) => header.id);
    let headerLabels = headers[index].map((header: any) => header.label);

    const tableWidth = headerIds.reduce((prevValue: any, key: number) => prevValue + columnWidths[index][key] + cellPadding * 2, 0);
    const width = padding * 2 + tableWidth;
    const height = padding * 2 + rowHeight * values.length + headerHeight * 3;

    let page = pdf.addPage([width, height]); // Get page
    page.moveTo(padding, height - padding); // Add padding
    page.moveDown(headerHeight);

    let projectLogoImage;
    if (projectLogo) {
      const projectLogoBytes = await fetch(projectLogo).then((res) => res.arrayBuffer());
      projectLogoImage = await pdf.embedJpg(projectLogoBytes);
      page.drawImage(projectLogoImage, {
        x: page.getX() + cellPadding,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        height: 25,
        width: 40,
      });
    }

    let rdsLogoImage;
    // const rdsLogoBytes = await fetch("https://rdsre.s3.ca-central-1.amazonaws.com/assets/logo.jpg").then((res) => res.arrayBuffer());
    // rdsLogoImage = await pdf.embedJpg(rdsLogoBytes);

    if (rdsLogoImage) {
      page.drawImage(rdsLogoImage, {
        x: page.getWidth() - page.getX() - 25,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        height: 25,
        width: 25,
      });
      page.moveDown(headerHeight);
    }

    page.drawText(pdfTitles[index], {
      x: page.getX() + cellPadding + 50,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: fontSize + 4,
      lineHeight: fontSize / 2,
    });

    page.drawText(convertAllDates(new Date(), 'PPpp'), {
      x: page.getWidth() - page.getX() - 200,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: fontSize + 4,
      lineHeight: fontSize / 2,
    });

    page.moveDown(headerHeight);

    for (const [i, header] of headerIds.entries()) {
      page.drawRectangle({ height: headerHeight, width: columnWidths[index][header] + cellPadding * 2, color: hexToPdfRgb('#2e453d') });
      page.drawText(headerLabels[i], {
        x: page.getX() + cellPadding,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        size: fontSize,
        color: rgb(1, 1, 1),
        lineHeight: fontSize / 2,
      });
      page.moveRight(columnWidths[index][header] + cellPadding * 2);
    }
    page.moveLeft(tableWidth);

    // eslint-disable-next-line
    for (const [i, value] of values.entries()) {
      // Add rows
      page.moveDown(rowHeight);
      headerIds.forEach((header: any) => {
        page.drawRectangle({
          height: rowHeight,
          width: columnWidths[index][header] + cellPadding * 2,
          borderWidth: 1,
          borderColor: grayscale(0.5),
        });
        page.drawText(value[header] ? value[header].toString() : '0', {
          x: page.getX() + cellPadding,
          y: page.getY() + rowHeight - fontSize - cellPadding,
          size: fontSize,
          lineHeight: fontSize / 2,
        });
        page.moveRight(columnWidths[index][header] + cellPadding * 2);
      });
      page.moveLeft(tableWidth);
    }
    page.moveDown(headerHeight);
  }

  if (downloadPdf) {
    const pdfFile = await pdf.save();
    download(pdfFile, fileName, 'application/pdf');
  } else {
    let pages = pdf.getPages();
    for (const page of pages) {
      let today = new Date();
      let hotlistMessage =
        'All Prices, Incentives and Specifications are subject to change without notice. *Please speak with an RDS Sales Representative for details. E. & O.E. ';
      hotlistMessage += today.toLocaleDateString('en-us', { month: 'long', day: 'numeric', year: 'numeric' });

      const registeredText = 'Residential Development Services ® ' + today.getFullYear();
      page.drawText(hotlistMessage, { x: 20, y: 20, size: 10 });
      page.drawText(registeredText, { x: 20, y: 30, size: 10 });
    }
    return pdf;
  }
};

export const downloadMultiplePdf = async (
  data: any,
  headers: any,
  backgrounds: any,
  columnWidths: any,
  pdfTitles: string[],
  fileName: string,
  projectLogo: any = null
) => {
  if (!data.length) return;
  // Constants
  const padding = 20;
  const fontSize = 12;
  const realtorFontSize = 100;
  const textOffset = 5;
  const cellPadding = 5;
  const rowHeight = cellPadding * 2 + fontSize * 3 + textOffset;
  const headerHeight = cellPadding * 2 + fontSize + textOffset;
  const logoLength = 50;

  // Get text width
  const context = document.createElement('canvas').getContext('2d');
  context && (context.font = fontSize + 'px sans-serif');
  const realtorContext = document.createElement('canvas').getContext('2d');
  realtorContext && (realtorContext.font = realtorFontSize + 'px sans-serif');

  // Get Page Dimensions

  const pdf = await PDFDocument.create();

  for (const [index, values] of data.entries()) {
    let headerIds = headers[index].map((header: any) => header.id);
    let headerLabels = headers[index].map((header: any) => header.label);

    const tableWidth = headerIds.reduce((prevValue: any, key: number) => prevValue + columnWidths[index][key] + cellPadding * 2, 0);
    const width = padding * 2 + tableWidth;
    const height = padding * 2 + rowHeight * values.length + headerHeight * 3;

    let page = pdf.addPage([width, height]); // Get page
    page.moveTo(padding, height - padding); // Add padding
    page.moveDown(headerHeight);

    let rdsLogoImage;
    // const rdsLogoBytes = await fetch("https://rdsre.s3.ca-central-1.amazonaws.com/assets/logo.jpg").then((res) => res.arrayBuffer());
    // rdsLogoImage = await pdf.embedJpg(rdsLogoBytes);

    let projectLogoImage;
    if (projectLogo) {
      const projectLogoBytes = await fetch(projectLogo).then((res) => res.arrayBuffer());
      projectLogoImage = await pdf.embedJpg(projectLogoBytes);
      page.drawImage(projectLogoImage, {
        x: page.getX() + cellPadding + 50,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        height: 25,
        width: 40,
      });
    }

    if (rdsLogoImage) {
      page.drawImage(rdsLogoImage, {
        x: page.getWidth() - page.getX() - 25,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        height: 25,
        width: 25,
      });
      page.moveDown(headerHeight);
    }

    page.drawText(pdfTitles[index], {
      x: page.getX() + cellPadding,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: fontSize + 4,
      lineHeight: fontSize / 2,
    });

    page.drawText(convertAllDates(new Date(), 'PPpp'), {
      x: page.getWidth() - page.getX() - 200,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: fontSize + 4,
      lineHeight: fontSize / 2,
    });

    page.moveDown(headerHeight);

    for (const [i, header] of headerIds.entries()) {
      page.drawRectangle({ height: headerHeight, width: columnWidths[index][header] + cellPadding * 2, color: hexToPdfRgb('#2e453d') });
      page.drawText(headerLabels[i], {
        x: page.getX() + cellPadding,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        size: fontSize,
        color: rgb(1, 1, 1),
        lineHeight: fontSize / 2,
      });
      page.moveRight(columnWidths[index][header] + cellPadding * 2);
    }
    page.moveLeft(tableWidth);

    for (const [i, value] of values.entries()) {
      // Add rows
      page.moveDown(rowHeight);
      headerIds.forEach((header: any) => {
        page.drawRectangle({
          height: rowHeight,
          width: columnWidths[index][header] + cellPadding * 2,
          borderWidth: 1,
          borderColor: grayscale(0.5),
        });
        page.drawText(value[header] ? value[header].toString() : '0', {
          x: page.getX() + cellPadding,
          y: page.getY() + rowHeight - fontSize - cellPadding,
          size: fontSize,
          lineHeight: fontSize / 2,
        });
        page.moveRight(columnWidths[index][header] + cellPadding * 2);
      });
      page.moveLeft(tableWidth);
    }
    page.moveDown(headerHeight);
  }

  const pdfFile = await pdf.save();
  download(pdfFile, fileName, 'application/pdf');
};

/**
 * Validates an array
 * @param arr
 * @param validator
 * @param defaultValue
 * @returns
 */
export function validateArray<T>(arr: T[], validator: (entry: T) => boolean, defaultValue: boolean) {
  return arr.length ? arr.every(validator) : defaultValue;
}

export const getDoubleDigitNumber = (num: number | string) => {
  let numeral = typeof num === 'string' ? parseInt(num) : num;
  return numeral < 10 ? '0' + numeral : numeral + '';
};

export const getInnerText: (elem: React.ReactElement | string) => string = (elem: React.ReactElement | string) => {
  if (!elem) return '';
  if (typeof elem === 'string') return elem;
  const children = elem.props.children;
  if (children instanceof Array) return children.map(getInnerText).join('');
  return getInnerText(children);
};

export const sortSuites = (data: any, type: string) => {
  data.sort((a: any, b: any) => {
    //a and b are IUnits
    let keyA = a[type];
    let keyB = b[type];

    if (keyA === null && keyB === null) return 0;
    else if (keyA === null) return 1;
    else if (keyB === null) return -1;

    if (typeof keyA === 'number' && typeof keyB === 'number') return keyA - keyB;

    typeof keyA === 'string' ? keyA.toLowerCase() : (keyA = React.isValidElement(keyA) ? getInnerText(keyA) : keyA.toString());
    typeof keyB === 'string' ? keyB.toLowerCase() : (keyB = React.isValidElement(keyB) ? getInnerText(keyB) : keyB.toString());

    return keyA.localeCompare(keyB, 'en', { numeric: true });
  });

  return data;
};

export const sortNestedSuites = (data: any, type: string, type2: string) => {
  data.sort((a: any, b: any) => {
    //a and b are IUnits
    let keyA = a[type][type2];
    let keyB = b[type][type2];

    if (keyA === null && keyB === null) return 0;
    else if (keyA === null) return 1;
    else if (keyB === null) return -1;

    if (typeof keyA === 'number' && typeof keyB === 'number') return keyA - keyB;

    typeof keyA === 'string' ? keyA.toLowerCase() : (keyA = React.isValidElement(keyA) ? getInnerText(keyA) : keyA.toString());
    typeof keyB === 'string' ? keyB.toLowerCase() : (keyB = React.isValidElement(keyB) ? getInnerText(keyB) : keyB.toString());

    return keyA.localeCompare(keyB, 'en', { numeric: true });
  });

  return data;
};

export const getInitials = (string: string) => {
  var names = string.split(' '),
    initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export const blobToBase64 = async (blobs: Blob[]) => {
  let base64 = await Promise.all(
    blobs.map(async (blob: Blob) => {
      return new Promise((resolve, _) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    })
  );
  return base64;
};

export const downloadImages = async (
  files: any[],
  projectEmail: string,
  projectName: string,
  share: boolean,
  projectId: string,
  project?: IProject | null,
  // When given, the merged PDF is handed to this instead of being shared/downloaded here (used to
  // upload it and send a link). Resolves to whether the hand-off succeeded.
  onMerged?: (pdf: File) => Promise<boolean>
) => {
  interface IMarketingBlob {
    modelType: string;
    suite: string;
    basePrice: string;
    rental: number;
    blob: any;
    size: number;
    hstRebatePrice: string;
  }

  let marketingFloorPlansBlobs: IMarketingBlob[] = [];
  let floorPlanTable: any;

  let headers = [
    {
      label: 'Suite',
      id: 'suite',
    },
    {
      label: 'Model Type',
      id: 'modelType',
    },
    {
      label: 'Unit Type',
      id: 'unitType',
    },
    {
      label: 'Size',
      id: 'size',
    },
    {
      label: 'Unit Number',
      id: 'unit',
    },
    {
      label: 'Level',
      id: 'level',
    },
    {
      label: 'Bathrooms',
      id: 'bathroom',
    },
    {
      label: 'Exposure',
      id: 'exposure',
    },
    {
      label: 'Outdoor Size',
      id: 'outdoorSize',
    },
    {
      label: 'PPSF',
      id: 'ppsf',
    },
    {
      label: 'Starting Price',
      id: 'basePrice',
    },
    // Net-HST projects already bake the rebate into Starting Price above, so a separate rebate
    // column would just repeat the same number.
    ...(project?.netHst
      ? []
      : [
          {
            label: 'Approx. HST Rebate Price',
            id: 'hstRebatePrice',
          },
        ]),
  ];

  let pdfWidths: any = {
    suite: 100,
    basePrice: 100,
    hstRebatePrice: 200,
    level: 100,
    unit: 100,
    modelType: 100,
    unitType: 100,
    size: 100,
    exposure: 100,
    bathroom: 100,
    outdoorSize: 100,
    ppsf: 100,
  };

  if (files[0].rental) {
    headers.push({
      label: 'Rental Guarantee',
      id: 'rental',
    });
    pdfWidths.rental = 100;
  }

  let sheetTitle = ``;
  let data = files.map((unit: any) => {
    // Net-HST projects show the net price as the Starting Price itself, not the gross price, so the
    // PPSF has to be worked out from that same price to match it (like the IPL's Net PPSF).
    const displayPrice = project?.netHst ? netHST(unit.basePrice, project) : unit.basePrice;
    return {
      ...unit,
      basePrice: numToCurrency.format(displayPrice),
      hstRebatePrice: numToCurrency.format(effectivePriceAfterRebate(unit.basePrice, project)),
      rental: unit.rental ? numToCurrency.format(unit.rental) : '',
      ppsf: unit.size ? numToCurrency.format(displayPrice / unit.size) : '-',
      outdoorSize: getOutdoorSize(unit.outdoor),
    };
  });

  floorPlanTable = await downloadPdf([data], [headers], [], [pdfWidths], [sheetTitle], sheetTitle, null, false);

  await Promise.all(
    files.map(async (unit: any, index: number, self: any[]) => {
      let response = await fetch(unit.pdfFloorPlan ? unit.pdfFloorPlan : unit.marketingFloorPlan!, { cache: 'no-cache' });
      let blob = await response.blob();
      marketingFloorPlansBlobs.push({
        rental: unit.rental ? unit.rental : 0,
        suite: unit.suite,
        // Net-HST projects draw the net price as "Price" on the floorplan itself, not the gross
        // price - see the drawText calls below, which skip the separate rebate line in that case.
        basePrice: project?.netHst ? netHST(unit.basePrice, project) : unit.basePrice,
        modelType: unit.modelType,
        hstRebatePrice: numToCurrency.format(effectivePriceAfterRebate(unit.basePrice, project)),
        blob: blob,
        size: unit.size,
      });
    })
  );

  let allBlobs = marketingFloorPlansBlobs
    .filter((blob: IMarketingBlob) => blob.blob)
    .sort((a, b) => a.size - b.size)
    .map((blobs: IMarketingBlob) => blobs.blob);

  let renderingsBase64 = await blobToBase64(allBlobs);

  marketingFloorPlansBlobs = marketingFloorPlansBlobs
    .filter((blob: IMarketingBlob) => blob.blob)
    .sort((a, b) => a.size - b.size)
    .map((marketing: IMarketingBlob, index: number) => {
      return {
        ...marketing,
        blob: renderingsBase64[index],
      };
    });

  const mergedPdf = await PDFDocument.create();
  let [copiedPages] = await mergedPdf.copyPages(floorPlanTable!, [0]);
  mergedPdf.addPage(copiedPages);

  if (files.length > 0) {
    await Promise.all(
      marketingFloorPlansBlobs.map(async (file: any) => {
        let floorPlanDoc;
        let page: any;
        let fontSize = 14;
        let y = 80;
        let yIncrease = 10;
        if (!file.blob.includes('application/pdf')) {
          floorPlanDoc = await PDFDocument.create();
          const response = await fetch(file.blob!, { cache: 'no-cache' }).then((response) => response.arrayBuffer());
          const jpgImage = await mergedPdf.embedJpg(response);
          page = mergedPdf.addPage();

          if (jpgImage.width > jpgImage.height) {
            fontSize = 40;
            yIncrease = 20;
            page.setSize(jpgImage.width, jpgImage.height + 300);
            jpgImage.scale(0.9);
            page.drawImage(jpgImage, {
              y: 140,
              width: jpgImage.width,
              height: jpgImage.height,
            });
          } else {
            page.drawImage(jpgImage, {
              y: 140,
              width: page.getWidth(),
              height: page.getHeight(),
            });
          }

          if (jpgImage.height > jpgImage.width) {
            page.setSize(612.0, 1008.0);
          }

          if (file.suite) {
            page.drawText(`Suite: ${file.suite}`, {
              x: 35,
              y: y + yIncrease * 2,
              size: fontSize,
              color: rgb(0, 0, 0),
            });
          }
          if (file.basePrice) {
            page.drawText(`Price: ${numToCurrency.format(file.basePrice)}`, {
              x: 35,
              y: y + yIncrease * 4,
              size: fontSize,
              color: rgb(0, 0, 0),
            });
            if (!project?.netHst) {
              page.drawText(`Effective Price After HST Rebate: ${numToCurrency.format(effectivePriceAfterRebate(file.basePrice, project))}`, {
                x: 35,
                y: y + yIncrease * 6,
                size: fontSize,
                color: rgb(0, 0, 0),
              });
            }
          }
          if (file.rental) {
            page.drawText(`RENTAL GUARANTEE AMOUNT: ${numToCurrency.format(file.rental)}`, {
              x: 35,
              y: y / 2,
              size: fontSize,
              color: rgb(1, 0, 0),
            });
          }
          page.drawText(`Please consult a RDS Representative for details. (e) ${projectEmail}; E&OE. ${new Date().toLocaleDateString()}`, {
            x: 35,
            y: 20,
            size: 9,
            color: rgb(0, 0, 0),
          });
          // Draw Suite and BasePrice

          let copiedPages = await mergedPdf.copyPages(floorPlanDoc, floorPlanDoc.getPageIndices());
          await copiedPages.forEach((page: any) => mergedPdf.addPage(page));
        } else {
          floorPlanDoc = await PDFDocument.load(file.blob);
          page = floorPlanDoc.getPage(0);
          page.setHeight(page.getHeight() + 120);
          await page.translateContent(0, 100);

          let pageRotation = page.getRotation();
          if (file.suite) {
            let pageRotationValue = pageRotate(pageRotation, 35, -20, 1, page.getSize());
            let checkMediaBox = page.getMediaBox();
            
            let x = pageRotationValue.x;
            let y = pageRotationValue.y;
            if (pageRotation.angle === 0 && checkMediaBox && checkMediaBox.x < 0 && checkMediaBox.y < 0) {
              if (checkMediaBox.width > checkMediaBox.height) {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = checkMediaBox.y - 20;
              } else {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = pageRotationValue.y - Math.abs(checkMediaBox.y);
              }
            } else if (pageRotation.angle === 0) {
                x = 35
                y = -20;
            } else {
              if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = pageRotationValue.y - Math.abs(checkMediaBox.y);
              }
            }
            page.drawText(`Suite: ${file.suite}`, {
              x: x,
              y: y,
              size: fontSize,
              color: rgb(0, 0, 0),
            });
          }
          if (file.basePrice) {
            let pageRotationValue = pageRotate(pageRotation, 35, -40, 1, page.getSize());
            let checkMediaBox = page.getMediaBox();

            let x = pageRotationValue.x;
            let y = pageRotationValue.y;
            if (pageRotation.angle === 0 && checkMediaBox && checkMediaBox.x < 0 && checkMediaBox.y < 0) {
              if (checkMediaBox.width > checkMediaBox.height) {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = checkMediaBox.y - 40;
              } else {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = pageRotationValue.y - Math.abs(checkMediaBox.y);
              }
            } else if (pageRotation.angle === 0) {
                x = 35;
                y = -40;
            } else {
              if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = pageRotationValue.y - Math.abs(checkMediaBox.y);
              }
            }
            page.drawText(`Price: ${numToCurrency.format(file.basePrice)}`, {
              x: x,
              y: y,
              size: fontSize,
              color: rgb(0, 0, 0),
            });
            if (!project?.netHst) {
              page.drawText(`Effective Price After HST Rebate: ${numToCurrency.format(effectivePriceAfterRebate(file.basePrice, project))}`, {
                x: x,
                y: y - 20,
                size: fontSize,
                color: rgb(0, 0, 0),
              });
            }
          }
          if (file.rental) {
            let pageRotationValue = pageRotate(pageRotation, 35, -80, 1, page.getSize());
            let checkMediaBox = page.getMediaBox();

            let x = pageRotationValue.x;
            let y = pageRotationValue.y;
            if (pageRotation.angle === 0 && checkMediaBox && checkMediaBox.x < 0 && checkMediaBox.y < 0) {
              if (checkMediaBox.width > checkMediaBox.height) {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = checkMediaBox.y - 80;
              } else {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = pageRotationValue.y - Math.abs(checkMediaBox.y);
              }
            } else if (pageRotation.angle === 0) {
              x = 35;
              y = -80;
            } else {
              if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
                x = pageRotationValue.x - Math.abs(checkMediaBox.x);
                y = pageRotationValue.y - Math.abs(checkMediaBox.y);
              }
            }
            page.drawText(`RENTAL GUARANTEE AMOUNT: ${numToCurrency.format(file.rental)}`, {
              x: x,
              y: y,
              size: fontSize,
              color: rgb(1, 0, 0),
            });
          }
          let pageRotationValue = pageRotate(pageRotation, 35, -110, 1, page.getSize());
          let checkMediaBox = page.getMediaBox();

          let x = pageRotationValue.x;
          let y = pageRotationValue.y;
          if (pageRotation.angle === 0 && checkMediaBox && checkMediaBox.x < 0 && checkMediaBox.y < 0) {
            if (checkMediaBox.width > checkMediaBox.height) {
              x = pageRotationValue.x - Math.abs(checkMediaBox.x);
              y = checkMediaBox.y - 90;
            } else {
              x = pageRotationValue.x - Math.abs(checkMediaBox.x);
              y = pageRotationValue.y - Math.abs(checkMediaBox.y);
            }
          } else if (pageRotation.angle === 0) {
            x = 35;
            y = -110;
          } else {
            if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
              x = pageRotationValue.x - Math.abs(checkMediaBox.x);
              y = pageRotationValue.y - Math.abs(checkMediaBox.y);
            }
          }
          page.drawText(`Please consult a RDS Representative for details. (e) ${projectEmail}; E&OE. ${new Date().toLocaleDateString()}`, {
            x: x,
            y: y,
            size: 9,
            color: rgb(0, 0, 0),
          });

          let copiedPages = await mergedPdf.copyPages(floorPlanDoc, floorPlanDoc.getPageIndices());
          await copiedPages.forEach((page: any) => mergedPdf.addPage(page));
        }
      })
    );
    const mergedPdfFile: any = await mergedPdf.save();

    const pdf = await new File([mergedPdfFile], `${projectName} Floorplans.pdf`, { type: 'application/pdf' });
    if (onMerged) {
      return onMerged(pdf);
    }
    // navigator.share is unavailable on most desktop browsers (and some older mobile ones), and
    // even where it exists it doesn't always support sharing files - navigator.canShare checks
    // that specifically. Previously this branched on a userAgent sniff whose two branches did the
    // exact same thing, then just logged to console with no fallback on any device where share
    // wasn't supported or failed, so the button silently did nothing.
    let navigator: any = window.navigator;
    const canShareFiles = share && navigator.share && (!navigator.canShare || navigator.canShare({ files: [pdf] }));
    if (canShareFiles) {
      try {
        await navigator.share({ files: [pdf], title: `${projectName} Floorplans`, text: `${projectName} Floorplans` });
        // Only a completed native share should read as "successfully shared" to callers (eg. for
        // logging a Units Shared activity) - a fallback download below is not a share.
        return true;
      } catch (err: any) {
        // AbortError means the user closed the native share sheet themselves - that's a
        // deliberate choice, not a failure to recover from, so don't auto-download behind them.
        if (err?.name !== 'AbortError') {
          console.log(err, 'err');
          download(mergedPdfFile, `${projectName} Floorplans.pdf`, 'application/pdf');
        }
        return false;
      }
    } else {
      download(mergedPdfFile, `${projectName} Floorplans.pdf`, 'application/pdf');
      return false;
    }
  }

  return false;
};

export const reverseCamelCase = (string: string) => {
  if (!string) return string;
  let newString = string.charAt(0).toUpperCase();
  for (let i = 1; i < string.length; ++i) {
    if ('A'.charCodeAt(0) <= string.charCodeAt(i) && string.charCodeAt(i) <= 'Z'.charCodeAt(0)) newString += ' ';
    newString += string.charAt(i);
  }
  return newString;
};

export const checkRealtorPublic = (tags: string[]) => {
  if (tags.includes('Friends and Family')) {
    return 'F&F';
  } else if (tags.includes('Public')) {
    return 'Public';
  } else return 'N/A';
};

// DASHBOARD EXCEL TABLES

export const unitCells = [
  { id: 'status', numeric: false, disablePadding: true, label: 'Status' },
  { id: 'suites', numeric: true, disablePadding: false, label: 'Number of Suites' },
  { id: 'size', numeric: true, disablePadding: false, label: 'Size (sq/ft)' },
  { id: 'soldPrice', numeric: true, disablePadding: false, label: 'Revenue' },
  { id: 'PPSF', numeric: true, disablePadding: false, label: 'PPSF' },
  { id: 'soldPriceNet', numeric: true, disablePadding: false, label: 'Revenue Net' },
  { id: 'PPSFNet', numeric: true, disablePadding: false, label: 'PPSF Net' },
];

export const columnWidths = {
  status: 15,
  suites: 15,
  size: 15,
  soldPrice: 30,
  PPSF: 15,
  soldPriceNet: 30,
  PPSFNet: 15,
};

export const pdfColumnWidths = {
  status: 110,
  suites: 100,
  suitesPercentage: 50,
  size: 100,
  sizePercentage: 50,
  soldPrice: 100,
  soldPricePercentage: 100,
  PPSF: 100,
  soldPriceNet: 100,
  PPSFNet: 100,
};

export const unitRows = (rows: any, totalRow: any) => {
  let excel = rows.map((row: any) => {
    return {
      ...row,
      PPSF: numToCurrency.format(row.PPSF),
      PPSFNet: numToCurrency.format(row.PPSFNet),
      sizePercentage: `${row.suitesPercentage}%`,
      soldPrice: numToCurrency.format(row.soldPrice),
      soldPriceNet: numToCurrency.format(row.soldPriceNet),
      suitesPercentage: `${row.suitesPercentage}%`,
    };
  });
  let totalExcelRows = {
    ...totalRow,
    PPSF: totalRow.soldPrice ? numToCurrency.format(totalRow.soldPrice / totalRow.size) : '0(0%)',
    PPSFNet: totalRow.soldPrice ? numToCurrency.format(totalRow.soldPriceNet / totalRow.size) : '0(0%)',
    sizePercentage: `${totalRow.sizePercentage}%}`,
    soldPrice: numToCurrency.format(totalRow.soldPrice),
    soldPriceNet: numToCurrency.format(totalRow.soldPriceNet),
    suitesPercentage: `${totalRow.suitesPercentage}%}`,
  };

  return [...excel, totalExcelRows];
};

export const optionCells = [
  { id: 'optionName', numeric: false, disablePadding: true, label: 'Option' },
  { id: 'totalAvailableOptions', numeric: true, disablePadding: false, label: 'Total Options' },
  { id: 'available', numeric: true, disablePadding: false, label: 'Available' },
  { id: 'availableRevenue', numeric: true, disablePadding: false, label: 'Available Revenue' },
  { id: 'availableRevenueNetHST', numeric: true, disablePadding: false, label: 'Available Revenue Net HST' },
  { id: 'pendingOptions', numeric: true, disablePadding: false, label: 'Pending Options (O, S)' },
  { id: 'pendingRevenue', numeric: true, disablePadding: false, label: 'Pending Revenue (O, S)' },
  { id: 'soldOptions', numeric: true, disablePadding: false, label: 'Sold Options (C, F)' },
  { id: 'soldRevenue', numeric: true, disablePadding: false, label: 'Sold Revenue (C, F)' },
  { id: 'totalOptions', numeric: true, disablePadding: false, label: 'Combined Options (Sold + Pending)' },
  { id: 'combinedRevenue', numeric: true, disablePadding: false, label: 'Combined Revenue (Sold + Pending)' },
  { id: 'totalPPSF', numeric: true, disablePadding: false, label: 'Total PPSF' },
  { id: 'totalRevenue', numeric: true, disablePadding: false, label: 'Total Revenue' },
  { id: 'totalRevenueNetHST', numeric: true, disablePadding: false, label: 'Total Revenue Net HST' },
];

export const pdfOptionWidths = {
  available: 50,
  availableRevenue: 150,
  availableRevenueNetHST: 200,
  combinedRevenue: 200,
  optionName: 150,
  optionPrice: 100,
  pendingOptions: 150,
  pendingOptionsPercentage: 100,
  pendingRevenue: 150,
  pendingRevenuePercentage: 100,
  soldOptions: 100,
  soldOptionsPercentage: 50,
  soldRevenue: 150,
  soldRevenuePercentage: 50,
  totalAvailableOptions: 100,
  totalOptions: 200,
  totalOptionsPercentage: 100,
  totalPPSF: 50,
  totalRevenue: 100,
  totalRevenueNetHST: 200,
};

export const optionWidths = {
  optionName: 15,
  totalAvailableOptions: 15,
  available: 15,
  availableRevenue: 15,
  availableRevenueNetHST: 15,
  pendingOptions: 15,
  pendingRevenue: 15,
  soldOptions: 15,
  soldRevenue: 15,
  totalOptions: 15,
  combinedRevenue: 15,
  totalPPSF: 15,
  totalRevenue: 15,
  totalRevenueNetHST: 15,
};

export const excelOptionRows = (rows: any, totalRow: any) => {
  let excel = rows.map((row: any) => {
    return {
      ...row,
      available: row.totalAvailableOptions - row.totalOptions,
      availableRevenue: numToCurrency.format((row.totalAvailableOptions - row.totalOptions) * row.optionPrice),
      availableRevenueNetHST: numToCurrency.format((row.totalAvailableOptions - row.totalOptions) * row.optionPrice * 0.93),
      pendingOptions: row.pendingOptions + ' (' + row.pendingOptionsPercentage + '%)',
      pendingRevenue:
        numToCurrency.format(row.pendingRevenue) +
        ' (' +
        ((row.pendingRevenue / (row.totalRevenue + (row.totalAvailableOptions - row.totalOptions) * row.optionPrice)) * 100).toFixed(2) +
        '%)',
      soldOptions: row.soldOptions + ' (' + row.soldOptionsPercentage + '%)',
      soldRevenue: numToCurrency.format(row.soldRevenue) + ' (' + row.soldRevenuePercentage + '%)',
      totalOptions: row.totalOptions + ' (' + row.totalOptionsPercentage + '%)',
      combinedRevenue: numToCurrency.format(row.combinedRevenue),
      totalPPSF: numToCurrency.format(row.totalPPSF),
      totalRevenue: numToCurrency.format(row.totalRevenue + (row.totalAvailableOptions - row.totalOptions) * row.optionPrice),
      totalRevenueNetHST: numToCurrency.format(
        (row.totalRevenue + (row.totalAvailableOptions - row.totalOptions) * row.optionPrice) * 0.93
      ),
    };
  });
  let totalExcelRows = {
    ...totalRow,
    available: totalRow.available,
    availableRevenue: numToCurrency.format(totalRow.availableRevenue * 0.93),
    availableRevenueNetHST: numToCurrency.format(totalRow.availableRevenue * 0.93),
    pendingOptions: totalRow.pendingOptions,
    pendingRevenue: numToCurrency.format(totalRow.pendingRevenue),
    soldOptions: totalRow.soldOptions,
    soldRevenue: numToCurrency.format(totalRow.soldRevenue),
    totalOptions: totalRow.totalOptions,
    combinedRevenue: numToCurrency.format(totalRow.combinedRevenue),
    totalPPSF: numToCurrency.format(totalRow.totalPPSF),
    totalRevenue: numToCurrency.format(totalRow.totalRevenue),
    totalRevenueNetHST: numToCurrency.format(totalRow.totalRevenue * 0.93),
  };

  return [...excel, totalExcelRows];
};

export const totalCells = [
  { id: 'optionName', numeric: false, disablePadding: true, label: '' },
  { id: 'soldUnitRevenue', numeric: true, disablePadding: false, label: 'Sold Unit Revenue' },
  { id: 'soldOptionsRevenue', numeric: true, disablePadding: false, label: 'Sold Options Revenue' },
  { id: 'overallSoldRevenue', numeric: true, disablePadding: false, label: 'Overall Sold Revenue' },
  { id: 'soldUnitRevenueHST', numeric: true, disablePadding: false, label: 'Sold Unit Revenue (Net HST)' },
  { id: 'overallSoldRevenueHST', numeric: true, disablePadding: false, label: 'Overall Sold Revenue (Net HST)' },
  { id: 'overallRevenue', numeric: true, disablePadding: false, label: 'Overall Total Revenue' },
  { id: 'overallRevenuePPSF', numeric: true, disablePadding: false, label: 'Overall Total Revenue PPSF' },
  { id: 'overallRevenueHST', numeric: true, disablePadding: false, label: 'Overall Total Revenue (Net of HST)' },
];

export const totalWidths = {
  soldUnitRevenue: 15,
  soldOptionsRevenue: 15,
  overallSoldRevenue: 15,
  soldUnitRevenueHST: 15,
  overallSoldRevenueHST: 15,
  overallRevenue: 15,
  overallRevenuePPSF: 15,
  overallRevenueHST: 15,
};

export const pdfTotalWidths = {
  optionName: 100,
  overallRevenue: 150,
  overallRevenueHST: 210,
  overallRevenuePPSF: 150,
  overallSoldRevenue: 150,
  overallSoldRevenueHST: 200,
  soldOptionsRevenue: 150,
  soldOptionsRevenueHST: 200,
  soldUnitRevenue: 150,
  soldUnitRevenueHST: 200,
};

export const excelTotalRows = (rows: any, totalRow: any) => {
  let excel = rows.map((row: any) => {
    return {
      ...row,
      optionName: capitalizeFirstLetter(row.optionName),
      soldUnitRevenue: numToCurrency.format(row.soldUnitRevenue),
      soldOptionsRevenue: numToCurrency.format(row.overallSoldRevenue),
      overallSoldRevenue: numToCurrency.format(row.soldUnitRevenueHST),
      soldUnitRevenueHST: numToCurrency.format(row.soldUnitRevenueHST),
      overallSoldRevenueHST: numToCurrency.format(row.overallSoldRevenueHST),
      overallRevenue: numToCurrency.format(row.overallRevenue),
      overallRevenuePPSF: numToCurrency.format(row.overallRevenuePPSF),
      overallRevenueHST: numToCurrency.format(row.overallRevenueHST),
    };
  });
  let totalExcelRows = {
    ...totalRow,
    soldUnitRevenue: numToCurrency.format(totalRow.soldUnitRevenue),
    soldOptionsRevenue: numToCurrency.format(totalRow.soldOptionsRevenue),
    overallSoldRevenue: numToCurrency.format(totalRow.overallSoldRevenue),
    soldUnitRevenueHST: numToCurrency.format(totalRow.soldUnitRevenueHST),
    overallSoldRevenueHST: numToCurrency.format(totalRow.overallSoldRevenueHST),
    overallRevenue: numToCurrency.format(totalRow.overallRevenue),
    overallRevenuePPSF: numToCurrency.format(totalRow.overallRevenuePPSF),
    overallRevenueHST: numToCurrency.format(totalRow.overallRevenueHST),
  };

  return [...excel, totalExcelRows];
};

export const customSort = (rows: any, columnToSort: any, subColumn: any, sortAsc: boolean) => {
  const sortedRows = rows?.slice();
  sortedRows.sort((a: any, b: any) => {
    //a and b are IUnits
    let keyA = a[columnToSort];
    let keyB = b[columnToSort];

    if (subColumn) {
      keyA = a[columnToSort][subColumn];
      keyB = b[columnToSort][subColumn];
    }

    if (columnToSort === 'realtor' || columnToSort === 'purchaser') {
      if (keyA === 'Public' || keyB === 'Public') {
        return -1;
      }
      if (keyA < keyB) {
        return -1;
      }
      if (keyA > keyB) {
        return 1;
      }
      return 0;
    }

    if (keyA === null && keyB === null) return 0;
    else if (keyA === null) return 1;
    else if (keyB === null) return -1;

    if (typeof keyA === 'number' && typeof keyB === 'number') return sortAsc ? keyA - keyB : keyB - keyA;

    typeof keyA === 'string' ? keyA.toLowerCase() : (keyA = keyA.toString());
    typeof keyB === 'string' ? keyB.toLowerCase() : (keyB = keyB.toString());

    return sortAsc ? keyA.localeCompare(keyB, 'en', { numeric: true }) : -keyA.localeCompare(keyB, 'en', { numeric: true });
  });
  return sortedRows;
};

export const downloadAllFloorplans = async (files: any[], fileName: string, setLoading: any = null) => {
  const zip = new JSZip();

  //fetches the files and saves the files as blobs in an array

  const addFilesToZip: any = async (fileBlobs: any) => {
    if (fileBlobs) {
      if (fileBlobs.pdf.type === 'image/jpeg' || fileBlobs.file.includes('jpg') || fileBlobs.file.includes('jpeg')) {
        zip?.file(`${fileBlobs.name}.jpeg`, fileBlobs.pdf);
      } else if (fileBlobs.pdf.type === 'application/pdf' || fileBlobs.pdf.type === 'application/octet-stream') {
        zip?.file(`${fileBlobs.name}.pdf`, fileBlobs.pdf);
      } else if (fileBlobs.pdf.type === 'image/png') {
        zip?.file(`${fileBlobs.name}.png`, fileBlobs.pdf);
      }
    }
  };

  //downloads the zip
  const downloadZip = (zip: JSZip) => {
    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${fileName}.zip`);
    });
    if (setLoading) {
      setLoading(false);
    }
  };

  for (let file of files) {
    await addFilesToZip(file);
  }

  await downloadZip(zip);
};

export const downloadAllDocuments = async (files: any[], fileName: string) => {
  const zip = new JSZip();

  //fetches the files and saves the files as blobs in an array

  const addFilesToZip: any = async (fileBlobs: any) => {
    let suiteZip = zip.folder(`${fileBlobs.suite}`);
    if (fileBlobs.documents.length) {
      fileBlobs.documents.forEach((document: any) => {
        let blob = new Blob([document.pdf]);
        suiteZip?.file(`${fileBlobs.suite} - ${document.name}.pdf`, blob);
      });
    }
  };

  //downloads the zip
  const downloadZip = (zip: JSZip) => {
    zip.generateAsync({ type: 'blob' }).then(async (content) => {
      await saveAs(content, `${fileName}.zip`);
    });
    return 'Download Complete';
  };

  for (let file of files) {
    await addFilesToZip(file);
  }

  return downloadZip(zip);
};

export const uploadUpgrades = async (file: File) => {
  const workbook = new ExcelJS.Workbook();
  const fileBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(fileBuffer);
  let headerOrder: string[] = [];
  let upgrades: any[] = [];
  let errorMessage = 'The file uploaded does not contain the following headers: ';
  let missingHeaders = [];

  let thisFile = workbook.model.worksheets[0] as any;

  for (let headerCell of thisFile.rows[0].cells) {
    if (headerCell.value) {
      headerOrder.push(normalToCamel(headerCell.value));
    } else {
      headerOrder.push('');
    }
  }

  const hasName = headerOrder.some((e) => e === 'name');
  const hasPrice = headerOrder.some((e) => e === 'price');
  const hasCostPrice = headerOrder.some((e) => e === 'costPrice');
  const hasMainCategory = headerOrder.some((e) => e === 'mainCategory');
  const hasSubCategory = headerOrder.some((e) => e === 'subCategory');
  const hasUpgradeType = headerOrder.some((e) => e === 'upgradeType');
  const hasDescription = headerOrder.some((e) => e === 'description');
  const hasModelsAllowed = headerOrder.some((e) => e === 'modelsAllowed');
  const hasUnitsAllowed = headerOrder.some((e) => e === 'unitsAllowed');
  const hasFreeUpgrades = headerOrder.some((e) => e === 'freeUpgrades');

  const error = !hasName || !hasPrice || !hasCostPrice;

  if (!hasName) {
    missingHeaders.push('Name');
  }
  if (!hasPrice) {
    missingHeaders.push('Price');
  }
  if (!hasCostPrice) {
    missingHeaders.push('Cost Price');
  }
  if (!hasMainCategory) {
    missingHeaders.push('Main Category');
  }
  if (!hasSubCategory) {
    missingHeaders.push('Sub Category');
  }
  if (!hasUpgradeType) {
    missingHeaders.push('Upgrade Type');
  }
  if (!hasDescription) {
    missingHeaders.push('Description');
  }
  if (!hasModelsAllowed) {
    missingHeaders.push('Models Allowed');
  }
  if (!hasUnitsAllowed) {
    missingHeaders.push('Units Allowed');
  }
  if (!hasFreeUpgrades) {
    missingHeaders.push('Free Upgrades');
  }

  if (error) {
    for (let [index, missingHeader] of missingHeaders.entries()) {
      if (index !== missingHeaders.length - 1) {
        errorMessage += missingHeader + ', ';
      } else {
        errorMessage += missingHeader;
      }
    }

    return { error: errorMessage, upgrades: [] };
  }

  const followingRows = thisFile.rows.slice(1);

  let errorValueMessage = '';

  followingRows.forEach((row: any, index: number) => {
    if (!row.cells[0].address.includes('A')) {
      errorValueMessage += `Row ${index + 1} is missing value in Name `;
      return;
    }
    if (!row.cells[1].address.includes('B')) {
      errorValueMessage += `Row ${index + 1} is missing value in Price `;
      return;
    }
    if (!row.cells[2].address.includes('C')) {
      errorValueMessage += `Row ${index + 1} is missing value in Cost Price `;
      return;
    }
    if (!row.cells[3].address.includes('D')) {
      errorValueMessage += `Row ${index + 1} is missing value in Main Category `;
      return;
    }
    if (!row.cells[4].address.includes('E')) {
      errorValueMessage += `Row ${index + 1} is missing value in Sub Category `;
      return;
    }
    if (!row.cells[5].address.includes('F')) {
      errorValueMessage += `Row ${index + 1} is missing value in Upgrade Type `;
      return;
    }
  });

  if (errorValueMessage) {
    return { error: errorValueMessage, upgrades: [] };
  }

  for (let row of thisFile.rows) {
    let newUpgrade: any = {};
    for (let i = 0; i < thisFile.rows[0].cells.length; ++i) {
      if (headerOrder[i] && headerOrder[i].length) {
        if (i <= 5) {
          newUpgrade[headerOrder[i]] = row.cells[i].value;
        } else {
          if (i === 6) {
            if (row.cells[i] && row.cells[i].address.includes('G')) {
              newUpgrade[headerOrder[i]] = row.cells[i].value;
            } else {
              let cell = row.cells.find((cell: any) => cell.address.includes('G'));
              if (cell) {
                newUpgrade[headerOrder[i]] = cell.value;
              } else {
                newUpgrade[headerOrder[i]] = '';
              }
            }
          }
          if (i === 7) {
            if (row.cells[i] && row.cells[i].address.includes('H')) {
              newUpgrade[headerOrder[i]] = row.cells[i].value;
            } else {
              let cell = row.cells.find((cell: any) => cell.address.includes('H'));
              if (cell) {
                newUpgrade[headerOrder[i]] = cell.value;
              }
            }
          }
          if (i === 8) {
            if (row.cells[i] && row.cells[i].address.includes('I')) {
              newUpgrade[headerOrder[i]] = row.cells[i].value;
            } else {
              let cell = row.cells.find((cell: any) => cell.address.includes('I'));
              if (cell) {
                newUpgrade[headerOrder[i]] = cell.value;
              }
            }
          }
          if (i === 9) {
            if (row.cells[i] && row.cells[i].address.includes('J')) {
              newUpgrade[headerOrder[i]] = row.cells[i].value;
            } else {
              let cell = row.cells.find((cell: any) => cell.address.includes('J'));
              if (cell) {
                newUpgrade[headerOrder[i]] = cell.value;
              }
            }
          }
        }
      } else {
        continue;
      }
    }

    if (Object.values(newUpgrade).every((val) => !val)) {
      continue;
    } else {
      upgrades.push(newUpgrade);
    }
  }

  return { error: '', upgrades: upgrades.slice(1) };
};
//

export const uniqueArrayOfObjects = (arr: any[], props: any[] = []) => [
  ...new Map(arr.map((entry) => [props.map((k) => entry[k]).join('|'), entry])).values(),
];

export const createDecorTablePdf = async (
  deal: IDeal,
  unit: IUnit,
  data: any[],
  categories: any,
  type: string,
  project: string,
  all: boolean
) => {
  if (!data.length) return;
  // Constants
  const padding = 20;
  const fontSize = 12;
  const realtorFontSize = 100;
  const textOffset = 5;
  let cellPadding = 5;
  const rowHeight = cellPadding * 6 + fontSize * 3 + textOffset;
  const headerHeight = cellPadding * 2 + fontSize + textOffset;
  const maxRowPerPage = 15;

  // Get text width
  const context = document.createElement('canvas').getContext('2d');
  context && (context.font = fontSize + 'px sans-serif');
  const realtorContext = document.createElement('canvas').getContext('2d');
  realtorContext && (realtorContext.font = realtorFontSize + 'px sans-serif');

  // Get Page Dimensions

  let headers = ['selected', 'component', 'selection', 'description', 'price'];

  const tableWidth = headers.reduce((prevValue, key) => prevValue + 100 + cellPadding * 2, 0);

  const pdf = await PDFDocument.create();
  const helveticaBold = await pdf.embedFont(StandardFonts.HelveticaBold);
  let helveticaFont = await pdf.embedFont(StandardFonts.Helvetica);

  // Embed images

  //const logoImage = await pdf.embedPng(logoBytes);

  let upgradeCategories = categories
    .filter((category: any) => category.mainCategory !== 'Miscellaneous')
    .sort((a: any, b: any) => {
      return mainCategories.indexOf(a.mainCategory) - mainCategories.indexOf(b.mainCategory);
    });
  let miscCategories = categories.find((category: any) => category.mainCategory === 'Miscellaneous');

  pdf.addPage(PageSizes.Legal); // Get page

  let page = pdf.getPages()[0];

  page.moveTo(padding + 10, page.getHeight() - padding); // Add padding
  let rowCount = 0;

  let pageY = page.getHeight();
  if (rowCount === 0) {
    page.moveDown(headerHeight);
    page.drawText('SCHEDULE A', {
      x: page.getX() + cellPadding + 220,
      y: page.getY(),
      size: fontSize,
      lineHeight: fontSize / 2,
      font: helveticaBold,
    });

    page.moveDown(headerHeight);
    page.drawText("PURCHASER'S UPGRADES & EXTRAS FORM", {
      x: page.getX() + cellPadding + 140,
      y: page.getY(),
      size: fontSize,
      lineHeight: fontSize / 2,
      font: helveticaBold,
    });

    page.moveDown(headerHeight);

    page.drawText(`Suite`, {
      x: page.getX() + cellPadding + 180,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 8,
      lineHeight: fontSize / 2,
    });

    page.drawText(`${unit.suite}`, {
      x: page.getX() + cellPadding + 200,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 8,
      lineHeight: fontSize / 2,
      font: helveticaBold,
    });

    page.drawText(`Unit`, {
      x: page.getX() + cellPadding + 250,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 8,
      lineHeight: fontSize / 2,
    });

    page.drawText(`${unit.unit}`, {
      x: page.getX() + cellPadding + 267,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 8,
      lineHeight: fontSize / 2,
      font: helveticaBold,
    });

    page.drawText(`Level`, {
      x: page.getX() + cellPadding + 310,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 8,
      lineHeight: fontSize / 2,
    });

    page.drawText(`${unit.level}`, {
      x: page.getX() + cellPadding + 332,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 8,
      lineHeight: fontSize / 2,
      font: helveticaBold,
    });

    page.moveDown(headerHeight);

    page.drawText(`SUITE`, {
      x: page.getWidth() - page.getWidth() / 2 - 200,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 8,
      lineHeight: fontSize / 2,
    });

    page.drawText(`${unit.suite}`, {
      x: page.getWidth() - page.getWidth() / 2 - 170,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: 16,
      lineHeight: fontSize / 2,
      font: helveticaBold,
      color: rgb(0.95, 0.1, 0.1),
    });
    pageY = page.getY();
  }

  // Y value for next page

  for (let [index, category] of upgradeCategories.entries()) {
    let page = pdf.getPages()[0];

    if ((pageY < 300 && category.subCategories.length > 2) || pageY < 200) {
      page = pdf.addPage(PageSizes.Legal); // Get page
      page = pdf.getPages()[pdf.getPages().length - 1];
      pageY = page.getHeight();
      page.moveTo(padding + 10, pageY - padding); // Add padding
    } else {
      page = pdf.getPages()[pdf.getPages().length - 1];
      page.moveTo(padding + 10, page.getY() - padding); // Add padding
    }

    // Add Main Category
    page.moveDown(headerHeight);

    page.drawRectangle({
      borderWidth: 1,
      borderColor: rgb(0, 0, 0),
      height: headerHeight,
      width: (100 + cellPadding * 2) * 5,
      color: rgb(1, 1, 1),
    });
    page.drawText(category.mainCategory, {
      x: page.getX() + cellPadding + 240,
      y: page.getY() + headerHeight - fontSize - cellPadding,
      size: fontSize,
      lineHeight: fontSize / 2,
    });

    // Add headers
    page.moveDown(headerHeight);
    headers.forEach((header, index) => {
      let width = 100 + cellPadding * 2;
      if (header === 'component') {
        if (type === 'preview') {
          width = 210 + cellPadding * 2;
        }
      }

      if (header === 'selected') {
        if (type === 'preview') {
          width = 100 + cellPadding * 2;
        } else {
          width = 50 + cellPadding * 2;
        }
      }

      if (header === 'selection') {
        if (type === 'preview') {
          width = 0 + cellPadding * 2;
        } else {
          width = 50 + cellPadding * 2;
        }
      }

      if (header === 'description') {
        if (type === 'preview') {
          width = 210 + cellPadding * 2;
        } else {
          width = 200 + cellPadding * 2;
        }
      }

      if (type === 'preview' && (header === 'price' || header === 'selection')) {
        return;
      }

      page.drawRectangle({
        borderWidth: 1,
        borderColor: rgb(0, 0, 0),
        height: headerHeight,
        width: width,
        color: rgb(1, 1, 1),
      });
      page.drawText(header ? capitalizeFirstLetter(header) : '', {
        x: page.getX() + cellPadding,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        size: fontSize,
        lineHeight: fontSize / 2,
      });

      page.moveRight(width);
    });
    page.moveLeft(tableWidth);

    rowCount += category.subCategories.length + 2;

    let allCategories = category.subCategories.entries();

    if (project === '61f9934d3ace1c59fcb6c5de' && type === 'preview') {
      allCategories = category.subCategories
        .filter((subCategory: any) => {
          if (category.mainCategory === 'Suite' && (subCategory.component === 'Door Handles' || subCategory.component === 'Paint')) {
            return false;
          } else if (category.mainCategory === 'Kitchen' && subCategory.component === 'Pulls') {
            return false;
          } else if (
            subCategory.component === 'Faucet' ||
            subCategory.component === 'Shower Faucet Set' ||
            subCategory.component === 'Vanity Faucet'
          ) {
            return false;
          } else return true;
        })
        .entries();
    }

    for (const [i, subCategory] of allCategories) {
      page.moveDown(headerHeight);
      headers.forEach((header, index) => {
        let text = subCategory[header];

        if (header === 'price') {
          if (subCategory[header] === 'Included') {
            text = 'Included';
          } else if (subCategory[header]) {
            text = numToCurrencyDecimal.format(subCategory[header]);
          } else {
            text = '';
          }
        } else {
          text = subCategory[header].toString();
        }

        if (header === 'description') {
          if (subCategory.quantity > 1) {
            text = `${subCategory[header]} (x${subCategory.quantity})`;
          }
        }

        let width = 100 + cellPadding * 2;

        if (header === 'component') {
          if (type === 'preview') {
            width = 210 + cellPadding * 2;
          }
        }

        if (header === 'selected') {
          if (type === 'preview') {
            width = 100 + cellPadding * 2;
          } else {
            width = 50 + cellPadding * 2;
          }
        }

        if (header === 'selection') {
          if (type === 'preview') {
            width = 0 + cellPadding * 2;
          } else {
            width = 50 + cellPadding * 2;
          }
        }

        if (header === 'description') {
          if (type === 'preview') {
            width = 210 + cellPadding * 2;
          } else {
            width = 200 + cellPadding * 2;
          }
        }

        if (type === 'preview' && (header === 'price' || header === 'selection')) {
          return;
        }

        page.drawRectangle({
          borderWidth: 1,
          borderColor: rgb(0, 0, 0),
          height: headerHeight,
          width: width,
          color: rgb(1, 1, 1),
        });

        let textProperties = {
          x: page.getX() + cellPadding,
          y: text.length > 60 ? page.getY() + headerHeight - fontSize : page.getY() + headerHeight - fontSize - cellPadding,
          size: text.length > 60 ? 6 : 7,
          lineHeight: fontSize / 2,
        };

        if (header === 'description' && text.length > 60) {
          text = wrapText(text, 300, helveticaFont, 9);
          text = text.text;
          if (text.lineHeight) {
            textProperties.lineHeight = text.lineHeight;
            textProperties.y = textProperties.y + 10;
          }
        }
        if (header === 'component' && text.length > 30) {
          text = wrapText(text, 125, helveticaFont, 9);
          text = text.text;
          if (text.lineHeight) {
            textProperties.lineHeight = text.lineHeight;
            textProperties.y = textProperties.y + 10;
          }
        }

        page.drawText(text, textProperties);
        page.moveRight(width);
      });
      page.moveLeft(tableWidth);
    }

    page.moveDown(headerHeight);

    page.drawRectangle({
      borderWidth: 1,
      borderColor: rgb(0, 0, 0),
      height: headerHeight,
      width: (100 + cellPadding * 2) * 5,
      color: rgb(1, 1, 1),
    });

    if (type !== 'preview') {
      page.drawText(
        `Subtotal: ${numToCurrencyDecimal.format(
          category.subCategories.reduce((a: any, b: any) => {
            if (typeof a === 'number' && typeof b.price === 'number') {
              return a + b.price;
            } else {
              return a;
            }
          }, 0)
        )}`,
        {
          x: page.getX() + cellPadding + 420,
          y: page.getY() + headerHeight - fontSize - cellPadding,
          size: fontSize,
          font: helveticaBold,
          lineHeight: fontSize / 2,
        }
      );
    }

    pageY = page.getY();
  }

  if (type === 'amendment') {
    let latestPage = pdf.getPages()[pdf.getPages().length - 1];

    if (pageY < 200) {
      page = pdf.addPage(PageSizes.Legal); // Get page
      page = pdf.getPages()[pdf.getPages().length - 1];
      latestPage = pdf.getPages()[pdf.getPages().length - 1];
      latestPage.moveTo(padding + 10, latestPage.getHeight() - padding); // Add padding
    }
    latestPage.moveDown(5);
    let allSubTotals = upgradeCategories.reduce((a: any, b: any) => {
      let subCategorySum = b.subCategories.reduce((a: any, b: any) => {
        if (typeof a === 'number' && typeof b.price === 'number') {
          return a + b.price;
        } else {
          return a;
        }
      }, 0);
      return a + subCategorySum;
    }, 0);

    latestPage.drawText(`Subtotal`, {
      x: page.getX() + (315 + cellPadding * 4),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });

    latestPage.drawText(`${numToCurrencyDecimal.format(allSubTotals)}`, {
      x: page.getX() + (420 + cellPadding * 5),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });

    latestPage.moveDown(15);
    let coordPrice = 0;
    let creditPrice = 0;
    if (miscCategories) {
      let coord = miscCategories.subCategories.find((subCategory: any) => subCategory.component === 'Coordination');
      let credit = miscCategories.subCategories.find((subCategory: any) => subCategory.component === 'Credit');

      if (coord) {
        latestPage.drawText(`Coordination Fee`, {
          x: page.getX() + (315 + cellPadding * 4),
          y: latestPage.getY() - fontSize - cellPadding,
          size: fontSize,
          font: helveticaBold,
          lineHeight: fontSize / 2,
        });

        latestPage.drawText(`${numToCurrencyDecimal.format(coord.price)}`, {
          x: page.getX() + (420 + cellPadding * 5),
          y: latestPage.getY() - fontSize - cellPadding,
          size: fontSize,
          font: helveticaBold,
          lineHeight: fontSize / 2,
        });

        coordPrice = coord.price;

        latestPage.moveDown(15);
      }

      if (credit) {
        latestPage.drawText(`Credit Balance`, {
          x: page.getX() + (315 + cellPadding * 4),
          y: latestPage.getY() - fontSize - cellPadding,
          size: fontSize,
          font: helveticaBold,
          lineHeight: fontSize / 2,
        });

        latestPage.drawText(`${numToCurrencyDecimal.format(credit.price)}`, {
          x: page.getX() + (420 + cellPadding * 5),
          y: latestPage.getY() - fontSize - cellPadding,
          size: fontSize,
          font: helveticaBold,
          lineHeight: fontSize / 2,
        });

        creditPrice = credit.price;

        latestPage.moveDown(15);
      }
    }

    latestPage.drawLine({
      start: { x: page.getX() + (315 + cellPadding * 4), y: latestPage.getY() - 10 },
      end: { x: page.getX() + cellPadding + 545, y: latestPage.getY() - 10 },
      thickness: 2,
      color: rgb(0, 0, 0),
      opacity: 0.75,
    });

    latestPage.drawLine({
      start: { x: page.getX() + (315 + cellPadding * 4), y: latestPage.getY() - 15 },
      end: { x: page.getX() + cellPadding + 545, y: latestPage.getY() - 15 },
      thickness: 2,
      color: rgb(0, 0, 0),
      opacity: 0.75,
    });

    latestPage.moveDown(15);

    latestPage.drawText(`Total Net of HST`, {
      x: page.getX() + (315 + cellPadding * 4),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });

    latestPage.drawText(`${numToCurrencyDecimal.format(allSubTotals + coordPrice - creditPrice)}`, {
      x: page.getX() + (420 + cellPadding * 5),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });

    latestPage.moveDown(15);

    latestPage.drawText(`HST`, {
      x: page.getX() + (315 + cellPadding * 4),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });

    latestPage.drawText(`${numToCurrencyDecimal.format((allSubTotals + coordPrice - creditPrice) * 0.13)}`, {
      x: page.getX() + (420 + cellPadding * 5),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });

    latestPage.drawLine({
      start: { x: page.getX() + (315 + cellPadding * 4), y: latestPage.getY() - 23 },
      end: { x: page.getX() + cellPadding + 545, y: latestPage.getY() - 23 },
      thickness: 2,
      color: rgb(0, 0, 0),
      opacity: 0.75,
    });

    latestPage.moveDown(20);

    latestPage.drawText(`Total`, {
      x: page.getX() + (315 + cellPadding * 4),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });

    latestPage.drawText(`${numToCurrencyDecimal.format((allSubTotals + coordPrice - creditPrice) * 1.13)}`, {
      x: page.getX() + (420 + cellPadding * 5),
      y: latestPage.getY() - fontSize - cellPadding,
      size: fontSize,
      font: helveticaBold,
      lineHeight: fontSize / 2,
    });
    latestPage.moveDown(15);
  }

  const pdfFile: any = await pdf.save();
  const blob = new Blob([pdfFile]);
  const file = new File([blob], `decorAmendment.pdf`, { type: 'application/pdf' });

  if (!all && (type === 'preview' || type === 'previewPrice' || type === 'previewPriceAll')) {
    download(pdfFile, type === 'preview' ? `${unit.suite} Decor Preview.pdf` : `${unit.suite} Decor Construction.pdf`, 'application/pdf');
  }

  return file;
};

const checkHighRisk = (country: string) => {
  if (
    country === 'Iran' ||
    country === 'Myanmar' ||
    country === 'Islamic State' ||
    country === 'Afghanistan' ||
    country === 'Ukraine' ||
    country === 'Russia'
  ) {
    return true;
  } else return false;
};

export const downloadDraft = (data: any, project?: IProject | null) => {

  const groupByKey = (data: any, key: any) =>
    Object.values(
      data.reduce((res: any, item: any) => {
        const value = item[key];
        const existing = res[value] || { [key]: value, data: [] };
        return {
          ...res,
          [value]: {
            ...existing,
            data: [...existing.data, item],
          },
        };
      }, {})
    );

  const groupByKeys = (keys: any) => (array: any) =>
    array.reduce((objectsByKeyValue: any, obj: any) => {
      const value = keys.map((key: any) => (key === 'bathroom' ? `${obj[key]}W` : obj[key])).join('+');
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
      return objectsByKeyValue;
    }, {});

  let groupedData = groupByKey(data.draft, 'status');

  let groupedDataStatusTotals = groupedData.map((grouped: any) => {
    const validUnitPrices = grouped.data.map((unit: any) => unit.basePrice);
    const mid = Math.floor(validUnitPrices.length / 2);
    const nums = [...validUnitPrices].sort((a, b) => a - b);
    let statusName = unitStatusTitles.find((status: any) => status.code === grouped.status);
    return {
      status: statusName ? statusName.name : grouped.status,
      count: `${grouped.data.length} (${((grouped.data.length / data.draft.length) * 100).toFixed(2)}%)`,
      size: `${numberWithCommas(grouped.data.reduce((a: any, b: any) => a + b.size, 0))} (${(
        (grouped.data.reduce((a: any, b: any) => a + b.size, 0) / data.draft.reduce((a: any, b: any) => a + parseInt(b.size), 0)) *
        100
      ).toFixed(1)}%)`,
      averagePrice: `${numToCurrency.format(grouped.data.reduce((a: any, b: any) => a + b.basePrice, 0) / grouped.data.length)}`,
      revenue: `${numToCurrency.format(grouped.data.reduce((a: any, b: any) => a + b.basePrice, 0))} (${(
        (grouped.data.reduce((a: any, b: any) => a + b.basePrice, 0) /
          data.draft.reduce((a: any, b: any) => a + parseInt(b.basePrice), 0)) *
        100
      ).toFixed(1)}%)`,
      revenueNet: numToCurrency.format(grouped.data.reduce((a: any, b: any) => a + netHST(b.basePrice, project), 0)),
      ppsf: numToCurrency.format(
        grouped.data.reduce((a: any, b: any) => a + b.basePrice, 0) / grouped.data.reduce((a: any, b: any) => a + b.size, 0)
      ),
      ppsfNet: numToCurrency.format(
        grouped.data.reduce((a: any, b: any) => a + netHST(b.basePrice, project), 0) / grouped.data.reduce((a: any, b: any) => a + b.size, 0)
      ),
      lowestPrice: numToCurrency.format(Math.min(...validUnitPrices)),
      highestPrice: numToCurrency.format(Math.max(...validUnitPrices)),
      median: validUnitPrices.length % 2 !== 0 ? numToCurrency.format(nums[mid]) : numToCurrency.format((nums[mid - 1] + nums[mid]) / 2),
      averageSize: `${(grouped.data.reduce((a: any, b: any) => a + parseInt(b.size), 0) / grouped.data.length).toFixed(2)}`,
      outdoorSize: `${numberWithCommas(grouped.data.reduce((a: any, b: any) => a + getOutdoorSize(b.outdoor), 0))}`,
      averageOutdoorSize: `${numberWithCommas(data.draft.reduce((a: any, b: any) => a + getOutdoorSize(b.outdoor), 0) / grouped.data.length)}`,
    };
  });

  let groupedDataTotals = {
    status: 'Total',
    count: `${data.draft.length}`,
    size: `${numberWithCommas(data.draft.reduce((a: any, b: any) => a + parseInt(b.size), 0))}`,
    revenue: `${numToCurrency.format(data.draft.reduce((acc: any, o: any) => acc + o?.basePrice, 0))}`,
    averagePrice: numToCurrency.format(data.draft.reduce((acc: any, o: any) => acc + o?.basePrice, 0) / data.draft.length),
    revenueNet: `${numToCurrency.format(data.draft.reduce((acc: any, o: any) => acc + netHST(o?.basePrice, project), 0))}`,
    ppsf: numToCurrency.format(
      data.draft.reduce((acc: any, o: any) => acc + o?.basePrice, 0) / data.draft.reduce((acc: any, o: any) => acc + o?.size, 0)
    ),
    ppsfNet: numToCurrency.format(
      data.draft.reduce((acc: any, o: any) => acc + netHST(o?.basePrice, project), 0) / data.draft.reduce((acc: any, o: any) => acc + o?.size, 0)
    ),
    averageSize: `${data.draft.reduce((a: any, b: any) => a + parseInt(b.size), 0) / data.draft.length}`,
    outdoorSize: `${numberWithCommas(data.draft.reduce((a: any, b: any) => a + getOutdoorSize(b.outdoor), 0))}`,
    averageOutdoorSize: `${numberWithCommas(data.draft.reduce((a: any, b: any) => a + getOutdoorSize(b.outdoor), 0) / data.draft.length)}`,
  };

  let summaryHeaders = [
    {
      label: 'Status',
      id: 'status',
    },
    {
      label: 'Number of Suites',
      id: 'count',
    },
    {
      label: 'Revenue',
      id: 'revenue',
    },
    {
      label: 'Average Price',
      id: 'averagePrice',
    },
    {
      label: 'PPSF',
      id: 'ppsf',
    },
    {
      label: 'Revenue Net',
      id: 'revenueNet',
    },
    {
      label: 'PPSF Net',
      id: 'ppsfNet',
    },
    {
      label: 'Size',
      id: 'size',
    },
    {
      label: 'Average Size',
      id: 'averageSize',
    },
    {
      label: 'Total Outdoor Size',
      id: 'outdoorSize',
    },
    {
      label: 'Average Outdoor Size',
      id: 'averageOutdoorSize',
    },
    {
      label: 'Lowest Price',
      id: 'lowestPrice',
    },
    {
      label: 'Highest Price',
      id: 'highestPrice',
    },
    {
      label: 'Median',
      id: 'median',
    },
  ];

  let summaryWidths = {
    status: 15,
    count: 15,
    size: 15,
    revenue: 15,
    revenueNet: 15,
    ppsf: 15,
    ppsfNet: 15,
    median: 15,
    lowestPrice: 15,
    highestPrice: 15,
    averagePrice: 15,
    averageSize: 15,
    outdoorSize: 15,
    averageOutdoorSize: 15,
  };

  let unitsTotal = [...groupedDataStatusTotals, groupedDataTotals];

  // Unit Type

  const groupByUnitandBathroom = groupByKeys(['unitType', 'bathroom']);
  let groupedUnitTypeData = groupByUnitandBathroom(data.draft);
  groupedUnitTypeData = Object.entries(groupedUnitTypeData).map((unit: any) => {
    return {
      unitType: unit[0],
      data: unit[1],
    };
  });

  let groupedDataUnitType = groupedUnitTypeData.map((grouped: any) => {
    const validUnitPrices = grouped.data.map((unit: any) => unit.basePrice);
    const mid = Math.floor(validUnitPrices.length / 2);
    const nums = [...validUnitPrices].sort((a, b) => a - b);
    return {
      unitType: grouped.unitType,
      totalRevenue: `${numToCurrency.format(grouped.data.reduce((acc: any, o: any) => acc + o?.basePrice, 0))}`,
      averagePrice: `${numToCurrency.format(grouped.data.reduce((acc: any, o: any) => acc + o?.basePrice, 0) / grouped.data.length)}`,
      averageSize: `${(grouped.data.reduce((acc: any, o: any) => acc + o?.size, 0) / grouped.data.length).toFixed(0)}`,
      averageOutdoorSize: `${(grouped.data.reduce((acc: any, o: any) => acc + getOutdoorSize(o?.outdoor), 0) / grouped.data.length).toFixed(
        0
      )}`,
      averagePpsf: `${numToCurrency.format(
        grouped.data.reduce((acc: any, o: any) => acc + o?.basePrice, 0) / grouped.data.reduce((acc: any, o: any) => acc + o?.size, 0)
      )}`,
      lowestPrice: numToCurrency.format(Math.min(...validUnitPrices)),
      highestPrice: numToCurrency.format(Math.max(...validUnitPrices)),
      median: validUnitPrices.length % 2 !== 0 ? numToCurrency.format(nums[mid]) : numToCurrency.format((nums[mid - 1] + nums[mid]) / 2),
      unitsTotal: `${grouped.data.length} (${((grouped.data.length / data.draft.length) * 100).toFixed(2)}%)`,
    };
  });

  let unitColumns = [
    {
      label: 'Unit Types',
      id: 'unitType',
    },
    {
      label: 'Total Revenue',
      id: 'totalRevenue',
    },
    {
      label: 'Average Price',
      id: 'averagePrice',
    },
    {
      label: 'Average Size',
      id: 'averageSize',
    },
    {
      label: 'Average Outdoor Size',
      id: 'averageOutdoorSize',
    },
    {
      label: 'Average PPSF',
      id: 'averagePpsf',
    },
    {
      label: 'Lowest Price',
      id: 'lowestPrice',
    },
    {
      label: 'Highest Price',
      id: 'highestPrice',
    },
    {
      label: 'Median',
      id: 'median',
    },
    {
      label: 'Units Total',
      id: 'unitsTotal',
    },
  ];

  let widths = {
    unitType: 15,
    levels: 15,
    outdoorType: 15,
    averagePrice: 15,
    averageSize: 15,
    averageOutdoorSize: 15,
    averagePpsf: 15,
    lowestPrice: 15,
    highestPrice: 15,
    median: 15,
    unitsTotal: 15,
  };

  // Models

  let groupedModelTypeData = groupByKey(data.draft, 'modelType');
  let groupedDataModelType = groupedModelTypeData.map((grouped: any) => {
    const validUnitPrices = grouped.data.map((unit: any) => unit.basePrice);
    const mid = Math.floor(validUnitPrices.length / 2);
    const nums = [...validUnitPrices].sort((a, b) => a - b);

    const levels = grouped.data.map((grouped: any) => parseInt(grouped.level, 10));

    const levelRanges = findRanges([...new Set(levels)]);

    return {
      modelType: grouped.modelType,
      levels: levelRanges,
      totalRevenue: `${numToCurrency.format(grouped.data.reduce((acc: any, o: any) => acc + o?.basePrice, 0))}`,
      averagePrice: `${numToCurrency.format(grouped.data.reduce((acc: any, o: any) => acc + o?.basePrice, 0) / grouped.data.length)}`,
      averageSize: `${(grouped.data.reduce((acc: any, o: any) => acc + o?.size, 0) / grouped.data.length).toFixed(0)}`,
      averageOutdoorSize: `${(grouped.data.reduce((acc: any, o: any) => acc + getOutdoorSize(o?.outdoor), 0) / grouped.data.length).toFixed(
        0
      )}`,
      averagePpsf: `${numToCurrency.format(
        grouped.data.reduce((acc: any, o: any) => acc + o?.basePrice, 0) / grouped.data.reduce((acc: any, o: any) => acc + o?.size, 0)
      )}`,
      lowestPrice: numToCurrency.format(Math.min(...validUnitPrices)),
      highestPrice: numToCurrency.format(Math.max(...validUnitPrices)),
      median: validUnitPrices.length % 2 !== 0 ? numToCurrency.format(nums[mid]) : numToCurrency.format((nums[mid - 1] + nums[mid]) / 2),
      unitsTotal: `${grouped.data.length} (${((grouped.data.length / data.draft.length) * 100).toFixed(2)}%)`,
    };
  });

  let modelColumns = [
    {
      label: 'Model Types',
      id: 'modelType',
    },
    {
      label: 'Levels',
      id: 'levels',
    },
    {
      label: 'Total Revenue',
      id: 'totalRevenue',
    },
    {
      label: 'Average Price',
      id: 'averagePrice',
    },
    {
      label: 'Average Size',
      id: 'averageSize',
    },
    {
      label: 'Average Outdoor Size',
      id: 'averageOutdoorSize',
    },
    {
      label: 'Average PPSF',
      id: 'averagePpsf',
    },
    {
      label: 'Lowest Price',
      id: 'lowestPrice',
    },
    {
      label: 'Highest Price',
      id: 'highestPrice',
    },
    {
      label: 'Median',
      id: 'median',
    },
    {
      label: 'Units Total',
      id: 'unitsTotal',
    },
  ];

  // Closing

  let closingColumns = [
    {
      label: 'Suite',
      id: 'suite',
    },
    {
      label: 'Status',
      id: 'status',
    },
    {
      label: 'Price',
      id: 'basePrice',
    },
    {
      label: 'PPSF',
      id: 'ppsf',
    },
    {
      label: 'Unit',
      id: 'unit',
    },
    {
      label: 'Level',
      id: 'level',
    },
    {
      label: 'Model Type',
      id: 'modelType',
    },
    {
      label: 'Unit Type',
      id: 'unitType',
    },
    {
      label: 'Size',
      id: 'size',
    },
    {
      label: 'Bathroom',
      id: 'bathroom',
    },
    {
      label: 'Exposure',
      id: 'exposure',
    },
    {
      label: 'Outdoor Type',
      id: 'outdoorType',
    },
    {
      label: 'Outdoor Size',
      id: 'outdoorSize',
    },
  ];

  let closingWidths = {
    suite: 15,
    outdoorType: 15,
    basePrice: 15,
    ppsf: 15,
    bathroom: 15,
    exposure: 15,
    level: 15,
    modelType: 15,
    outdoorSize: 15,
    size: 15,
    status: 15,
    unit: 15,
    unitType: 15,
  };

  let closing = data.draft.map((unit: IUnit) => {
    return {
      ...unit,
      ppsf: numToCurrency.format(unit.basePrice / unit.size),
      basePrice: numToCurrency.format(unit.basePrice),
      outdoorType: getOutdoorType(unit.outdoor),
      outdoorSize: getOutdoorSize(unit.outdoor),
    };
  });

  let excelData = [unitsTotal, closing, sortSuites(groupedDataUnitType, 'unitType'), sortSuites(groupedDataModelType, 'modelType')];
  let excelHeaders = [summaryHeaders, closingColumns, unitColumns, modelColumns];
  let excelWidths = [summaryWidths, closingWidths, widths, widths];
  let titles = ['Overall Summary', 'Closing', 'Unit Summary', 'Model Summary'];

  downloadExcel(excelData, excelHeaders, [], [excelWidths], titles, `${data.name} Draft.xlsx`);
};

function findRanges(numbers: any[]) {
  return [...numbers]
    .sort((a, b) => a - b)
    .reduce(
      (acc, x, i) => {
        if (i === 0) {
          acc.ranges.push(x);
          acc.rangeStart = x;
        } else {
          if (x === acc.last + 1) {
            acc.ranges[acc.ranges.length - 1] = acc.rangeStart + '-' + x;
          } else {
            acc.ranges.push(x);
            acc.rangeStart = x;
          }
        }
        acc.last = x;
        return acc;
      },
      { ranges: [] }
    )
    .ranges.join(', ');
}

export const setBackgroundColor = (status: string) => {
  if (status === 'Received') {
    return {
      background: 'rgb(230, 184, 183)',
    };
  } else if (status === 'Signed') {
    return {
      background: 'rgb(233 204 255)',
    };
  } else if (status === 'Verified') {
    return {
      background: 'rgb(0, 176, 240)',
    };
  } else if (status === 'Completed') {
    return {
      background: 'rgb(146, 208, 80)',
    };
  } else return;
};

export const drawCheckbox = (start: any, end: any, thickness: number, color: any, opacity: number) => {
  return {
    start: start,
    end: end,
    thickness: thickness,
    color: rgb(color.first, color.second, color.third),
    opacity: opacity,
  };
};

export const wrapText = (text: string, width: number, font: any, fontSize: number) => {
  // The PDF fonts can't measure (encode) line breaks or tabs, so wrap each line of the text on its own
  // and join them back with '\n' (drawText splits on it when a lineHeight is set).
  const paragraphs = text.replace(/\t/g, ' ').split(/\r\n|\r|\n/);
  let lineHeight = paragraphs.length > 1 ? 10 : 0;
  const wrappedParagraphs = paragraphs.map((paragraph) => {
    const words = paragraph.split(' ');
    let line = '';
    let result = '';
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const testWidth = font.widthOfTextAtSize(testLine, fontSize);
      if (testWidth > width) {
        result += line + '\n';
        line = words[n] + ' ';
        lineHeight = 10;
      } else {
        line = testLine;
      }
    }
    return result + line;
  });
  return {
    text: wrappedParagraphs.join('\n'),
    lineHeight: lineHeight,
  };
};

export const exportStack = (fileName: string, unitData: any, stackData: any, project: IProject) => {
  const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const workbook = new ExcelJS.Workbook(); // Create Workbook
  const worksheet = workbook.addWorksheet('Stack'); // Create Worksheet

  const divmodExcel = (n: number) => {
    const a = Math.floor(n / 26);
    const b = n % 26;

    return b === 0 ? [a - 1, b + 26] : [a, b];
  };

  const uppercaseAlphas = Array.from({ length: 26 }, (_, i) => String.fromCodePoint(i + 'A'.codePointAt(0)!));

  const toExcelCol = (n: number) => {
    const chars = [];

    let d;
    while (n > 0) {
      [n, d] = divmodExcel(n);
      chars.unshift(uppercaseAlphas[d - 1]);
    }
    return chars.join('');
  };

  let stackSort = stackData.sort((a: any, b: any) => b.stackRow - a.stackRow);
  let unitSort = [...unitData].sort((a: any, b: any) => b.stackRow - a.stackRow);
  let uniqueStackRow = [...new Set(stackData.map((item: any) => item.stackRow))]; // [ 'A', 'B']
  let uniqueUnitRow = [...new Set([...unitData].map((item: any) => item.stackRow))].reverse(); // [ 'A', 'B']

  let stackRowDifference: any = uniqueStackRow.reduce((acc: any, curr: any, i, src: any) => {
    if (i !== 0) acc.push(src[i - 1] - curr - 1);
    return acc;
  }, []);

  let unitRowDifference: any = uniqueUnitRow.reduce((acc: any, curr: any, i, src: any) => {
    if (i !== 0) acc.push(src[i - 1] - curr - 1);
    return acc;
  }, []);

  const allStatus: IStatus[] = [...project.status, ...unitStatusTitles];

  for (const stack of stackSort) {
    let rowIndex = uniqueStackRow.indexOf(stack.stackRow);
    let sum = 0;
    let rowValue = 1;
    for (let i = 0; i < rowIndex; i++) {
      sum += stackRowDifference[i];
    }
    if (rowIndex) {
      rowValue = rowIndex * 8 + rowIndex * 1 + sum * 6 + 1;
    }

    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).value = `${stack.unit}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).font = {
      color: { argb: 'ffffff', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '000000' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).value = `${stack.exposure}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).font = {
      color: { argb: '000000', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: unitTypesColour(stack)?.slice(1) },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).value = `${stack.unitType}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).font = {
      color: { argb: '000000', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: unitTypesColour(stack)?.slice(1) },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).value = `${stack.modelType}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).font = {
      color: { argb: '000000', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: unitTypesColour(stack)?.slice(1) },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).value = `${stack.bathroom}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).font = {
      color: { argb: '000000', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: unitTypesColour(stack)?.slice(1) },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).value = `${stack.size}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).font = {
      color: { argb: '000000', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: unitTypesColour(stack)?.slice(1) },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 6}`).value = `${getOutdoorSize(stack.outdoor)}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 6}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 6}`).font = {
      color: { argb: '000000', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 6}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: unitTypesColour(stack)?.slice(1) },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 6}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 7}`).value = `${stack.size + getOutdoorSize(stack.outdoor)}`;
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 7}`).alignment = { horizontal: 'center' };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 7}`).font = {
      color: { argb: '000000', theme: 1 },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 7}`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: unitTypesColour(stack)?.slice(1) },
    };
    worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 7}`).border = {
      left: { style: 'thick' },
      right: { style: 'thick' },
    };
  }

  for (const stack of unitSort) {
    let rowIndex = uniqueUnitRow.indexOf(stack.stackRow);
    let statusColor = allStatus.find((status: IStatus) => status.code === stack.status);
    let sum = 1;
    let rowValue = 0;
    for (let i = 0; i < rowIndex; i++) {
      sum += unitRowDifference[i];
    }

    if (rowIndex) {
      rowValue = rowIndex * 6 + sum * 8 + (sum - 1);
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).value = `${stack.suite} (${stack.status})`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).font = {
        color: { argb: 'ffffff', theme: 1 },
        bold: true,
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '000000' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).value = `Level ${stack.level}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).font = {
        color: { argb: '000000', theme: 1 },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 1}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).value = `${numToCurrency.format(stack.basePrice / stack.size)}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).font = {
        color: { argb: '000000', theme: 1 },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 2}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).value = `${numToCurrency.format(stack.basePrice)}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).font = {
        color: { argb: '000000', theme: 1 },
        bold: true,
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 3}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).value = `${
        stack.allocation ? stack.allocation.fullName : stack.tempAllocation ? stack.tempAllocation : ''
      }`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).font = {
        color: { argb: '000000', theme: 1 },
        bold: true,
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 4}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).value = `${stack.custom}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).font = {
        color: { argb: '000000', theme: 1 },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 5}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
        bottom: { style: 'thick' },
      };
    } else {
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 9}`).value = `${stack.suite} (${stack.status})`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 9}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 9}`).font = {
        color: { argb: 'ffffff', theme: 1 },
        bold: true,
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 9}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '000000' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 9}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 10}`).value = `Level ${stack.level}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 10}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 10}`).font = {
        color: { argb: '000000', theme: 1 },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 10}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 10}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 11}`).value = `${numToCurrency.format(stack.basePrice / stack.size)}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 11}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 11}`).font = {
        color: { argb: '000000', theme: 1 },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 11}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 11}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 12}`).value = `${numToCurrency.format(stack.basePrice)}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 12}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 12}`).font = {
        color: { argb: '000000', theme: 1 },
        bold: true,
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 12}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 12}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 13}`).value = `${
        stack.allocation ? stack.allocation.fullName : stack.tempAllocation ? stack.tempAllocation : ''
      }`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 13}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 13}`).font = {
        color: { argb: '000000', theme: 1 },
        bold: true,
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 13}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 13}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 14}`).value = `${stack.custom}`;
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 14}`).alignment = { horizontal: 'center' };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 14}`).font = {
        color: { argb: '000000', theme: 1 },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 14}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColor ? statusColor.color.slice(1) : 'ffffff' },
      };
      worksheet.getCell(`${toExcelCol(stack.stackCol)}${rowValue + 14}`).border = {
        left: { style: 'thick' },
        right: { style: 'thick' },
        bottom: { style: 'thick' },
      };
    }
  }

  worksheet.columns.forEach((column) => {
    column.width = 15;
  });

  workbook.xlsx.writeBuffer().then((file: any) => {
    const blob = new Blob([file], { type: EXCEL_TYPE });
    saveAs(blob, fileName);
  });
};

const sanitizeSheetName = (name: string, usedNames: Set<string>) => {
  let sanitized = name.replace(/[:\\/?*[\]]/g, ' ').trim() || 'Sheet';
  sanitized = sanitized.slice(0, 31);
  let candidate = sanitized;
  let suffix = 1;
  while (usedNames.has(candidate.toLowerCase())) {
    suffix += 1;
    const suffixText = ` (${suffix})`;
    candidate = `${sanitized.slice(0, 31 - suffixText.length)}${suffixText}`;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
};

const addPriceGridSheet = (
  workbook: ExcelJS.Workbook,
  sheetName: string,
  units: IUnit[],
  projectName: string,
  project?: IProject,
  includeAllocation: boolean = false,
  includeSummary: boolean = true,
  downloadedAt?: Date
) => {
  const worksheet = workbook.addWorksheet(sheetName); // Create Worksheet

  const thinBorder = {
    top: { style: 'thin' as const },
    left: { style: 'thin' as const },
    bottom: { style: 'thin' as const },
    right: { style: 'thin' as const },
  };

  const isNetPriceProject = !!project?.netHst;
  const basePriceLabel = isNetPriceProject ? 'Net Unit Price' : 'Base Price';
  const ppsfLabel = isNetPriceProject ? 'Net PPSF' : 'PPSF';

  const baseRowLabels = ['Suite Number', 'Model Type', 'Unit Type', 'Size'];
  const trailingRowLabels = ['Exposure', 'Ceiling Height', basePriceLabel, ppsfLabel, 'Parking Eligibility', ...(includeAllocation ? ['Allocation'] : [])];

  const allStatus: IStatus[] = [...((project?.status as any) || []), ...unitStatusTitles];
  const parkingOptions: any[] = (project?.options || []).filter(
    (option: any) => option.name && option.name.toLowerCase().includes('parking')
  );

  const levels = [...new Set(units.map((unit) => unit.level))].sort((a: any, b: any) =>
    b.toString().localeCompare(a.toString(), 'en', { numeric: true })
  );

  const maxSuites = levels.reduce((max: number, level: string) => {
    const count = units.filter((unit) => unit.level === level).length;
    return Math.max(max, count);
  }, 0);

  const totalColumns = maxSuites + 1; // label column + one column per suite

  worksheet.mergeCells(1, 1, 1, totalColumns);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = projectName;
  titleCell.font = { bold: true, underline: true, size: 14 };

  // When the sheet is downloaded, e.g. "09-21-2026 3:45 PM" - on the spacer row right under the title
  if (downloadedAt) {
    if (totalColumns > 1) worksheet.mergeCells(2, 1, 2, totalColumns);
    const downloadedCell = worksheet.getCell(2, 1);
    downloadedCell.value = format(downloadedAt, 'MM-dd-yyyy h:mm a');
    downloadedCell.alignment = { horizontal: 'left' };
  }

  let currentRow = 3;

  // Status legend - one row per status actually used by these units, cell background matching the status color.
  const legendStatuses = ([...new Set(units.map((unit) => unit.status))].filter(Boolean) as string[])
    .map((code) => allStatus.find((status) => status.code === code))
    .filter(Boolean) as IStatus[];

  if (legendStatuses.length) {
    worksheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const legendTitleCell = worksheet.getCell(currentRow, 1);
    legendTitleCell.value = 'Status Legend';
    legendTitleCell.font = { bold: true, color: { argb: 'ffffff' } };
    legendTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1a1a1a' } };
    currentRow += 1;

    // Lay the statuses out left to right, wrapping onto additional rows once they run out of columns.
    for (let i = 0; i < legendStatuses.length; i += totalColumns) {
      const rowStatuses = legendStatuses.slice(i, i + totalColumns);
      rowStatuses.forEach((status, colIndex) => {
        const legendCell = worksheet.getCell(currentRow, colIndex + 1);
        legendCell.value = status.name;
        legendCell.font = { bold: true };
        legendCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: status.color.replace('#', '') } };
        legendCell.border = thinBorder;
        legendCell.alignment = { horizontal: 'center' };
      });
      currentRow += 1;
    }

    currentRow += 1; // spacer row between legend and the first level table
  }

  levels.forEach((level: string) => {
    const levelUnits: IUnit[] = sortSuites(
      units.filter((unit) => unit.level === level),
      'suite'
    );

    worksheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const levelCell = worksheet.getCell(currentRow, 1);
    levelCell.value = `LEVEL (${level})`;
    levelCell.font = { bold: true, color: { argb: 'ffffff' } };
    levelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1a1a1a' } };
    currentRow += 1;

    // Each level can have a different set of outdoor types (Balcony, Terrace, etc.), so instead of
    // one generic "Outdoor Size" row, add one row per outdoor type actually present on this level.
    const outdoorTypesInLevel = [
      ...new Set(levelUnits.flatMap((unit: IUnit) => (unit.outdoor || []).map((o) => o.name).filter(Boolean))),
    ].sort();

    const levelRowLabels = [...baseRowLabels, ...outdoorTypesInLevel, ...trailingRowLabels];

    levelRowLabels.forEach((label) => {
      const row = worksheet.getRow(currentRow);
      const labelCell = row.getCell(1);
      labelCell.value = label;
      labelCell.font = { bold: true };
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f0f0f0' } };
      labelCell.border = thinBorder;

      levelUnits.forEach((unit: IUnit, index: number) => {
        const cell = row.getCell(index + 2);
        cell.alignment = { horizontal: 'center' };
        cell.border = thinBorder;

        // Highlight the entire suite's column with its status color.
        const unitStatus = allStatus.find((status) => status.code === unit.status);
        if (unitStatus) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: unitStatus.color.replace('#', '') } };
        }

        if (outdoorTypesInLevel.includes(label)) {
          const outdoorEntry = (unit.outdoor || []).find((o) => o.name === label);
          cell.value = outdoorEntry ? outdoorEntry.size : '-';
          return;
        }

        switch (label) {
          case 'Suite Number':
            cell.value = unit.suite;
            cell.font = { bold: true };
            break;
          case 'Model Type':
            cell.value = unit.modelType;
            break;
           case 'Unit Type':
            cell.value = unit.unitType;
            break;
          case 'Size':
            cell.value = unit.size;
            break;
          case 'Exposure':
            cell.value = unit.exposure;
            break;
          case 'Ceiling Height':
            cell.value = unit.ceilingHeight || '-';
            break;
          case basePriceLabel: {
            const displayPrice = isNetPriceProject ? netHST(unit.basePrice, project) : unit.basePrice;
            cell.value = displayPrice ? numToCurrency.format(displayPrice) : '-';
            break;
          }
          case ppsfLabel: {
            const ppsfPrice = isNetPriceProject ? netHST(unit.basePrice, project) : unit.basePrice;
            cell.value = unit.size ? numToCurrency.format(ppsfPrice / unit.size) : '-';
            break;
          }
          case 'Parking Eligibility': {
            const disallowedParkingOptions = parkingOptions.filter(
              (option) => !(option.allowedUnits || []).includes(unit.unitType)
            );
            if (disallowedParkingOptions.length) {
              cell.value = disallowedParkingOptions.map((option) => `No ${option.name}`).join('\n');
              cell.font = { bold: true, color: { argb: 'ff0000' } };
              cell.alignment = { horizontal: 'center', wrapText: true };
            }
            break;
          }
          case 'Allocation':
            cell.value = unit.allocation ? unit.allocation.fullName : unit.tempAllocation ? unit.tempAllocation : '';
            cell.alignment = { horizontal: 'center', wrapText: true };
            break;
        }
      });

      row.commit();
      currentRow += 1;
    });
    currentRow += 1; // spacer row between levels
  });

  if (includeSummary) {
    const validUnits = units.filter((unit) => unit.size && unit.basePrice);
    const totalRevenue = validUnits.reduce(
      (sum, unit) => sum + (isNetPriceProject ? netHST(unit.basePrice, project) : unit.basePrice),
      0
    );
    const totalSize = validUnits.reduce((sum, unit) => sum + unit.size, 0);
    const averagePrice = validUnits.length ? totalRevenue / validUnits.length : 0;
    const averagePPSF = totalRevenue / totalSize || 0;

    worksheet.mergeCells(currentRow, 1, currentRow, 2);
    const summaryTitleCell = worksheet.getCell(currentRow, 1);
    summaryTitleCell.value = 'Summary';
    summaryTitleCell.font = { bold: true, color: { argb: 'ffffff' } };
    summaryTitleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1a1a1a' } };
    currentRow += 1;

    const summaryRows = [
      { label: 'Total Number of Units', value: units.length },
      { label: 'Total Size (Sq.Ft.)', value: totalSize.toLocaleString() },
      { label: isNetPriceProject ? 'Total Net Revenue' : 'Total Revenue', value: numToCurrency.format(totalRevenue) },
      { label: isNetPriceProject ? 'Overall Average Net Price' : 'Overall Average Price', value: numToCurrency.format(averagePrice) },
      { label: isNetPriceProject ? 'Overall Average Net PPSF' : 'Overall Average PPSF', value: numToCurrency.format(averagePPSF) },
    ];

    summaryRows.forEach(({ label, value }) => {
      const row = worksheet.getRow(currentRow);
      const labelCell = row.getCell(1);
      labelCell.value = label;
      labelCell.font = { bold: true };
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'f0f0f0' } };
      labelCell.border = thinBorder;

      const valueCell = row.getCell(2);
      valueCell.value = value;
      valueCell.font = { bold: true };
      valueCell.alignment = { horizontal: 'center' };
      valueCell.border = thinBorder;

      row.commit();
      currentRow += 1;
    });
  }

  worksheet.getColumn(1).width = 18;
  for (let i = 2; i <= totalColumns; i++) {
    worksheet.getColumn(i).width = 12;
  }
};

export const exportIplGrid = (
  fileName: string,
  units: IUnit[],
  projectName: string,
  priceLists: IPriceList[] = [],
  project?: IProject,
  includeSummary: boolean = true
) => {
  const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const workbook = new ExcelJS.Workbook(); // Create Workbook
  const usedNames = new Set<string>();
  const downloadedAt = new Date();

  addPriceGridSheet(
    workbook,
    sanitizeSheetName('Price Grid', usedNames),
    units,
    projectName,
    project,
    true,
    includeSummary,
    downloadedAt
  );

  priceLists.forEach((priceList) => {
    addPriceGridSheet(
      workbook,
      sanitizeSheetName(priceList.name, usedNames),
      priceList.draft as unknown as IUnit[],
      projectName,
      project,
      false,
      includeSummary,
      downloadedAt
    );
  });

  workbook.xlsx.writeBuffer().then((file: any) => {
    const blob = new Blob([file], { type: EXCEL_TYPE });
    saveAs(blob, fileName);
  });
};

// Simple label/value sheet builder (dark header row, bordered cells) used by exportPrelaunchSummary.
// Kept separate from downloadExcel's own inline version rather than refactoring that function,
// since downloadExcel is called from many existing reports and isn't worth the regression risk here.
const addDataSheet = (workbook: ExcelJS.Workbook, sheetName: string, headers: any[], values: any[], widths: Record<string, number> = {}) => {
  const worksheet = workbook.addWorksheet(sheetName);
  const headerIds = headers.map((header: any) => header.id);

  worksheet.columns = headers.map((header: any) => ({ header: header.label, key: header.id, width: widths[header.id] || 15 }));

  // Bordered per-cell (header row + each actual data row) rather than via column.border, which
  // applies to the whole column and would keep drawing borders well past the last row with data.
  const thickBorder = {
    top: { style: 'thick' as const },
    left: { style: 'thick' as const },
    bottom: { style: 'thick' as const },
    right: { style: 'thick' as const },
  };

  const headerRow = worksheet.getRow(1);
  headerIds.forEach((header: any) => {
    headerRow.getCell(header).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2e453d' } };
    headerRow.getCell(header).font = { color: { argb: 'ffffff' } };
    headerRow.getCell(header).border = thickBorder;
  });

  values.forEach((value: any, index: number) => {
    const row = worksheet.getRow(index + 2);
    row.values = value;
    headerIds.forEach((header: any) => {
      row.getCell(header).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ffffff' } };
      row.getCell(header).alignment = { wrapText: true };
      row.getCell(header).border = thickBorder;
    });
    row.height = 20;
    row.commit();
  });
};

// Buckets units into fixed-size price ranges (auto min/max off the unit set), same math as
// unitTypeSummary/RangeSummary.tsx's on-screen table but with a fixed range size for export.
const buildPriceRangeData = (units: IUnit[], rangeSize: number, project?: IProject) => {
  if (!units.length) return [];

  const isNetPriceProject = !!project?.netHst;
  const priceOf = (unit: IUnit) => (isNetPriceProject ? netHST(unit.basePrice, project) : unit.basePrice);
  const salesStatusCodes = getSalesStatusCodes(project);

  const autoMin =
    Math.floor(units.reduce((min: number, unit: IUnit) => (priceOf(unit) < min ? priceOf(unit) : min), priceOf(units[0])) / rangeSize) *
    rangeSize;
  const autoMax = units.reduce((max: number, unit: IUnit) => (priceOf(unit) > max ? priceOf(unit) : max), priceOf(units[0]));

  let accumulativeUnit = 0;
  let accumulativeSold = 0;
  let accumulativeAvailable = 0;
  let accumulativeRevenue = 0;
  const totalRevenue = units.reduce((a, b) => a + priceOf(b), 0);

  const bucketCount = Math.ceil((autoMax - autoMin) / rangeSize) || 1;
  const rangeArray: any[] = [];

  for (let i = autoMin / rangeSize; i < autoMin / rangeSize + bucketCount; i++) {
    const bucketMin = i * rangeSize;
    const bucketMax = i * rangeSize + (rangeSize - 1);
    const unitsInBucket = units.filter((unit) => bucketMin <= priceOf(unit) && priceOf(unit) <= bucketMax);

    const total = unitsInBucket.length;
    const sold = unitsInBucket.filter((unit) => salesStatusCodes.includes(unit.status)).length;
    const available = unitsInBucket.filter((unit) => !salesStatusCodes.includes(unit.status)).length;
    const revenue = unitsInBucket.reduce((a, b) => a + priceOf(b), 0);

    accumulativeUnit += total;
    accumulativeSold += sold;
    accumulativeAvailable += available;
    accumulativeRevenue += revenue;

    rangeArray.push({
      range: `${numToCurrency.format(bucketMin)} to ${numToCurrency.format(bucketMax)}`,
      units: total,
      accumulativeUnit,
      sold,
      accumulativeSold,
      available,
      accumulativeAvailable: `${accumulativeAvailable} (${((accumulativeAvailable / units.length) * 100).toFixed(2)}%)`,
      revenue: numToCurrency.format(revenue),
      accumulativeRevenue: `${numToCurrency.format(accumulativeRevenue)} (${
        totalRevenue ? ((accumulativeRevenue / totalRevenue) * 100).toFixed(2) : '0.00'
      }%)`,
    });
  }

  return rangeArray;
};

// Combined "Prelaunch Summary" export: an IPL sheet (same layout as the Closing Report's IPL
// export) plus Unit Type Summary, Unit Type Summary Bathrooms, and a $50,000-bucketed Price Range
// sheet, all in one workbook. `units` is whatever draft (or current units) is active on screen.
// When `project.netHst` is true, the IPL and Price Range sheets show net-of-HST prices
// (matching the Closing Report's IPL export) — every other project's numbers are unaffected.
export const exportPrelaunchSummary = (
  fileName: string,
  projectName: string,
  units: IUnit[],
  unitTypeSummaryData: any[],
  unitTypeSummaryColumns: any[],
  bathroomSummaryData: any[],
  bathroomSummaryColumns: any[],
  columnWidths: Record<string, number>,
  project?: IProject,
  rangeSize: number = 50000
) => {
  const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const workbook = new ExcelJS.Workbook();

  addPriceGridSheet(workbook, 'IPL', units, projectName, project);
  addDataSheet(workbook, 'Unit Type Summary', unitTypeSummaryColumns, unitTypeSummaryData, columnWidths);
  addDataSheet(workbook, 'Unit Type Summary Bathrooms', bathroomSummaryColumns, bathroomSummaryData, columnWidths);

  const priceRangeColumns = [
    { label: 'Range', id: 'range' },
    { label: 'Unit Count', id: 'units' },
    { label: 'Accumulative Unit Count', id: 'accumulativeUnit' },
    { label: 'Available', id: 'available' },
    { label: 'Accumulative Available', id: 'accumulativeAvailable' },
    { label: 'Revenue', id: 'revenue' },
    { label: 'Accumulative Revenue', id: 'accumulativeRevenue' },
  ];
  addDataSheet(workbook, 'Price Range', priceRangeColumns, buildPriceRangeData(units, rangeSize, project));

  workbook.xlsx.writeBuffer().then((file: any) => {
    const blob = new Blob([file], { type: EXCEL_TYPE });
    saveAs(blob, fileName);
  });
};

export const columnToLetter = (column: number) => {
  var temp,
    letter = '';
  while (column > 0) {
    temp = (column - 1) % 26;
    letter = String.fromCharCode(temp + 65) + letter;
    column = (column - temp - 1) / 26;
  }
  return letter;
};

export const excelAutoWidth = (worksheet: any, minimalWidth = 10) => {
  worksheet.columns.forEach((column: any) => {
    let maxColumnLength = 0;
    column.eachCell({ includeEmpty: true }, (cell: any) => {
      maxColumnLength = Math.max(maxColumnLength, minimalWidth, cell.value ? cell.value.toString().length : 0);
    });
    column.width = maxColumnLength + 2;
  });
};

export const timeZoneDate = (date: Date) => {
  if (new Date(date).getTimezoneOffset() <= 0) {
    let hours = Math.abs(new Date(date).getTimezoneOffset()) / 60 + 5;
    return new Date(subHours(new Date(date), hours));
  } else {
    let hours = Math.abs(new Date(date).getTimezoneOffset()) / 60;
    if (hours <= 5) {
      return new Date(subHours(new Date(date), 5 - hours));
    } else {
      return new Date(addHours(new Date(date), hours - 5));
    }
  }
};

export const unitTypeText = (unitType: string) => {
  switch (unitType) {
    case 'JR1BR':
      return 'Junior 1 Bedroom';
    case '1BR':
      return 'One Bedroom';
    case '1BR(I)':
      return 'One Bedroom(I)';
    case '1BR+D':
      return 'One Bedroom + Den';
    case '1BR+S':
      return 'One Bedroom + Study';
    case '1BR+M':
      return 'One Bedroom + Media';
    case '1BR+F':
      return 'One Bedroom + Flex';
    case '1BR+D(I)':
      return 'One Bedroom + Den(I)';
    case '1BR+M(I)':
      return 'One Bedroom + Den(M)';
    case '2BR':
      return 'Two Bedroom';
    case '2BR(I)':
      return 'Two Bedroom(I)';
    case '2BR+D':
      return 'Two Bedroom + Den';
    case '2BR+S':
      return 'Two Bedroom + Study';
    case '2BR+M':
      return 'Two Bedroom + Media';
    case '2BR+F':
      return 'Two Bedroom + Flex';
    case 'JR2':
      return 'Junior Two Bedroom';
    case '2BR+D(I)':
      return 'Two Bedroom + Den(I)';
    case '2BR+M(I)':
      return 'Two Bedroom + Media(I)';
    case '3BR':
      return 'Three Bedroom';
    case '3BR(I)':
      return 'Three Bedroom(I)';
    case '3BR+D':
      return 'Three Bedroom + Den';
    case '3BR+M':
      return 'Three Bedroom + Media';
    case '3BR+F':
      return 'Three Bedroom + Flex';
    case 'TH':
      return 'Townhome';
    default:
      return unitType;
  }
};

export const downloadPackage = async (
  data: any[],
  headers: any[],
  fileName: string,
  modelPackage: any[],
  floorplans: any[],
  qrCodes: any[],
  project: any
) => {
  const pdf = await PDFDocument.create();

  if (modelPackage.length && modelPackage[0].packageGetUrl) {
    const modelPackagePdf = await fetch(modelPackage[0].packageGetUrl).then((res) => res.arrayBuffer());
    const documentPdf = await PDFDocument.load(modelPackagePdf);
    let copiedPages = await pdf.copyPages(documentPdf, documentPdf.getPageIndices());
    copiedPages.forEach((page: any) => pdf.addPage(page));
  }

  const helvetica = await pdf.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const helveticaOblique = await pdf.embedFont(StandardFonts.HelveticaOblique);
  const helveticaBoldOblique = await pdf.embedFont(StandardFonts.HelveticaBoldOblique);

  let projectLogoImage: any;
  let developerLogoImage: any;
  let developerRatio: any;
  let partnerLogoImage: any;
  let partnerRatio: any;
  let rdsLogoImage: any;
  if (!project.logoGetUrl) return;

  if (project.developerLogoGetUrl) {
    let developerUrl = project.developerLogoGetUrl.split('?X')[0];
    const developerLogoBytes = await fetch(developerUrl, { cache: 'no-cache' }).then((res) => res.arrayBuffer());
    if (developerUrl.includes('jpg')) {
      developerLogoImage = await pdf.embedJpg(developerLogoBytes);
    } else if (developerUrl.includes('png')) {
      developerLogoImage = await pdf.embedPng(developerLogoBytes);
    }
    developerRatio = (developerLogoImage.width / developerLogoImage.height) * 30;
  }

  if (project.partnerLogoGetUrl) {
    let partnerUrl = project.partnerLogoGetUrl.split('?X')[0];
    const partnerLogoBytes = await fetch(partnerUrl, { cache: 'no-cache' }).then((res) => res.arrayBuffer());
    if (partnerUrl.includes('jpg')) {
      partnerLogoImage = await pdf.embedJpg(partnerLogoBytes);
    } else if (partnerUrl.includes('png')) {
      partnerLogoImage = await pdf.embedPng(partnerLogoBytes);
    }
    partnerRatio = (partnerLogoImage.width / partnerLogoImage.height) * 30;
  }

  const rdsLogoBytes = await fetch('https://rdsappimages.s3.ca-central-1.amazonaws.com/assets/rds/logo.png', { cache: 'no-cache' }).then(
    (res) => res.arrayBuffer()
  );

  rdsLogoImage = await pdf.embedPng(rdsLogoBytes);
  let rdsRatio = (rdsLogoImage.width / rdsLogoImage.height) * 30;

  let logoUrl = project.logoGetUrl.split('?X')[0];

  const projectLogoBytes = await fetch(logoUrl, { cache: 'no-cache' }).then((res) => res.arrayBuffer());
  projectLogoImage = await pdf.embedJpg(projectLogoBytes);

  let logoRatio = (projectLogoImage.width / projectLogoImage.height) * 100;

  for (const [index, item] of data.entries()) {
    let letterSize = item.length <= 7 ? true : false;
    const page = letterSize ? pdf.addPage(PageSizes.Letter) : pdf.addPage(PageSizes.Legal);

    if (project.developerLogoGetUrl) {
      page.drawImage(developerLogoImage!, {
        x: 50,
        y: 55,
        height: 30,
        width: developerRatio,
      });
    }

    if (project.partnerLogoGetUrl) {
      page.drawImage(partnerLogoImage!, {
        x: 250,
        y: 55,
        height: 30,
        width: partnerRatio,
      });
    }

    if (rdsLogoImage) {
      page.drawImage(rdsLogoImage, {
        x: 527,
        y: 55,
        height: 30,
        width: rdsRatio,
      });
    }

    // Draw Border
    page.drawRectangle({
      x: 30,
      y: 30,
      width: 550,
      height: letterSize ? 725 : 950,
      borderWidth: 5,
      borderColor: rgb(0, 0, 0),
    });

    // Logo
    page.drawImage(projectLogoImage, {
      x: page.getWidth() / 2 - logoRatio / 2,
      y: letterSize ? 625 : 850,
      height: 100,
      width: logoRatio,
    });

    if (qrCodes[index]) {
      let qrCodeImage = await pdf.embedJpg(await qrCodes[index]);
      page.drawImage(qrCodeImage, {
        x: 50,
        y: letterSize ? 650 : 875,
        height: 75,
        width: 75,
      });
      page.drawText('Portal', {
        font: helveticaOblique,
        x: 82,
        y: 870,
        size: 4,
      });

      page.drawImage(qrCodeImage, {
        x: 485,
        y: letterSize ? 650 : 875,
        height: 75,
        width: 75,
      });
      page.drawText('Portal', {
        font: helveticaOblique,
        x: 517,
        y: 870,
        size: 4,
      });
    }

    // Title
    const titleWidth = helveticaBold.widthOfTextAtSize(modelPackage[0].title, 24);
    page.drawText(modelPackage[0].title, {
      x: page.getWidth() / 2 - titleWidth / 2,
      y: letterSize ? 595 : 815,
      size: 24,
      lineHeight: 10,
    });

    if (letterSize) {
      page.moveTo(50, 610 - (595 - 390 - (item.length + 1) * 15) / 2);
    } else {
      page.moveTo(50, 830 - (815 - 390 - (item.length + 1) * 15) / 2);
    }

    // Table
    const fontSize = 12;
    const textOffset = 2;
    const cellPadding = 2;
    const headerHeight = cellPadding * 2 + fontSize + textOffset;
    let tableWidth = 0;

    tableWidth = headers[index].reduce((prevValue: any, key: any) => {
      let longestText = [...item.map((item: any) => (item[key.id] ? unitTypeText(item[key.id]) : '')), key.label].reduce(
        (a, b) => (a.toString().length > b.toString().length ? a.toString() : b.toString()),
        ''
      );
      return prevValue + helveticaBold.widthOfTextAtSize(longestText, 8) + 10;
    }, 0);

    let difference = 511.288 - tableWidth;

    // Add headers

    headers[index].forEach((header: any) => {
      let longestText = [...item.map((item: any) => (item[header.id] ? unitTypeText(item[header.id]) : '')), header.label].reduce(
        (a, b) => (a.toString().length > b.toString().length ? a.toString() : b.toString()),
        ''
      );

      page.drawRectangle({
        x: page.getX(),
        y: page.getY(),
        height: 15,
        width: helveticaBold.widthOfTextAtSize(longestText, 8) + 10 + difference / headers[index].length,
        borderWidth: 0.1,
        borderColor: rgb(0, 0, 0),
      });
      page.drawText(header.label, {
        font: helveticaBold,
        x:
          page.getX() +
          5 +
          (helveticaBold.widthOfTextAtSize(longestText, 8) +
            difference / headers[0].length -
            helveticaBold.widthOfTextAtSize(header.label, 8)) /
            2,
        y: page.getY() + headerHeight - fontSize - cellPadding,
        size: 8,
        lineHeight: fontSize / 2,
      });
      page.moveRight(helveticaBold.widthOfTextAtSize(longestText, 8) + 10 + difference / headers[index].length);
    });
    page.moveLeft(tableWidth + difference);

    // Add rows
    for (const rowItem of item) {
      page.moveDown(15);
      headers[index].forEach((header: any) => {
        let longestText = [...item.map((item: any) => (item[header.id] ? unitTypeText(item[header.id]) : '')), header.label].reduce(
          (a, b) => (a.toString().length > b.toString().length ? a.toString() : b.toString()),
          ''
        );
        page.drawRectangle({
          x: page.getX(),
          y: page.getY(),
          borderWidth: 0.1,
          width: helveticaBold.widthOfTextAtSize(longestText, 8) + 10 + difference / headers[index].length,
          height: 15,
          borderColor: rgb(0, 0, 0),
        });
        let text = rowItem[header.id] ? rowItem[header.id].toString() : '';
        if (header.id === 'rental' || header.id === 'basePrice') {
          text = numToCurrency.format(rowItem[header.id]);
        }
        if (header.id === 'size' || header.id === 'outdoorSize') {
          text = rowItem[header.id] ? numberWithCommas(rowItem[header.id]) : '0';
        }
        text = unitTypeText(text);
        page.drawText(rowItem[header.id] ? text : '-', {
          x:
            page.getX() +
            5 +
            (helveticaBold.widthOfTextAtSize(longestText, 8) +
              difference / headers[index].length -
              helveticaBold.widthOfTextAtSize(text, 8)) /
              2,
          y: page.getY() + headerHeight - fontSize - cellPadding,
          size: 8,
        });
        page.moveRight(helveticaBold.widthOfTextAtSize(longestText, 8) + 10 + difference / headers[index].length);
      });
      page.moveLeft(tableWidth + difference);
    }

    page.moveTo(page.getX(), 190);

    for (let i = 0; i < 3; i++) {
      page.drawRectangle({
        x: page.getX(),
        y: page.getY(),
        borderWidth: 0.1,
        width: 511.288 / 3,
        height: 250,
        borderColor: rgb(0, 0, 0),
      });
      page.moveRight(511.288 / 3);
    }

    page.moveLeft(511.288);

    let yLocation = page.getY();

    for (const [numIndex, descriptions] of modelPackage[index].descriptions.slice(0, 3).entries()) {
      page.moveTo(page.getX(), yLocation);
      for (const description of descriptions) {
        if (description.type === 'paragraph') {
          for (const content of description.content) {
            let font = helvetica;
            if (content.marks.includes('bold')) {
              font = helveticaBold;
            }
            if (content.marks.includes('italic')) {
              font = helveticaOblique;
            }
            if (content.marks.includes('italic') && content.marks.includes('bold')) {
              font = helveticaBoldOblique;
            }
            const textWidth = font.widthOfTextAtSize(content.text, 7);

            if (content.marks.includes('underline')) {
              page.drawLine({
                start: {
                  x: page.getX() + 511.288 / 3 / 2 - textWidth / 2 + (numIndex * 511.288) / 3,
                  y: page.getY() + 200 - (150 - descriptions.length * 10) / 2,
                },
                end: {
                  x: page.getX() + 511.288 / 3 / 2 + textWidth / 2 + (numIndex * 511.288) / 3,
                  y: page.getY() + 200 - (150 - descriptions.length * 10) / 2,
                },
                thickness: 0.1,
                color: rgb(0, 0, 0),
              });
            }
            if (content.marks.includes('strike')) {
              page.drawLine({
                start: {
                  x: page.getX() + 511.288 / 3 / 2 - textWidth / 2 + (numIndex * 511.288) / 3,
                  y: page.getY() + 202 - (150 - descriptions.length * 10) / 2,
                },
                end: {
                  x: page.getX() + 511.288 / 3 / 2 + textWidth / 2 + (numIndex * 511.288) / 3,
                  y: page.getY() + 202 - (150 - descriptions.length * 10) / 2,
                },
                thickness: 0.1,
                color: rgb(0, 0, 0),
              });
            }
            page.drawText(content.text, {
              x: page.getX() + 511.288 / 3 / 2 - textWidth / 2 + (numIndex * 511.288) / 3,
              y: page.getY() + 200 - (150 - descriptions.length * 10) / 2,
              font: font,
              size: 7,
              lineHeight: 10,
            });
            page.moveDown(10);
          }
        } else if (description.type === 'bulletList') {
          for (const content of description.content) {
            let font = helvetica;
            if (content.marks.includes('bold')) {
              font = helveticaBold;
            }
            if (content.marks.includes('italic')) {
              font = helveticaOblique;
            }
            if (content.marks.includes('italic') && content.marks.includes('bold')) {
              font = helveticaBoldOblique;
            }
            const textWidth = font.widthOfTextAtSize(content.text, 7);

            if (content.marks.includes('underline')) {
              page.drawLine({
                start: { x: page.getX() + 511.288 / 3 / 2 - textWidth / 2, y: page.getY() + 200 - (descriptions.length * 10) / 2 },
                end: { x: page.getX() + 511.288 / 3 / 2 + textWidth / 2, y: page.getY() + 200 - (descriptions.length * 10) / 2 },
                thickness: 0.1,
                color: rgb(0, 0, 0),
              });
            }
            if (content.marks.includes('strike')) {
              page.drawLine({
                start: { x: page.getX() + 511.288 / 3 / 2 - textWidth / 2, y: page.getY() + 202 - (descriptions.length * 10) / 2 },
                end: { x: page.getX() + 511.288 / 3 / 2 + textWidth / 2, y: page.getY() + 202 - (descriptions.length * 10) / 2 },
                thickness: 0.1,
                color: rgb(0, 0, 0),
              });
            }
            page.drawText(content.text, {
              x: page.getX() + 511.288 / 3 / 2 - textWidth / 2,
              y: page.getY() + 200 - (200 - descriptions.length * 10) / 2,
              font: font,
              size: 7,
              lineHeight: 10,
            });
            page.moveDown(10);
          }
        }
      }
    }

    //Disclaimer

    page.moveTo(page.getX(), 175);

    for (const description of modelPackage[index].descriptions[3]) {
      for (const content of description.content) {
        let font = helvetica;
        if (content.marks.includes('bold')) {
          font = helveticaBold;
        }
        const textWidth = font.widthOfTextAtSize(content.text, 7);

        if (content.marks.includes('underline')) {
          page.drawLine({
            start: {
              x: page.getWidth() / 2 - textWidth / 2,
              y: page.getY(),
            },
            end: {
              x: page.getWidth() / 2 - textWidth / 2,
              y: page.getY(),
            },
            thickness: 0.1,
            color: rgb(0, 0, 0),
          });
        }
        if (content.marks.includes('strike')) {
          page.drawLine({
            start: {
              x: page.getWidth() / 2 - textWidth / 2,
              y: page.getY(),
            },
            end: {
              x: page.getWidth() / 2 - textWidth / 2,
              y: page.getY(),
            },
            thickness: 0.1,
            color: rgb(0, 0, 0),
          });
        }
        page.drawText(content.text, {
          x: page.getWidth() / 2 - textWidth / 2,
          y: page.getY(),
          size: 7,
          lineHeight: 10,
        });
        page.moveDown(10);
      }
    }
  }

  //Floorplans

  for (const floorplan of floorplans) {
    let floorplanDoc = await PDFDocument.create();
    const floorplanImage = await fetch(floorplan).then((res) => res.arrayBuffer());
    if (floorplan.includes('pdf')) {
      floorplanDoc = await PDFDocument.load(floorplanImage);
    } else {
      const jpgImage = await floorplanDoc.embedJpg(floorplanImage);
      if (jpgImage.width > jpgImage.height) {
        const page = floorplanDoc.addPage([PageSizes.Letter[1], PageSizes.Letter[0]]);
        page.setSize(792, 612);
        page.drawImage(jpgImage, {
          width: 792,
          height: 612,
        });
      } else {
        const page = floorplanDoc.addPage(PageSizes.Letter);
        page.drawImage(jpgImage, {
          width: 612,
          height: 792,
        });
      }
    }
    let copiedPages = await pdf.copyPages(floorplanDoc, floorplanDoc.getPageIndices());
    await copiedPages.forEach((page: any) => pdf.addPage(page));
  }

  const pdfFile = await pdf.save();
  download(pdfFile, fileName, 'application/pdf');
};

// Draws one worksheet's pages (header, unit info or wishlist choices, purchaser/realtor info, options,
// notes, then one page per purchaser ID) into an already-created PDFDocument, always
// starting on a fresh page. Shared by the single-worksheet export (unit and wishlist worksheets) and the
// combined "all of a realtor's worksheets" export so both use the exact same layout.
const drawWorksheetPages = async (pdf: any, helvetica: any, helveticaBold: any, worksheet: any, project: IProject) => {
  const PAGE_WIDTH = PageSizes.Letter[0];
  const PAGE_HEIGHT = PageSizes.Letter[1];
  const MARGIN = 50;
  const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
  const ACCENT = hexToPdfRgb('#2e453d');
  const MUTED = hexToPdfRgb('#6a6a67');
  const BORDER = hexToPdfRgb('#e4e4e0');
  const STRIPE = hexToPdfRgb('#f1efe9');
  const TEXT = rgb(0.1, 0.1, 0.1);

  let page = pdf.addPage(PageSizes.Letter);
  let y = PAGE_HEIGHT - MARGIN;

  const ensureSpace = (needed: number) => {
    if (y - needed < MARGIN) {
      page = pdf.addPage(PageSizes.Letter);
      y = PAGE_HEIGHT - MARGIN;
    }
  };

  const drawCentered = (text: string, size: number, font: any, color: any) => {
    const textWidth = font.widthOfTextAtSize(text, size);
    page.drawText(text, { x: PAGE_WIDTH / 2 - textWidth / 2, y, size, font, color });
  };

  const drawSectionTitle = (title: string) => {
    // Reserve room for the title plus a bit of whatever follows it, so a title never ends up
    // alone at the bottom of a page with its content starting fresh on the next one.
    ensureSpace(76);
    page.drawText(title.toUpperCase(), { x: MARGIN, y, size: 11, font: helveticaBold, color: ACCENT });
    y -= 6;
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_WIDTH - MARGIN, y }, thickness: 0.75, color: BORDER });
    y -= 18;
  };

  const drawFieldsGrid = (fields: { label: string; value: any }[]) => {
    const colWidth = CONTENT_WIDTH / 2;
    for (let i = 0; i < fields.length; i += 2) {
      ensureSpace(32);
      fields.slice(i, i + 2).forEach((field, colIndex) => {
        const x = MARGIN + colIndex * colWidth;
        page.drawText(field.label.toUpperCase(), { x, y, size: 7, font: helveticaBold, color: MUTED });
        page.drawText(field.value ? String(field.value) : '-', { x, y: y - 13, size: 10, font: helveticaBold, color: TEXT });
      });
      y -= 32;
    }
    y -= 6;
  };

  const drawTable = (headers: string[], colWidths: number[], rows: string[][]) => {
    const headerHeight = 20;
    const rowHeight = 18;
    const bodyHeight = rows.length ? rows.length * rowHeight : 22;
    const tableHeight = headerHeight + bodyHeight;
    const pageUsableHeight = PAGE_HEIGHT - MARGIN * 2;

    // If the whole table fits on a single page, force a page break before it (rather than
    // per-row) so it never starts on one page and gets cut off partway onto the next. Only a
    // table taller than a full page is allowed to spill across pages, repeating its header.
    ensureSpace(Math.min(tableHeight, pageUsableHeight));

    const drawHeader = () => {
      let x = MARGIN;
      headers.forEach((header, i) => {
        page.drawRectangle({ x, y: y - 14, width: colWidths[i], height: headerHeight, color: ACCENT });
        page.drawText(header, { x: x + 6, y: y - 9, size: 8, font: helveticaBold, color: rgb(1, 1, 1) });
        x += colWidths[i];
      });
      y -= headerHeight;
    };

    drawHeader();

    if (!rows.length) {
      page.drawText('None', { x: MARGIN + 6, y: y - 12, size: 9, font: helvetica, color: MUTED });
      y -= 22 + 12;
      return;
    }

    rows.forEach((row, rowIndex) => {
      if (y - rowHeight < MARGIN) {
        page = pdf.addPage(PageSizes.Letter);
        y = PAGE_HEIGHT - MARGIN;
        drawHeader();
      }
      let x = MARGIN;
      const bgColor = rowIndex % 2 === 0 ? rgb(1, 1, 1) : STRIPE;
      row.forEach((cell, i) => {
        page.drawRectangle({ x, y: y - 14, width: colWidths[i], height: rowHeight, color: bgColor });
        page.drawText(cell || '-', { x: x + 6, y: y - 10, size: 8, font: helvetica, color: TEXT });
        x += colWidths[i];
      });
      y -= rowHeight;
    });
    y -= 12;
  };

  // Header: project logo (top center) + project name below it.
  if (project.logoGetUrl) {
    try {
      const logoUrl = project.logoGetUrl.split('?X')[0];
      const logoBytes = await fetch(logoUrl, { cache: 'no-cache' }).then((res) => res.arrayBuffer());
      const logoImage = logoUrl.toLowerCase().includes('.png') ? await pdf.embedPng(logoBytes) : await pdf.embedJpg(logoBytes);
      const maxLogoHeight = 70;
      const maxLogoWidth = 220;
      const scale = Math.min(maxLogoHeight / logoImage.height, maxLogoWidth / logoImage.width);
      const logoWidth = logoImage.width * scale;
      const logoHeight = logoImage.height * scale;
      page.drawImage(logoImage, { x: PAGE_WIDTH / 2 - logoWidth / 2, y: y - logoHeight, width: logoWidth, height: logoHeight });
      y -= logoHeight + 12;
    } catch (err) {
      // Continue without the logo rather than failing the whole export.
    }
  }

  drawCentered(project.name || '', 18, helveticaBold, ACCENT);
  y -= 20;
  drawCentered(worksheet.wishlist ? 'Wishlist Worksheet' : 'Worksheet', 11, helvetica, MUTED);
  y -= 20;
  page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_WIDTH - MARGIN, y }, thickness: 1, color: BORDER });
  y -= 25;

  // Suite Number
  ensureSpace(24);
  page.drawText(`Suite Number: ${worksheet.unit ? worksheet.unit.suite : 'N/A (Wishlist)'}`, {
    x: MARGIN,
    y,
    size: 12,
    font: helveticaBold,
    color: TEXT,
  });
  y -= 30;

  // Unit Information (unit worksheets). Prices show net of HST on netHst projects, like the worksheet pages.
  if (worksheet.unit) {
    const netLabel = project.netHst ? ' (Net)' : '';
    const worksheetPrice = (price: number) =>
      price != null ? numToCurrency.format(project.netHst ? netHST(price, project) : price) : '';
    drawSectionTitle('Unit Information');
    drawFieldsGrid([
      { label: 'Unit Type', value: worksheet.unit.unitType },
      { label: 'Model Type', value: worksheet.unit.modelType },
      { label: 'Level', value: worksheet.unit.level },
      { label: 'Exposure', value: worksheet.unit.exposure },
      { label: 'Bathroom', value: worksheet.unit.bathroom },
      { label: 'Size', value: worksheet.unit.size },
      { label: `Unit Price${netLabel}`, value: worksheetPrice(worksheet.unit.basePrice) },
      { label: `Worksheet Price${netLabel}`, value: worksheetPrice(worksheet.worksheetBasePrice) },
      { label: 'Status', value: worksheet.status ? worksheet.status.charAt(0).toUpperCase() + worksheet.status.slice(1) : '' },
      { label: 'Created', value: worksheet.createdAt ? convertAllDates(worksheet.createdAt, 'PPpp') : '' },
    ]);
  }

  // Purchaser Information
  drawSectionTitle('Purchaser Information');
  (worksheet.purchasers || []).forEach((purchaser: any, index: number) => {
    ensureSpace(24);
    page.drawText(`Purchaser ${index + 1}: ${purchaser.firstName || ''} ${purchaser.lastName || ''}`, {
      x: MARGIN,
      y,
      size: 10,
      font: helveticaBold,
      color: ACCENT,
    });
    y -= 20;
    drawFieldsGrid([
      { label: 'Email', value: purchaser.email },
      { label: 'Primary Phone', value: purchaser.primaryPhone ? formatPhoneNumber(purchaser.primaryPhone) : '' },
      { label: 'Street Address', value: purchaser.streetAddress },
      { label: 'City', value: purchaser.city },
      { label: 'Province', value: purchaser.province },
      { label: 'Postal Code', value: purchaser.postalCode },
      { label: 'Country', value: purchaser.country },
      { label: 'Purchaser Type', value: purchaser.purchaserType },
      { label: 'Employer', value: purchaser.employer },
      { label: 'Occupation', value: purchaser.occupation },
      { label: 'Date of Birth', value: purchaser.dob ? convertAllDates(purchaser.dob, 'PP') : '' },
      { label: 'SIN', value: purchaser.sin ? formatSin(purchaser.sin) : '' },
      { label: 'ID Type', value: purchaser.idType },
      { label: 'ID Jurisdiction', value: purchaser.idJurisdiction },
    ]);
  });

  // Realtor Information
  drawSectionTitle('Realtor Information');
  if (worksheet.realtor) {
    drawFieldsGrid([
      { label: 'Full Name', value: worksheet.realtor.fullName },
      { label: 'Email', value: worksheet.realtor.email },
      { label: 'Direct Phone', value: worksheet.realtor.directPhone ? formatPhoneNumber(worksheet.realtor.directPhone) : '' },
      { label: 'Brokerage', value: worksheet.realtor.brokerage },
      { label: 'Brokerage Phone', value: worksheet.realtor.brokeragePhone ? formatPhoneNumber(worksheet.realtor.brokeragePhone) : '' },
      { label: 'Brokerage Fax', value: worksheet.realtor.brokerageFax },
      { label: 'Street Address', value: worksheet.realtor.streetAddress },
      { label: 'City', value: worksheet.realtor.city },
      { label: 'Province', value: worksheet.realtor.province },
      { label: 'Postal Code', value: worksheet.realtor.postalCode },
    ]);
  } else {
    ensureSpace(20);
    page.drawText('Public (No Realtor)', { x: MARGIN, y, size: 10, font: helvetica, color: MUTED });
    y -= 25;
  }

  // Options Selected
  drawSectionTitle('Options Selected');
  drawTable(
    ['Option', 'Amount Purchased', 'Total Cost', 'Renting'],
    [CONTENT_WIDTH * 0.4, CONTENT_WIDTH * 0.2, CONTENT_WIDTH * 0.2, CONTENT_WIDTH * 0.2],
    (worksheet.options || []).map((option: any) => [
      option.name || '-',
      option.purchaseAmount != null ? String(option.purchaseAmount) : '-',
      option.amount != null ? numToCurrency.format(option.amount) : '-',
      option.rent ? 'Yes' : 'No',
    ])
  );

  // Wishlist Choices (wishlist worksheets only)
  if (worksheet.wishlist || worksheet.wishlistChoices?.length) {
    drawSectionTitle('Wishlist Choices');
    drawTable(
      ['Unit Type', 'Model Types', 'Level', 'Exposure'],
      [CONTENT_WIDTH * 0.25, CONTENT_WIDTH * 0.35, CONTENT_WIDTH * 0.2, CONTENT_WIDTH * 0.2],
      (worksheet.wishlistChoices || []).map((choice: any) => [
        choice.unitType || '-',
        choice.modelTypes?.length ? choice.modelTypes.join(', ') : '-',
        choice.level || '-',
        choice.exposure || '-',
      ])
    );
  }

  // Notes
  if (worksheet.notes) {
    drawSectionTitle('Notes');
    const lineHeight = 12;
    const wrapped = wrapText(worksheet.notes, CONTENT_WIDTH - 20, helvetica, 9);
    const lineCount = wrapped.text.split('\n').length;
    const boxHeight = lineCount * lineHeight + 20;
    ensureSpace(boxHeight);
    page.drawRectangle({ x: MARGIN, y: y - boxHeight, width: CONTENT_WIDTH, height: boxHeight, color: STRIPE });
    page.drawText(wrapped.text, { x: MARGIN + 10, y: y - 16, size: 9, font: helvetica, color: TEXT, lineHeight });
    y -= boxHeight + 12;
  }

  // Each purchaser ID gets its own page (image IDs are scaled to fit; PDF IDs have their pages copied in).
  for (const [purchaserIndex, purchaser] of (worksheet.purchasers || []).entries()) {
    for (const identification of purchaser.identifications || []) {
      if (!identification.name || !purchaser._id) continue;
      const title = `Purchaser ${purchaserIndex + 1}: ${purchaser.firstName || ''} ${purchaser.lastName || ''} - ${identification.name || 'ID'}`;
      try {
        // Fetched through the API's /identifications route, which checks the login token and project access,
        // rather than straight from S3. credentials sends the login cookie; no-store keeps IDs out of the cache.
        const idUrl = `${env.basePath}/identifications/purchasers/${purchaser._id}/${encodeURIComponent(identification.name)}`;
        const idResponse = await fetch(idUrl, { credentials: 'include', cache: 'no-store' });
        if (!idResponse.ok) throw new Error(`ID request failed: ${idResponse.status}`);
        const idBytes = await idResponse.arrayBuffer();
        const isPdf =
          (idResponse.headers.get('content-type') || '').includes('pdf') ||
          (identification.name || '').toLowerCase().endsWith('.pdf');

        if (isPdf) {
          const idDoc = await PDFDocument.load(idBytes, { ignoreEncryption: true });
          const idPages = await pdf.copyPages(idDoc, idDoc.getPageIndices());
          idPages.forEach((idPage: any) => pdf.addPage(idPage));
          continue;
        }

        let idImage;
        try {
          idImage = await pdf.embedJpg(idBytes);
        } catch {
          try {
            idImage = await pdf.embedPng(idBytes);
          } catch {
            // Only JPG, PNG and PDF can go in a PDF (e.g. iPhone HEIC photos can't)
            throw new Error('unsupported');
          }
        }
        page = pdf.addPage(PageSizes.Letter);
        y = PAGE_HEIGHT - MARGIN;
        page.drawText(title, { x: MARGIN, y, size: 12, font: helveticaBold, color: ACCENT });
        y -= 24;
        const scale = Math.min(CONTENT_WIDTH / idImage.width, (y - MARGIN) / idImage.height, 1);
        const imgWidth = idImage.width * scale;
        const imgHeight = idImage.height * scale;
        page.drawImage(idImage, { x: PAGE_WIDTH / 2 - imgWidth / 2, y: y - imgHeight, width: imgWidth, height: imgHeight });
      } catch (err: any) {
        // Keep a page for an ID that fails so it's clear one is missing, rather than aborting the export.
        page = pdf.addPage(PageSizes.Letter);
        y = PAGE_HEIGHT - MARGIN;
        page.drawText(title, { x: MARGIN, y, size: 12, font: helveticaBold, color: ACCENT });
        const reason =
          err?.message === 'unsupported'
            ? 'This ID is not a JPG, PNG or PDF, so it could not be added. Download it from the purchaser instead.'
            : 'This ID could not be loaded.';
        page.drawText(reason, { x: MARGIN, y: y - 24, size: 10, font: helvetica, color: MUTED });
      }
    }
  }
};

export const exportWorksheetPdf = async (worksheet: any, project: IProject) => {
  const pdf = await PDFDocument.create();
  const helvetica = await pdf.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdf.embedFont(StandardFonts.HelveticaBold);

  await drawWorksheetPages(pdf, helvetica, helveticaBold, worksheet, project);

  const pdfBytes = await pdf.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const purchaserName = worksheet.purchasers?.[0]
    ? `${worksheet.purchasers[0].firstName} ${worksheet.purchasers[0].lastName}`
    : 'Wishlist';
  const suite = worksheet.unit ? `Suite ${worksheet.unit.suite} - ` : '';
  saveAs(blob, `${project.name} - ${suite}${purchaserName} Worksheet.pdf`);
};

export const exportRealtorWishlistWorksheetsPdf = async (worksheets: any[], realtorName: string, project: IProject) => {
  const pdf = await PDFDocument.create();
  const helvetica = await pdf.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdf.embedFont(StandardFonts.HelveticaBold);

  for (const worksheet of worksheets) {
    await drawWorksheetPages(pdf, helvetica, helveticaBold, worksheet, project);
  }

  const pdfBytes = await pdf.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  saveAs(blob, `${project.name} - ${realtorName} Wishlist Worksheets.pdf`);
};

export const ordinal_suffix_of = (i: number) => {
  let j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return i + 'st';
  }
  if (j === 2 && k !== 12) {
    return i + 'nd';
  }
  if (j === 3 && k !== 13) {
    return i + 'rd';
  }
  return i + 'th';
};

export const signField = (index: number) => {
  if (index === 0) {
    return 'P1';
  } else if (index === 1) {
    return 'P2';
  } else if (index === -1) {
    return 'Ex';
  } else if (index === -2) {
    return 'Re';
  } else if (index === -3) {
    return 'SO1';
  } else if (index === -4) {
    return 'SO2';
  } else if (index === -5) {
    return 'SO3';
  } else if (index === 2) {
    return 'P3';
  } else if (index === 3) {
    return 'P4';
  } else if (index === 4) {
    return 'P5';
  } else if (index === 5) {
    return 'P6';
  } else if (index === -6) {
    return 'Wit1';
  } else if (index === -7) {
    return 'Wit2';
  } else if (index === -8) {
    return 'Wit3';
  } else if (index === -9) {
    return 'Wit4';
  } else {
    return '';
  }
};

export const checkSpecialChars = (string: string) => {
  let format = /[`!^*()_\=\[\]{};:"\\|,<>\/?~]/;
  let check = format.test(string);
  return check;
};

export const getUnitType = (unitType: string) => {
  switch (unitType) {
    case 'studio':
      return 'Studio';
    case 'jrOneBr':
      return 'JR1BR';
    case 'oneBr':
      return '1BR';
    case 'oneBrInbound':
      return '1BR(I)';
    case 'oneBrDen':
      return '1BR+D';
    case 'oneBrStudy':
      return '1BR+S';
    case 'oneBrMedia':
      return '1BR+M';
    case 'oneBrFlex':
      return '1BR+F';
    case 'oneBrDenInbound':
      return '1BR+D(I)';
    case 'oneBrMediaInbound':
      return '1BR+M(I)';
    case 'twoBr':
      return '2BR';
    case 'twoBrInbound':
      return '2BR(I)';
    case 'twoBrDen':
      return '2BR+D';
    case 'twoBrMedia':
      return '2BR+M';
    case 'twoBrFlex':
      return '2BR+F';
    case 'twoBrStudy':
      return '2BR+S';
    case 'juniorTwo':
      return 'JR2';
    case 'twoBrDenInbound':
      return '2BR+D(I)';
    case 'twoBrMediaInbound':
      return '2BR+M(I)';
    case 'threeBr':
      return '3BR';
    case 'threeBrInbound':
      return '3BR(I)';
    case 'threeBrDen':
      return '3BR+D';
    case 'threeBrMedia':
      return '3BR+M';
    case 'threeBrFlex':
      return '3BR+F';
    case 'th':
      return 'TH';
    default:
      return 'N/A';
  }
};

export const envelopeIdRegex = (id: string) => {
  if (id.length > 0) {
    let envelopeIdTest = new RegExp('^[a-zA-Z0-9]+(?:--?[a-zA-Z0-9]+)*$');
    let validId = envelopeIdTest.test(id);
    if (validId && id.length === 36) {
      return false;
    } else return true;
  } else return false;
};
